import { useEffect, useMemo, useRef, useState } from "react";
import type {
  Character,
  FilmProject,
  ReviewGateState,
  Scene,
  SeedanceVoicePreset,
  StoryboardFrame,
  UploadedAssetManifest,
  UploadedAssetRecord
} from "../types";
import type { AssetLook, MatchedPerson, MatchedPlace } from "../utils/assetMatching";
import { assetDisplayUrl, frameDisplayUrl, matchPeople, matchPlaces, pickLookForScene } from "../utils/assetMatching";
import { projectLogline, projectTone, sceneDialogue, sceneDirectorPage, voiceTracksForScene } from "../utils/scenePage";
import {
  askAssistant,
  deriveExports,
  requestBoardQc,
  requestAnimatic,
  requestCaptions,
  requestCasting,
  requestDailies,
  requestRenderQc,
  fetchRenderHistory,
  requestMusicBeats,
  requestTableRead,
  requestVoiceLabel,
  fetchDoctor,
  fetchProjectExports,
  fetchRenderJob,
  fetchRenderStatus,
  fetchStitchJob,
  requestRenderEstimate,
  fetchObjectives,
  startRenderJob,
  startStitchJobOnEngine,
  stitchRenderJobOnEngine,
  type CutObjective,
  type DoctorReport,
  type BoardQcResult,
  type CastingMatch,
  type ProjectExportItem,
  type RenderEstimate,
  type RenderGateStatus,
  type RenderJob,
  type RenderQcReport,
  type StitchJobRecord
} from "../api/client";
import type { SimpleSceneRemakeRequest } from "./SimpleMode";
import { downloadStitch, stitchRenderJob, type StitchProgress } from "../utils/stitchRender";
import { clampRuntimeSeconds, formatRuntime } from "../utils/storyboard";

/**
 * Pro Glass — the redesigned professional workspace. Four sections (Story, Boards,
 * Cast & Places, Delivery) in a frosted-glass shell, dark theme first with a light twin.
 * Every action routes through the same App handlers the classic Pro and Simple mode use;
 * no engine logic lives here.
 */

export type ProSection = "story" | "boards" | "cast" | "delivery";

export const VOICE_PRESETS: Array<{ id: SeedanceVoicePreset; label: string; blurb: string }> = [
  { id: "cinematic_natural", label: "Cinematic natural", blurb: "grounded, present" },
  { id: "rugged_noir", label: "Rugged noir", blurb: "gravel and rain" },
  { id: "calm_warm", label: "Calm & warm", blurb: "close to the mic" },
  { id: "corporate_polished", label: "Corporate polished", blurb: "spotless delivery" },
  { id: "authoritative", label: "Authoritative", blurb: "newsroom weight" },
  { id: "tense_whisper", label: "Tense whisper", blurb: "barely holding on" },
  { id: "exhausted_broken", label: "Exhausted & broken", blurb: "end of the road" },
  { id: "comedic_dry", label: "Comedic dry", blurb: "deadpan timing" },
  { id: "youthful_bright", label: "Youthful bright", blurb: "quick and light" },
  { id: "villain_silky", label: "Villain silky", blurb: "smiling threat" }
];

const PRO_RUNTIME_OPTIONS = [
  { label: "0:15", value: 15 },
  { label: "0:30", value: 30 },
  { label: "1:00", value: 60 },
  { label: "2:00", value: 120 },
  { label: "5:00", value: 300 },
  { label: "10:00", value: 600 },
  { label: "15:00", value: 900 }
];

export interface ProGlassProps {
  project: FilmProject;
  theme: "light" | "dark";
  onThemeChange: (theme: "light" | "dark") => void;
  onOpenLibrary: () => void;
  onSmartIngest: (files: File[]) => void;
  onSaveCutPlans: (store: { activeId: string; plans: CutPlanDoc[] }) => void;
  onPatchProject: (patch: Partial<FilmProject>) => void;
  onMakePoster: () => void;
  isMakingPoster: boolean;
  apiStatus: "checking" | "online" | "offline";
  engineStale: boolean;
  saveStatus: string;
  isAnalyzing: boolean;
  isEasyModeRunning: boolean;
  isGeneratingBoards: boolean;
  isSavingPackage: boolean;
  isSharingLink: boolean;
  notice: string;
  onOpenSimple: () => void;
  onJustMakeIt: () => void;
  onScriptUpload: (file: File) => void;
  onAnalyze: () => void;
  onGenerateScene: (sceneId: string, panelsPerScene: number) => void;
  onRemakeScene: (request: SimpleSceneRemakeRequest) => void;
  onRestoreTake: (sceneId: string, takeId: string) => void;
  onSaveSceneNote: (sceneId: string, text: string) => void;
  onSetVoicePreset: (characterId: string, preset: SeedanceVoicePreset) => void;
  onUploadPersonPhoto: (personName: string, files: File[]) => void;
  onUploadAssets: (bucket: keyof UploadedAssetManifest, files: File[]) => void;
  onShareLink: () => void;
  onPackage: (gates: ReviewGateState) => Promise<boolean>;
}

const ALL_GATES: Array<{ key: keyof ReviewGateState; label: string }> = [
  { key: "scriptRights", label: "Script rights confirmed" },
  { key: "likenessRights", label: "Likeness rights for real people" },
  { key: "voiceRights", label: "Voice rights for uploaded reads" },
  { key: "brandIpRights", label: "Brand & IP cleared" },
  { key: "humanReview", label: "Human reviewed the full cut" },
  { key: "finalConfirm", label: "Final confirmation" }
];

function lookUrl(look: AssetLook | undefined, width?: number): string {
  return look ? assetDisplayUrl(look.asset, width) : "";
}

function sceneCoverUrl(scene: Scene, width?: number): string {
  const frame = scene.frames.find((f) => !f.disabled && (f.imageUrl || f.sourceUrl || f.storageKey));
  return frame ? frameDisplayUrl(frame, width) : "";
}

function initials(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((part) => part[0] || "").join("").toUpperCase();
}

function readsForCharacter(project: FilmProject, name: string) {
  const token = name.toLowerCase();
  return (project.uploadedAssets?.voiceAudio || []).filter(
    (asset) => !asset.disabled && ((asset.characterName || "").toLowerCase() === token || asset.fileName.toLowerCase().includes(token))
  );
}

/* ---------- Topbar ---------- */

function ProTopbar({
  project,
  section,
  saveStatus,
  isBusy,
  theme,
  onThemeChange,
  onSection,
  onOpenSimple,
  onOpenLibrary,
  onAskStudio,
  onJustMakeIt,
  onPatchProject
}: {
  project: FilmProject;
  section: ProSection;
  saveStatus: string;
  isBusy: boolean;
  theme: "light" | "dark";
  onThemeChange: (theme: "light" | "dark") => void;
  onSection: (section: ProSection) => void;
  onOpenSimple: () => void;
  onOpenLibrary: () => void;
  onAskStudio: () => void;
  onJustMakeIt: () => void;
  onPatchProject: (patch: Partial<FilmProject>) => void;
}) {
  const tabs: Array<{ id: ProSection; label: string }> = [
    { id: "story", label: "Story" },
    { id: "boards", label: "Boards" },
    { id: "cast", label: "Cast & Places" },
    { id: "delivery", label: "Delivery" }
  ];
  const runtimeOptions = PRO_RUNTIME_OPTIONS.some((option) => option.value === project.runtimeSeconds)
    ? PRO_RUNTIME_OPTIONS
    : [{ label: `${formatRuntime(project.runtimeSeconds)} custom`, value: project.runtimeSeconds }, ...PRO_RUNTIME_OPTIONS];
  return (
    <header className="pg-topbar">
      <span className="pg-brand">
        <b>K</b>
        <span className="pg-brand-meta">
          <strong>{project.title}</strong>
          <small>{saveStatus}</small>
        </span>
      </span>
      <nav className="pg-tabs" aria-label="Workspace sections">
        {tabs.map((tab) => (
          <button
            className={`pg-tab${section === tab.id ? " active" : ""}`}
            key={tab.id}
            onClick={() => onSection(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </nav>
      <span className="pg-topbar-actions">
        <JobTray />
        <button
          className="pg-pill ghost"
          onClick={() => onThemeChange(theme === "dark" ? "light" : "dark")}
          title="Switch theme"
          type="button"
        >
          {theme === "dark" ? "☀️" : "🌙"}
        </button>
        <button className="pg-pill ghost" onClick={onAskStudio} type="button">
          ✦ Ask
        </button>
        <button className="pg-pill ghost" onClick={onOpenLibrary} type="button">
          🗂 Library
        </button>
        <button className="pg-pill ghost" onClick={onOpenSimple} type="button">
          Simple
        </button>
        <label className="pg-runtime-control">
          <span>Length</span>
          <select
            aria-label="Project runtime preset"
            onChange={(event) => onPatchProject({ runtimeSeconds: Number(event.target.value) })}
            value={project.runtimeSeconds}
          >
            {runtimeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <input
            aria-label="Project custom runtime seconds"
            min={1}
            onChange={(event) => onPatchProject({ runtimeSeconds: clampRuntimeSeconds(Number(event.target.value), project.runtimeSeconds) })}
            step={1}
            type="number"
            value={project.runtimeSeconds}
          />
        </label>
        <button className="pg-pill primary" disabled={isBusy} onClick={onJustMakeIt} type="button">
          {isBusy ? "Working…" : "✨ Make film"}
        </button>
      </span>
    </header>
  );
}

/* ---------- Story: AI desk (coverage + pitch kit) ---------- */

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function objList(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object") : [];
}

function downloadText(fileName: string, text: string) {
  const blob = new Blob([text], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

export interface StudioJob {
  id: string;
  label: string;
  status: "running" | "done" | "failed";
  detail?: string;
  startedAt: number;
}

/** Fire-and-forget job announcements — the topbar tray listens. */
export function announceJob(job: { id: string; label: string; status: StudioJob["status"]; detail?: string }) {
  window.dispatchEvent(new CustomEvent("kinetix-job", { detail: job }));
}

function JobTray() {
  const [jobs, setJobs] = useState<StudioJob[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onJob = (event: Event) => {
      const detail = (event as CustomEvent).detail as { id: string; label: string; status: StudioJob["status"]; detail?: string };
      if (!detail || !detail.id) return;
      setJobs((current) => {
        const existing = current.find((job) => job.id === detail.id);
        const next = existing
          ? current.map((job) => (job.id === detail.id ? { ...job, ...detail } : job))
          : [...current, { ...detail, startedAt: Date.now() }];
        return next.slice(-14);
      });
    };
    window.addEventListener("kinetix-job", onJob);
    return () => window.removeEventListener("kinetix-job", onJob);
  }, []);
  const running = jobs.filter((job) => job.status === "running").length;
  if (!jobs.length) return null;
  return (
    <span className="pg-jobtray">
      <button className={`pg-pill ghost pg-bell${running ? " busy" : ""}`} onClick={() => setOpen((value) => !value)} title="Jobs" type="button">
        {running ? "⏳" : "🔔"}{running ? ` ${running}` : ""}
      </button>
      {open ? (
        <div className="pg-panel pg-jobtray-pop">
          <div className="pg-row">
            <strong className="pg-grow">Jobs</strong>
            <button className="pg-pill ghost small" onClick={() => setJobs((current) => current.filter((job) => job.status === "running"))} type="button">Clear done</button>
          </div>
          {jobs.slice().reverse().map((job) => (
            <div className="pg-row pg-jobtray-item" key={job.id}>
              <span>{job.status === "running" ? "⏳" : job.status === "done" ? "✅" : "⚠️"}</span>
              <small className="pg-grow">{job.label}{job.detail ? <i className="pg-muted"> — {job.detail}</i> : null}</small>
              <small className="pg-muted">{Math.max(1, Math.round((Date.now() - job.startedAt) / 1000))}s</small>
            </div>
          ))}
        </div>
      ) : null}
    </span>
  );
}

function storyAiContext(project: FilmProject): Record<string, unknown> {
  const scriptText = project.scriptUpload?.text || project.scriptExcerpt || "";
  return {
    title: project.title,
    script: scriptText.slice(0, 18000),
    scenes: project.scenes.map((scene) => ({ slugline: scene.slugline, synopsis: scene.synopsis })).slice(0, 80),
    characters: project.characters.map((character) => ({ name: character.name, role: character.role })).slice(0, 30)
  };
}

function coverageMarkdown(title: string, c: Record<string, unknown>): string {
  const scores = (c.scores && typeof c.scores === "object" ? c.scores : {}) as Record<string, unknown>;
  return [
    `# Coverage — ${title}`,
    `**Verdict: ${str(c.verdict) || "—"}** — ${str(c.verdictReason)}`,
    `**Logline:** ${str(c.logline)}`,
    `**Genre/Tone:** ${str(c.genre)} · ${str(c.tone)}`,
    `\n## Synopsis\n${str(c.synopsis)}`,
    `\n## Scores\n${Object.entries(scores).map(([k, v]) => `- ${k}: ${v}/10`).join("\n")}`,
    `\n## Strengths\n${strList(c.strengths).map((s) => `- ${s}`).join("\n")}`,
    `\n## Concerns\n${strList(c.concerns).map((s) => `- ${s}`).join("\n")}`,
    `\n## Characters\n${objList(c.characters).map((ch) => `- **${str(ch.name)}** — ${str(ch.note)}`).join("\n")}`,
    `\n## Pacing\n${objList(c.pacing).map((a) => `- **${str(a.act)}** — ${str(a.note)}`).join("\n")}`,
    `\n## Comps\n${strList(c.comps).map((s) => `- ${s}`).join("\n")}`
  ].join("\n");
}

function pitchMarkdown(title: string, p: Record<string, unknown>): string {
  const poster = (p.posterCopy && typeof p.posterCopy === "object" ? p.posterCopy : {}) as Record<string, unknown>;
  return [
    `# Pitch kit — ${title}`,
    `\n## Loglines\n${strList(p.loglines).map((s) => `- ${s}`).join("\n")}`,
    `\n## Taglines\n${strList(p.taglines).map((s) => `- ${s}`).join("\n")}`,
    `\n## Short synopsis\n${str(p.synopsisShort)}`,
    `\n## One-page synopsis\n${str(p.synopsisLong)}`,
    `\n## Comps\n${objList(p.comps).map((c) => `- **${str(c.comp)}** — ${str(c.why)}`).join("\n")}`,
    `\n## Audience\n${str(p.audience)}`,
    `\n## Poster\n**${str(poster.title)}** — ${str(poster.tag)}`,
    `\n## Why now\n${str(p.whyNow)}`
  ].join("\n");
}

function StoryAiDesk({
  project,
  onPatchProject,
  onMakePoster,
  isMakingPoster
}: {
  project: FilmProject;
  onPatchProject: (patch: Partial<FilmProject>) => void;
  onMakePoster: () => void;
  isMakingPoster: boolean;
}) {
  const [busy, setBusy] = useState<"" | "coverage" | "pitch">("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState<"" | "coverage" | "pitch">("");
  const coverage = project.coverage;
  const pitch = project.pitchKit;
  const hasMaterial = Boolean(project.scriptUpload?.text || project.scriptExcerpt || project.scenes.length);

  async function run(mode: "coverage" | "pitch") {
    setBusy(mode);
    setError("");
    try {
      const context = storyAiContext(project);
      if (mode === "pitch" && coverage) {
        context.coverageNotes = { logline: coverage.logline, strengths: coverage.strengths, comps: coverage.comps };
      }
      const result = await askAssistant(mode, context);
      if (!result.ok || !result.answer) {
        setError(result.message || "The studio reader is unavailable right now.");
      } else {
        const stamped = { ...(result.answer as Record<string, unknown>), generatedAt: new Date().toISOString() };
        onPatchProject(mode === "coverage" ? { coverage: stamped } : { pitchKit: stamped });
        setOpen(mode);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setBusy("");
    }
  }

  const verdict = str(coverage?.verdict).toUpperCase();
  const verdictTone = verdict === "RECOMMEND" ? "ok" : verdict === "CONSIDER" ? "warn" : "";
  const scores = (coverage?.scores && typeof coverage.scores === "object" ? coverage.scores : {}) as Record<string, unknown>;
  const poster = (pitch?.posterCopy && typeof pitch.posterCopy === "object" ? pitch.posterCopy : {}) as Record<string, unknown>;
  return (
    <div className="pg-ai-desk">
      <div className="pg-row">
        <strong className="pg-grow">Studio reader</strong>
        <button className="pg-pill ghost small" disabled={!hasMaterial || busy !== ""} onClick={() => void run("coverage")} type="button">
          {busy === "coverage" ? "Reading…" : coverage ? "Re-run coverage" : "📋 Coverage"}
        </button>
        <button className="pg-pill ghost small" disabled={!hasMaterial || busy !== ""} onClick={() => void run("pitch")} type="button">
          {busy === "pitch" ? "Writing…" : pitch ? "Re-run pitch kit" : "🎬 Pitch kit"}
        </button>
      </div>
      {!hasMaterial ? <small className="pg-muted">Upload a script first — then get studio coverage and a pitch kit in one tap.</small> : null}
      {error ? <small className="pg-muted">⚠ {error}</small> : null}
      {coverage ? (
        <div className="pg-panel pg-ai-doc">
          <button className="pg-row pg-ai-doc-head" onClick={() => setOpen(open === "coverage" ? "" : "coverage")} type="button">
            <strong className="pg-grow">Coverage</strong>
            <span className={`pg-chip ${verdictTone}`}>{verdict || "READ"}</span>
            <span className="pg-chip">{open === "coverage" ? "▴" : "▾"}</span>
          </button>
          {open === "coverage" ? (
            <>
              <p><b>{str(coverage.logline)}</b></p>
              <p className="pg-muted">{str(coverage.genre)} · {str(coverage.tone)} — {str(coverage.verdictReason)}</p>
              <p>{str(coverage.synopsis)}</p>
              <div className="pg-row pg-wrap">
                {Object.entries(scores).map(([key, value]) => (
                  <span className="pg-chip" key={key}>{key} {String(value)}/10</span>
                ))}
              </div>
              {strList(coverage.strengths).length ? (<><span className="pg-eyebrow">Working</span><ul className="pg-ai-list">{strList(coverage.strengths).map((s, i) => <li key={i}>{s}</li>)}</ul></>) : null}
              {strList(coverage.concerns).length ? (<><span className="pg-eyebrow">Watch</span><ul className="pg-ai-list">{strList(coverage.concerns).map((s, i) => <li key={i}>{s}</li>)}</ul></>) : null}
              {objList(coverage.characters).length ? (<><span className="pg-eyebrow">Characters</span><ul className="pg-ai-list">{objList(coverage.characters).map((c, i) => <li key={i}><b>{str(c.name)}</b> — {str(c.note)}</li>)}</ul></>) : null}
              {objList(coverage.pacing).length ? (<><span className="pg-eyebrow">Pacing</span><ul className="pg-ai-list">{objList(coverage.pacing).map((a, i) => <li key={i}><b>{str(a.act)}</b> — {str(a.note)}</li>)}</ul></>) : null}
              {strList(coverage.comps).length ? <small className="pg-muted">Comps: {strList(coverage.comps).join(" · ")}</small> : null}
              <button className="pg-pill ghost small" onClick={() => downloadText(`${project.title}-coverage.md`, coverageMarkdown(project.title, coverage))} type="button">⬇ Download .md</button>
            </>
          ) : null}
        </div>
      ) : null}
      {pitch ? (
        <div className="pg-panel pg-ai-doc">
          <button className="pg-row pg-ai-doc-head" onClick={() => setOpen(open === "pitch" ? "" : "pitch")} type="button">
            <strong className="pg-grow">Pitch kit</strong>
            <span className="pg-chip ok">{str(poster.title) || "READY"}</span>
            <span className="pg-chip">{open === "pitch" ? "▴" : "▾"}</span>
          </button>
          {open === "pitch" ? (
            <>
              <span className="pg-eyebrow">Loglines</span>
              <ul className="pg-ai-list">{strList(pitch.loglines).map((s, i) => <li key={i}>{s}</li>)}</ul>
              <span className="pg-eyebrow">Taglines</span>
              <ul className="pg-ai-list">{strList(pitch.taglines).map((s, i) => <li key={i}><i>{s}</i></li>)}</ul>
              <p>{str(pitch.synopsisShort)}</p>
              {objList(pitch.comps).length ? (<><span className="pg-eyebrow">Comps</span><ul className="pg-ai-list">{objList(pitch.comps).map((c, i) => <li key={i}><b>{str(c.comp)}</b> — {str(c.why)}</li>)}</ul></>) : null}
              {str(pitch.audience) ? <small className="pg-muted">Audience: {str(pitch.audience)}</small> : null}
              {project.posterAsset ? (
                <span className="pg-poster-preview">
                  <img alt="Film poster" loading="lazy" src={assetDisplayUrl(project.posterAsset, 320) || ""} />
                </span>
              ) : null}
              <div className="pg-row">
                <button className="pg-pill ghost small" onClick={() => downloadText(`${project.title}-pitch-kit.md`, pitchMarkdown(project.title, pitch))} type="button">⬇ Download .md</button>
                <button className="pg-pill primary small" disabled={isMakingPoster} onClick={onMakePoster} type="button">
                  {isMakingPoster ? "Painting…" : project.posterAsset ? "🎞 Re-make poster" : "🎞 Generate poster"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ---------- Story ---------- */

const SCRIPT_FILE_RE = /\.(pdf|fdx|txt|fountain)$/i;

function StartAnywhere({
  onScriptUpload,
  onSmartIngest
}: {
  onScriptUpload: (file: File) => void;
  onSmartIngest: (files: File[]) => void;
}) {
  const [idea, setIdea] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  function routeFiles(files: File[]) {
    if (!files.length) return;
    const script = files.find((file) => SCRIPT_FILE_RE.test(file.name));
    const rest = files.filter((file) => file !== script);
    if (script) onScriptUpload(script);
    if (rest.length) onSmartIngest(rest);
  }

  async function draftFromIdea() {
    if (!idea.trim()) return;
    setBusy(true);
    setError("");
    try {
      const result = await askAssistant("draft", { idea: idea.trim() });
      const answer = (result.answer || {}) as Record<string, unknown>;
      const scriptText = str(answer.scriptText);
      if (!result.ok || !scriptText) {
        setError(result.message || "The screenwriter is unavailable right now.");
      } else {
        const title = str(answer.title) || "Untitled Draft";
        onScriptUpload(new File([scriptText], `${title.replace(/[^A-Za-z0-9 _-]+/g, "").trim() || "draft"}.txt`, { type: "text/plain" }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Draft failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className={`pg-start-zone${dragOver ? " over" : ""}`}
      onDragLeave={() => setDragOver(false)}
      onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
      onDrop={(event) => {
        event.preventDefault();
        setDragOver(false);
        routeFiles(Array.from(event.dataTransfer.files || []));
      }}
    >
      <h2>Start from anything</h2>
      <p className="pg-muted">Drop a script, a folder of photos, voice reads — AI routes every file to the right place. Or just type the idea:</p>
      <textarea
        className="pg-input pg-idea"
        onChange={(event) => setIdea(event.target.value)}
        placeholder={'e.g. "a moody noir teaser about a lighthouse keeper who hears a voice under the waves"'}
        rows={3}
        value={idea}
      />
      <div className="pg-row">
        <button className="pg-pill primary" disabled={busy || !idea.trim()} onClick={() => void draftFromIdea()} type="button">
          {busy ? "Writing your script…" : "✍️ Draft my script"}
        </button>
        <label className="pg-pill ghost pg-file">
          Or drop / pick files
          <input
            className="visually-hidden"
            multiple
            onChange={(event) => {
              routeFiles(Array.from(event.target.files || []));
              event.target.value = "";
            }}
            type="file"
          />
        </label>
      </div>
      {error ? <small className="pg-muted">⚠ {error}</small> : null}
    </div>
  );
}

function ProStory({
  project,
  onAnalyze,
  onScriptUpload,
  onSmartIngest,
  isAnalyzing,
  onOpenScene,
  onPatchProject,
  onMakePoster,
  isMakingPoster
}: {
  project: FilmProject;
  onAnalyze: () => void;
  onScriptUpload: (file: File) => void;
  onSmartIngest: (files: File[]) => void;
  isAnalyzing: boolean;
  onOpenScene: (sceneId: string) => void;
  onPatchProject: (patch: Partial<FilmProject>) => void;
  onMakePoster: () => void;
  isMakingPoster: boolean;
}) {
  const scriptText = project.scriptUpload?.text || project.scriptExcerpt || "";
  const logline = projectLogline(project);
  const tone = projectTone(project);
  return (
    <div className="pg-body pg-story">
      <section className="pg-panel pg-script" aria-label="Script">
        <span className="pg-eyebrow">
          {project.scriptUpload ? `${project.scriptUpload.fileName}` : "No script yet"}
        </span>
        {scriptText ? (
          <pre className="pg-script-text">{scriptText}</pre>
        ) : (
          <StartAnywhere onScriptUpload={onScriptUpload} onSmartIngest={onSmartIngest} />
        )}
        <div className="pg-row pg-script-actions">
          <label className="pg-pill ghost pg-file">
            Upload new draft
            <input
              className="visually-hidden"
              type="file"
              accept=".pdf,.fdx,.txt,.fountain,text/plain,application/pdf"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onScriptUpload(file);
                event.target.value = "";
              }}
            />
          </label>
          <button className="pg-pill ghost" disabled={isAnalyzing} onClick={onAnalyze} type="button">
            {isAnalyzing ? "Reading…" : "Re-analyze"}
          </button>
          <small className="pg-muted">Edits to the script never overwrite your boards.</small>
        </div>
        <StoryAiDesk isMakingPoster={isMakingPoster} onMakePoster={onMakePoster} onPatchProject={onPatchProject} project={project} />
      </section>
      <section className="pg-breakdown" aria-label="Breakdown">
        <div className="pg-row pg-breakdown-head">
          <div>
            <h2>The breakdown</h2>
            <p className="pg-muted">What the engine read — open any scene in Boards.</p>
          </div>
          <span className="pg-chip">{project.scenes.length} scenes · {Math.round(project.scenes.reduce((sum, scene) => sum + scene.durationSeconds, 0))}s</span>
        </div>
        {project.scenes.map((scene) => {
          const beat = project.agentBeats.find((item) => item.sceneId === scene.id)?.purpose;
          const ready = scene.readiness === "ready";
          return (
            <button className="pg-panel pg-scene-card" key={scene.id} onClick={() => onOpenScene(scene.id)} type="button">
              <span className="pg-row">
                <b className="pg-num">{String(scene.number).padStart(2, "0")}</b>
                <strong className="pg-grow">{scene.slugline}</strong>
                <span className={`pg-chip ${ready ? "ok" : "warn"}`}>{ready ? "ready" : scene.readiness.replace(/_/g, " ")}</span>
                <span className="pg-chip">{Math.round(scene.durationSeconds)}s</span>
              </span>
              <span className="pg-scene-syn">{scene.synopsis}</span>
              <span className="pg-row pg-scene-foot">
                {beat ? <small className="pg-muted">Why it's here: {beat}</small> : <small />}
                <small className="pg-link">Open in Boards ›</small>
              </span>
            </button>
          );
        })}
        {logline ? (
          <div className="pg-panel pg-logline">
            <span className="pg-eyebrow">What the film is</span>
            <p>“{logline}”</p>
            {tone ? <small className="pg-muted">Tone: {tone} · from your script's analysis</small> : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

/* ---------- Takes Wall: the contact sheet. Every take across the film on one
   dense grid; tap takes into the active cut plan, CineFable-style. ---------- */

function newWallBeat(frame: StoryboardFrame, scene: Scene): BeatSpec {
  return {
    id: `beat-${Math.random().toString(36).slice(2, 9)}`,
    label: frame.label || frame.shotType || "Shot",
    seconds: 5,
    speak: false,
    enabled: true,
    prompt: frame.prompt || `Cinematic shot — ${frame.label || frame.shotType}. No text, no captions, no logos, no watermarks.`,
    kind: "chunk",
    audio: "score",
    sceneHeading: scene.slugline,
    refs: [],
    refHints: [frame.fileName || frame.label].filter(Boolean) as string[]
  };
}

function TakesWall({
  project,
  onSaveCutPlans,
  onOpenScene
}: {
  project: FilmProject;
  onSaveCutPlans: (store: { activeId: string; plans: CutPlanDoc[] }) => void;
  onOpenScene: (sceneId: string) => void;
}) {
  const [filter, setFilter] = useState("");
  const [dragBeatId, setDragBeatId] = useState<string | null>(null);
  const [dragFrameId, setDragFrameId] = useState<string | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const store = loadCutPlanStore(project);
  let plan = store.plans.find((doc) => doc.id === store.activeId) || store.plans[0];

  const takes: Array<{ frame: StoryboardFrame; scene: Scene }> = [];
  for (const scene of project.scenes) {
    for (const frame of scene.frames) {
      if (frame.disabled) continue;
      if (!(frame.generationStatus === "generated" || frame.source === "uploaded")) continue;
      if (!frameDisplayUrl(frame, 64)) continue;
      takes.push({ frame, scene });
    }
  }
  const fileToImage = new Map<string, string>();
  for (const take of takes) {
    const name = take.frame.fileName || take.frame.label;
    if (name && !fileToImage.has(name)) fileToImage.set(name, frameDisplayUrl(take.frame, 192));
  }
  const beatIndexFor = (frame: StoryboardFrame): number => {
    if (!plan) return -1;
    const name = frame.fileName || frame.label;
    if (!name) return -1;
    return plan.beats.findIndex((beat) => beat.enabled !== false && (beat.refHints || []).includes(name));
  };

  function persist(nextPlans: CutPlanDoc[], activeId: string) {
    onSaveCutPlans({ activeId, plans: nextPlans });
  }

  function addToStory(frame: StoryboardFrame, scene: Scene) {
    const next = loadCutPlanStore(project);
    let target = next.plans.find((doc) => doc.id === next.activeId) || next.plans[0];
    if (!target) {
      target = {
        id: `plan-${Math.random().toString(36).slice(2, 9)}`,
        objectiveId: "trailer",
        title: `${project.title} — wall cut`,
        beats: [],
        voiceKeys: [],
        openingCard: project.title.toUpperCase(),
        updatedAt: new Date().toISOString()
      };
      next.plans.push(target);
      next.activeId = target.id;
    }
    target.beats.push(newWallBeat(frame, scene));
    target.updatedAt = new Date().toISOString();
    persist(next.plans, next.activeId);
  }

  const [lastRemoved, setLastRemoved] = useState<{ beat: BeatSpec; index: number } | null>(null);
  const [snapBusy, setSnapBusy] = useState(false);
  const [snapNote, setSnapNote] = useState("");

  /** Snap each cut point to the nearest music hit within ±0.7s — durations stay sane (1.5-20s). */
  async function snapToScore() {
    const music = (project.uploadedAssets?.music || []).find((asset) => !asset.disabled && asset.storageKey);
    if (!music?.storageKey || !plan) return;
    setSnapBusy(true);
    setSnapNote("");
    try {
      const result = await requestMusicBeats(String(music.storageKey));
      const hits = result.beats || [];
      if (!result.ok || hits.length < 4) {
        setSnapNote(result.message || "Couldn't find enough hits in that track.");
        return;
      }
      const next = loadCutPlanStore(project);
      const target = next.plans.find((doc) => doc.id === next.activeId) || next.plans[0];
      if (!target) return;
      let clock = target.openingCard ? 2.2 : 0;
      let snapped = 0;
      for (const beat of target.beats) {
        if (beat.enabled === false) continue;
        const duration = (beat.outSeconds || 0) > 0 ? Math.max(1, (beat.outSeconds || 0) - (beat.inSeconds || 0)) : (beat.seconds || 5);
        const boundary = clock + duration;
        let best = -1;
        let bestDist = 0.7;
        for (const hit of hits) {
          const dist = Math.abs(hit - boundary);
          if (dist < bestDist) { best = hit; bestDist = dist; }
        }
        if (best > 0) {
          const newDuration = Math.round(Math.min(20, Math.max(1.5, best - clock)) * 10) / 10;
          if (Math.abs(newDuration - duration) > 0.05 && !((beat.outSeconds || 0) > 0)) {
            beat.seconds = newDuration;
            snapped += 1;
          }
          clock += (beat.outSeconds || 0) > 0 ? duration : newDuration;
        } else {
          clock = boundary;
        }
      }
      target.updatedAt = new Date().toISOString();
      persist(next.plans, next.activeId);
      setSnapNote(snapped ? `${snapped} cut${snapped === 1 ? "" : "s"} now land on the music.` : "Cuts already sit on the hits.");
    } catch (error) {
      setSnapNote(error instanceof Error ? error.message : "Beat detection failed (local engine only for now).");
    } finally {
      setSnapBusy(false);
    }
  }

  function removeBeat(beatId: string) {
    const next = loadCutPlanStore(project);
    const target = next.plans.find((doc) => doc.id === next.activeId) || next.plans[0];
    if (!target) return;
    const index = target.beats.findIndex((beat) => beat.id === beatId);
    if (index >= 0) setLastRemoved({ beat: target.beats[index], index });
    target.beats = target.beats.filter((beat) => beat.id !== beatId);
    target.updatedAt = new Date().toISOString();
    persist(next.plans, next.activeId);
  }

  function undoRemove() {
    if (!lastRemoved) return;
    const next = loadCutPlanStore(project);
    const target = next.plans.find((doc) => doc.id === next.activeId) || next.plans[0];
    if (!target) return;
    target.beats.splice(Math.min(lastRemoved.index, target.beats.length), 0, lastRemoved.beat);
    target.updatedAt = new Date().toISOString();
    persist(next.plans, next.activeId);
    setLastRemoved(null);
  }

  function endDrag() {
    setDragBeatId(null);
    setDragFrameId(null);
    setOverIndex(null);
  }

  /** Drop at `targetIndex` (beats.length = end): reorder a rail beat, or insert a dragged tile. */
  function handleStoryDrop(targetIndex: number) {
    const next = loadCutPlanStore(project);
    let target = next.plans.find((doc) => doc.id === next.activeId) || next.plans[0];
    if (dragBeatId) {
      if (!target) { endDrag(); return; }
      const from = target.beats.findIndex((beat) => beat.id === dragBeatId);
      if (from < 0) { endDrag(); return; }
      const [moved] = target.beats.splice(from, 1);
      const insertAt = Math.max(0, Math.min(from < targetIndex ? targetIndex - 1 : targetIndex, target.beats.length));
      target.beats.splice(insertAt, 0, moved);
    } else if (dragFrameId) {
      const take = takes.find((item) => item.frame.id === dragFrameId);
      if (!take) { endDrag(); return; }
      if (!target) {
        target = {
          id: `plan-${Math.random().toString(36).slice(2, 9)}`,
          objectiveId: "trailer",
          title: `${project.title} — wall cut`,
          beats: [],
          voiceKeys: [],
          openingCard: project.title.toUpperCase(),
          updatedAt: new Date().toISOString()
        };
        next.plans.push(target);
        next.activeId = target.id;
      }
      const insertAt = Math.max(0, Math.min(targetIndex, target.beats.length));
      target.beats.splice(insertAt, 0, newWallBeat(take.frame, take.scene));
    } else {
      endDrag();
      return;
    }
    target.updatedAt = new Date().toISOString();
    persist(next.plans, next.activeId);
    endDrag();
  }

  const visibleTakes = takes.filter((take) => {
    if (!filter.trim()) return true;
    const haystack = `${take.frame.label} ${take.frame.prompt || ""} ${take.scene.slugline}`.toLowerCase();
    return filter.trim().toLowerCase().split(/\s+/).every((term) => haystack.includes(term));
  });

  return (
    <div className="pg-takes-layout">
      <div className="pg-takes-main">
        <input
          className="pg-input pg-asset-search"
          onChange={(event) => setFilter(event.target.value)}
          placeholder="🔎 Filter takes — prompt words, scene, shot type…"
          type="search"
          value={filter}
        />
        {!takes.length ? (
          <p className="pg-muted">No finished takes yet — generate boards in the Scenes view and they all land here, every variant on one wall.</p>
        ) : (
          <div className="pg-takes-grid">
            {visibleTakes.map(({ frame, scene }) => {
              const inStory = beatIndexFor(frame);
              return (
                <figure
                  className={`pg-take-tile${inStory >= 0 ? " picked" : ""}${dragFrameId === frame.id ? " dragging" : ""}`}
                  draggable
                  key={frame.id}
                  onDragEnd={endDrag}
                  onDragStart={(event) => {
                    setDragFrameId(frame.id);
                    event.dataTransfer.effectAllowed = "copy";
                    event.dataTransfer.setData("text/plain", frame.id);
                  }}
                  title="Drag onto the Story rail — or use + Story"
                >
                  {inStory >= 0 ? <b className="pg-take-badge">{inStory + 1}</b> : null}
                  <span className="pg-take-img">
                    <img alt={frame.label} loading="lazy" src={frameDisplayUrl(frame, 576)} />
                  </span>
                  <figcaption>
                    <small className="pg-take-caption">{frame.prompt || frame.label || frame.shotType}</small>
                    <span className="pg-row pg-take-actions">
                      <button className="pg-chip" onClick={() => onOpenScene(scene.id)} title={scene.slugline} type="button">
                        sc {String(scene.number).padStart(2, "0")}
                      </button>
                      <span className="pg-grow" />
                      {inStory >= 0 ? (
                        <span className="pg-chip ok">in story</span>
                      ) : (
                        <button className="pg-pill ghost small" onClick={() => addToStory(frame, scene)} type="button">+ Story</button>
                      )}
                    </span>
                  </figcaption>
                </figure>
              );
            })}
          </div>
        )}
      </div>
      <aside className="pg-panel pg-story-rail" aria-label="Story">
        <div className="pg-row">
          <strong className="pg-grow">Story</strong>
          <span className="pg-chip">{plan ? `${plan.beats.filter((b) => b.enabled !== false).length} beats` : "no plan"}</span>
        </div>
        {plan ? <small className="pg-muted pg-clip" title={plan.title}>{plan.title}</small> : null}
        {!plan || !plan.beats.length ? (
          <p
            className="pg-muted pg-story-empty"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => { event.preventDefault(); handleStoryDrop(0); }}
          >
            Tap “+ Story” on any take — or drag a tile straight onto this rail.
          </p>
        ) : (
          <div
            className="pg-story-list"
            onDragLeave={(event) => {
              if (event.currentTarget === event.target) setOverIndex(null);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = dragBeatId ? "move" : "copy";
            }}
            onDrop={(event) => {
              event.preventDefault();
              handleStoryDrop(overIndex ?? plan.beats.length);
            }}
          >
            {plan.beats.map((beat, index) => {
              const hint = (beat.refHints || [])[0] || "";
              const thumb = hint ? fileToImage.get(hint) : "";
              return (
                <div
                  className={`pg-story-item${dragBeatId === beat.id ? " dragging" : ""}${overIndex === index ? " drop-target" : ""}`}
                  draggable
                  key={beat.id}
                  onDragEnd={endDrag}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setOverIndex((current) => (current === index ? current : index));
                  }}
                  onDragStart={(event) => {
                    setDragBeatId(beat.id);
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", beat.id);
                  }}
                  title="Drag to reorder"
                >
                  <i className="pg-grip" aria-hidden="true">⋮⋮</i>
                  <b className="pg-take-badge static">{index + 1}</b>
                  <span className="pg-story-thumb">{thumb ? <img alt="" loading="lazy" src={thumb} /> : <i>{beat.kind === "card" ? "Aa" : "▶"}</i>}</span>
                  <small className="pg-grow pg-clip">{beat.kind === "card" ? `“${beat.cardText || beat.label}”` : beat.label}</small>
                  {beat.kind !== "card" && (project.uploadedAssets?.sfx || []).some((asset) => !asset.disabled && asset.storageKey) ? (
                    <select
                      className="pg-sfx-select"
                      onChange={(event) => {
                        const value = event.target.value;
                        const next = loadCutPlanStore(project);
                        const target = next.plans.find((doc) => doc.id === next.activeId) || next.plans[0];
                        const found = target?.beats.find((entry) => entry.id === beat.id);
                        if (target && found) {
                          found.sfxKey = value || undefined;
                          target.updatedAt = new Date().toISOString();
                          persist(next.plans, next.activeId);
                        }
                      }}
                      title="SFX cue layered over this beat at stitch"
                      value={beat.sfxKey || ""}
                    >
                      <option value="">🔇</option>
                      {(project.uploadedAssets?.sfx || []).filter((asset) => !asset.disabled && asset.storageKey).map((asset) => (
                        <option key={asset.assetId || asset.fileName} value={String(asset.storageKey)}>🔊 {asset.fileName.slice(0, 18)}</option>
                      ))}
                    </select>
                  ) : null}
                  <button className="pg-linkbtn pg-story-x" onClick={() => removeBeat(beat.id)} title="Remove from story" type="button">✕</button>
                </div>
              );
            })}
            <div
              className={`pg-story-end${overIndex === plan.beats.length ? " drop-target" : ""}`}
              onDragOver={(event) => {
                event.preventDefault();
                setOverIndex((current) => (current === plan.beats.length ? current : plan.beats.length));
              }}
            >
              <small className="pg-muted">drop here to add to the end</small>
            </div>
          </div>
        )}
        {plan && (project.uploadedAssets?.music || []).some((asset) => !asset.disabled && asset.storageKey) ? (
          <button className="pg-pill ghost small" disabled={snapBusy} onClick={() => void snapToScore()} title="Detect the hits in your score and nudge every cut point onto one" type="button">
            {snapBusy ? "Listening…" : "🥁 Snap cuts to score"}
          </button>
        ) : null}
        {snapNote ? <small className="pg-muted">{snapNote}</small> : null}
        {lastRemoved ? (
          <button className="pg-pill ghost small" onClick={undoRemove} type="button">
            ↩ Undo remove “{lastRemoved.beat.label.slice(0, 18)}”
          </button>
        ) : null}
        <small className="pg-muted">Fine-tune timing, audio, and prompts in Delivery → Cut Composer.</small>
      </aside>
    </div>
  );
}

/* ---------- Boards ---------- */

function ProBoards({
  project,
  people,
  selectedSceneId,
  onSelectScene,
  isGenerating,
  isEasyModeRunning,
  onGenerateScene,
  onRemakeScene,
  onRestoreTake,
  onSaveSceneNote,
  onSaveCutPlans
}: {
  project: FilmProject;
  people: MatchedPerson[];
  selectedSceneId: string;
  onSelectScene: (sceneId: string) => void;
  isGenerating: boolean;
  isEasyModeRunning: boolean;
  onGenerateScene: (sceneId: string, panelsPerScene: number) => void;
  onRemakeScene: (request: SimpleSceneRemakeRequest) => void;
  onRestoreTake: (sceneId: string, takeId: string) => void;
  onSaveSceneNote: (sceneId: string, text: string) => void;
  onSaveCutPlans: (store: { activeId: string; plans: CutPlanDoc[] }) => void;
}) {
  const [boardsView, setBoardsView] = useState<"scenes" | "wall">("scenes");
  const scene = project.scenes.find((item) => item.id === selectedSceneId) || project.scenes[0];
  const [inspectorTab, setInspectorTab] = useState<"scene" | "shot" | "notes">("scene");
  const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null);
  const [panels, setPanels] = useState(0);
  const [qcResults, setQcResults] = useState<Record<string, BoardQcResult>>({});
  const [qcBusy, setQcBusy] = useState(false);
  const [readBusy, setReadBusy] = useState(false);
  const [readNote, setReadNote] = useState("");
  const [animaticBusy, setAnimaticBusy] = useState(false);

  async function runAnimatic() {
    const sceneEntries = project.scenes.map((item) => ({
      sceneId: item.id,
      seconds: Math.round(item.durationSeconds) || 4,
      boards: item.frames
        .filter((frame) => !frame.disabled && frame.storageKey && (frame.generationStatus === "generated" || frame.source === "uploaded"))
        .map((frame) => String(frame.storageKey)),
      lines: sceneDialogue(project, item.id).map((line) => ({ speaker: line.speaker, text: line.text }))
    })).filter((entry) => entry.boards.length);
    if (!sceneEntries.length) {
      setReadNote("No saved boards yet — generate boards first, then the animatic cuts them together.");
      return;
    }
    const music = (project.uploadedAssets?.music || []).find((asset) => !asset.disabled && asset.storageKey);
    setAnimaticBusy(true);
    setReadNote("");
    const jobId = `animatic-${Date.now()}`;
    announceJob({ id: jobId, label: "Animatic", status: "running" });
    try {
      const result = await requestAnimatic({
        projectId: project.id,
        title: project.title,
        musicKey: music?.storageKey ? String(music.storageKey) : undefined,
        scenes: sceneEntries
      });
      setReadNote(result.ok
        ? (result.status === "queued"
            ? String(result.message || "Animatic queued for your render agent.")
            : `Animatic ready — ${result.scenes} scenes, ${result.seconds}s${result.voiced ? ", voiced" : ""}. Find it in Delivery → Finished videos.`)
        : String(result.message || "Animatic failed."));
    } catch (error) {
      setReadNote(error instanceof Error ? error.message : "Animatic failed.");
    } finally {
      setAnimaticBusy(false);
      announceJob({ id: jobId, label: "Animatic", status: "done", detail: "check Finished videos" });
    }
  }

  async function runTableRead() {
    if (!scene) return;
    const lines = sceneDialogue(project, scene.id).map((line) => ({ speaker: line.speaker, text: line.text }));
    if (!lines.length) return;
    setReadBusy(true);
    setReadNote("");
    const jobId = `tableread-${Date.now()}`;
    announceJob({ id: jobId, label: "Table read", status: "running" });
    try {
      const result = await requestTableRead({ projectId: project.id, sceneId: scene.id, title: scene.slugline, lines });
      setReadNote(result.ok
        ? (result.status === "queued" ? String(result.message || "Queued for your render agent.") : "Table read saved to Finished videos (Delivery tab).")
        : String(result.message || "Table read failed."));
    } catch (error) {
      setReadNote(error instanceof Error ? error.message : "Table read failed.");
    } finally {
      setReadBusy(false);
      announceJob({ id: jobId, label: "Table read", status: "done", detail: "mp3 in Finished videos" });
    }
  }

  async function runBoardQc() {
    if (!scene) return;
    const targets = scene.frames.filter((frame) => !frame.disabled && frame.storageKey
      && (frame.generationStatus === "generated" || frame.source === "uploaded"));
    if (!targets.length) return;
    setQcBusy(true);
    for (const frame of targets) {
      try {
        const result = await requestBoardQc({
          storageKey: String(frame.storageKey),
          prompt: frame.prompt || "",
          sceneHeading: scene.slugline,
          characters: scene.characters
        });
        setQcResults((current) => ({ ...current, [frame.id]: result }));
      } catch (error) {
        setQcResults((current) => ({
          ...current,
          [frame.id]: { ok: false, status: "failed", message: error instanceof Error ? error.message : "failed" }
        }));
      }
    }
    setQcBusy(false);
  }
  useEffect(() => {
    setSelectedFrameId(null);
    setPanels(scene ? scene.frames.filter((f) => !f.disabled).length || 6 : 6);
  }, [scene?.id]);
  if (!scene) return <div className="pg-body"><p className="pg-muted">No scenes yet — read a script first.</p></div>;

  const frames = scene.frames.filter((frame) => !frame.disabled);
  const selectedFrame = frames.find((frame) => frame.id === selectedFrameId) || null;
  const cast = people.filter((person) => scene.characters.some((name) => name.toLowerCase() === person.name.toLowerCase()));
  const directorPage = sceneDirectorPage(project, scene.id);
  const dialogue = sceneDialogue(project, scene.id);
  const note = project.sceneNotes?.[scene.id] ?? "";
  const takes = project.sceneTakes?.[scene.id] || [];
  const busy = isGenerating || isEasyModeRunning;
  const generatedCount = frames.filter((f) => f.generationStatus === "generated" || f.source === "uploaded").length;

  function frameChip(frame: StoryboardFrame): { label: string; tone: string } {
    if (frame.source === "uploaded") return { label: "yours", tone: "ok" };
    if (frame.locked) return { label: "locked", tone: "ok" };
    switch (frame.generationStatus) {
      case "generated": return { label: "generated", tone: "ok" };
      case "generating": case "queued": return { label: "generating…", tone: "info" };
      case "failed": return { label: "failed — select to retry", tone: "bad" };
      default: return { label: "open slot", tone: "" };
    }
  }

  return (
    <div className="pg-body pg-boards">
      <aside className="pg-panel pg-rail" aria-label="Scenes">
        <span className="pg-eyebrow">Scenes</span>
        {project.scenes.map((item) => {
          const cover = sceneCoverUrl(item, 192);
          const warn = item.frames.some((f) => f.generationStatus === "failed");
          return (
            <button
              className={`pg-rail-item${item.id === scene.id ? " active" : ""}`}
              key={item.id}
              onClick={() => onSelectScene(item.id)}
              type="button"
            >
              <span className="pg-rail-thumb">{cover ? <img loading="lazy" src={cover} alt="" /> : null}</span>
              <span className="pg-row">
                <b>{String(item.number).padStart(2, "0")}</b>
                <small className="pg-grow pg-clip">{item.slugline}</small>
                <i className={`pg-dot ${warn ? "warn" : item.readiness === "ready" ? "ok" : "info"}`} />
              </span>
            </button>
          );
        })}
      </aside>

      <section className="pg-canvas" aria-label="Boards">
        <div className="pg-row pg-canvas-head">
          <div className="pg-grow">
            <h2>{boardsView === "wall" ? "Takes wall" : `Scene ${String(scene.number).padStart(2, "0")} · ${scene.slugline}`}</h2>
            <p className="pg-muted">{boardsView === "wall" ? "Every finished take in the film — tap any into your story." : scene.synopsis}</p>
          </div>
          <div className="pg-itabs">
            {(["scenes", "wall"] as const).map((view) => (
              <button className={boardsView === view ? "active" : ""} key={view} onClick={() => setBoardsView(view)} type="button">
                {view === "scenes" ? "Scenes" : "Wall"}
              </button>
            ))}
          </div>
          {boardsView === "scenes" ? <span className="pg-chip">{frames.length} boards · {Math.round(scene.durationSeconds)}s</span> : null}
          {boardsView === "scenes" ? <span className="pg-chip ok">{generatedCount} ready</span> : null}
        </div>
        {boardsView === "wall" ? (
          <TakesWall
            onOpenScene={(sceneId) => { onSelectScene(sceneId); setBoardsView("scenes"); }}
            onSaveCutPlans={onSaveCutPlans}
            project={project}
          />
        ) : (
        <>
        <div className="pg-grid">
          {frames.map((frame, index) => {
            const src = frameDisplayUrl(frame, 576);
            const chip = frameChip(frame);
            const active = frame.id === selectedFrameId;
            return (
              <button
                className={`pg-shot-card${active ? " active" : ""}`}
                key={frame.id}
                onClick={() => {
                  setSelectedFrameId(active ? null : frame.id);
                  setInspectorTab("shot");
                }}
                type="button"
              >
                <span className="pg-shot-img">{src ? <img loading="lazy" src={src} alt={frame.label} /> : <i>{index + 1}</i>}</span>
                <span className="pg-row pg-shot-meta">
                  <small className="pg-grow pg-clip">{scene.number}{String.fromCharCode(65 + index)} · {frame.label || frame.shotType}</small>
                  {qcResults[frame.id] ? (
                    qcResults[frame.id].ok ? (
                      <span
                        className={`pg-chip ${qcResults[frame.id].severity === "ok" ? "ok" : qcResults[frame.id].severity === "minor" ? "warn" : "bad"}`}
                        title={[...(qcResults[frame.id].issues || []), qcResults[frame.id].note || ""].filter(Boolean).join(" · ")}
                      >
                        {qcResults[frame.id].severity === "ok" ? "QC ✓" : qcResults[frame.id].severity === "minor" ? "QC minor" : "QC re-do"}
                      </span>
                    ) : <span className="pg-chip" title={qcResults[frame.id].message}>QC ?</span>
                  ) : null}
                  <span className={`pg-chip ${chip.tone}`}>{chip.label}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="pg-dock">
          <small className="pg-muted">Shots:</small>
          {[3, 6, 9].map((count) => (
            <button className={`pg-pill ghost small${panels === count ? " active" : ""}`} key={count} onClick={() => setPanels(count)} type="button">
              {count}
            </button>
          ))}
          <span className="pg-grow" />
          <button
            className="pg-pill ghost"
            disabled={animaticBusy}
            onClick={() => void runAnimatic()}
            title="Cut every scene's boards to scratch AI dialogue + temp music — watch the whole film before spending a render credit"
            type="button"
          >
            {animaticBusy ? "Cutting…" : "🎞 Animatic"}
          </button>
          <button
            className="pg-pill ghost"
            disabled={readBusy || !dialogue.length}
            onClick={() => void runTableRead()}
            title="AI voices read this scene's dialogue aloud — hear the timing before you render"
            type="button"
          >
            {readBusy ? "Reading…" : "🎙 Table read"}
          </button>
          <button
            className="pg-pill ghost"
            disabled={busy || qcBusy || !generatedCount}
            onClick={() => void runBoardQc()}
            title="AI pre-render check on every finished board — catch a wrong face or place BEFORE paying for video"
            type="button"
          >
            {qcBusy ? "Checking…" : "🔍 QC boards"}
          </button>
          <button
            className="pg-pill ghost"
            disabled={busy}
            onClick={() => onRemakeScene({ sceneId: scene.id, panelsPerScene: panels, lookOverrides: {} })}
            title="Snapshot the current boards as a take, then regenerate the whole scene"
            type="button"
          >
            Re-make scene
          </button>
          <button
            className="pg-pill primary"
            disabled={busy}
            onClick={() => onGenerateScene(scene.id, panels)}
            title="Generate boards for open and failed slots — your uploaded or locked boards are never touched"
            type="button"
          >
            {busy ? "Working…" : "Generate boards"}
          </button>
        </div>
        {readNote ? <small className="pg-muted">{readNote}</small> : null}
        </>
        )}
      </section>

      {boardsView === "wall" ? null : (
      <aside className="pg-panel pg-inspector" aria-label="Inspector">
        <div className="pg-itabs" role="tablist">
          {(["scene", "shot", "notes"] as const).map((tab) => (
            <button
              aria-selected={inspectorTab === tab}
              className={inspectorTab === tab ? "active" : ""}
              key={tab}
              onClick={() => setInspectorTab(tab)}
              role="tab"
              type="button"
            >
              {tab === "scene" ? "Scene" : tab === "shot" ? "Shot" : "Notes"}
            </button>
          ))}
        </div>
        {inspectorTab === "scene" ? (
          <>
            <div className="pg-kv"><small>Duration</small><b>{Math.round(scene.durationSeconds)}s</b></div>
            <div className="pg-kv"><small>Chunks</small><b>{scene.chunks?.filter((c) => c.enabled).length || "—"}</b></div>
            <div className="pg-kv"><small>Readiness</small><b>{scene.readiness.replace(/_/g, " ")}</b></div>
            <hr className="pg-hr" />
            <span className="pg-eyebrow">In this scene</span>
            {cast.length ? cast.map((person) => {
              const look = pickLookForScene(person, scene)?.look || person.defaultLook;
              const src = lookUrl(look, 128);
              return (
                <div className="pg-cast-row" key={person.name}>
                  <span className="pg-face">{src ? <img src={src} alt="" /> : <i>{initials(person.name)}</i>}</span>
                  <span className="pg-grow">
                    <strong>{person.name}</strong>
                    <small className="pg-muted">{look ? look.parsed.lookLabel : "Look made for you"}</small>
                  </span>
                </div>
              );
            }) : <p className="pg-muted">No named cast in this scene.</p>}
            {directorPage?.directorIntent ? (
              <>
                <hr className="pg-hr" />
                <span className="pg-eyebrow">Director's page</span>
                <p className="pg-small">{directorPage.directorIntent}</p>
              </>
            ) : null}
            {takes.length ? (
              <>
                <hr className="pg-hr" />
                <span className="pg-eyebrow">Takes</span>
                {takes.map((take) => (
                  <button className="pg-take" key={take.id} onClick={() => onRestoreTake(scene.id, take.id)} type="button">
                    {take.label} · restore
                  </button>
                ))}
              </>
            ) : null}
          </>
        ) : null}
        {inspectorTab === "shot" ? (
          selectedFrame ? (
            <>
              <div className="pg-kv"><small>Shot</small><b>{selectedFrame.label || selectedFrame.shotType}</b></div>
              <div className="pg-kv"><small>Type</small><b>{selectedFrame.shotType}</b></div>
              <div className="pg-kv"><small>Hold</small><b>{selectedFrame.durationSeconds}s</b></div>
              <div className="pg-kv"><small>Status</small><b>{selectedFrame.source === "uploaded" ? "yours" : selectedFrame.generationStatus || "—"}</b></div>
              {selectedFrame.prompt ? (
                <>
                  <span className="pg-eyebrow">Prompt</span>
                  <p className="pg-small pg-prompt">{selectedFrame.prompt.slice(0, 420)}{selectedFrame.prompt.length > 420 ? "…" : ""}</p>
                </>
              ) : null}
              <span className="pg-grow" />
              <button
                className="pg-pill primary"
                disabled={busy || selectedFrame.locked || selectedFrame.source === "uploaded"}
                onClick={() => onRemakeScene({ sceneId: scene.id, frameIds: [selectedFrame.id], lookOverrides: {} })}
                type="button"
              >
                Re-make just this shot
              </button>
              {selectedFrame.locked || selectedFrame.source === "uploaded" ? (
                <small className="pg-muted">This board is yours — it's never overwritten.</small>
              ) : (
                <small className="pg-muted">≈ 30 seconds · the rest of the scene doesn't change.</small>
              )}
            </>
          ) : (
            <p className="pg-muted">Select a board to inspect it.</p>
          )
        ) : null}
        {inspectorTab === "notes" ? (
          <>
            <span className="pg-eyebrow">Director's notes</span>
            <textarea
              className="pg-notes"
              defaultValue={note}
              key={scene.id}
              onBlur={(event) => {
                if (event.target.value !== note) onSaveSceneNote(scene.id, event.target.value);
              }}
              placeholder="Performance, pacing, what to fix next pass…"
              rows={5}
            />
            <small className="pg-muted">Saved with the film · printed with the pitch.</small>
            {dialogue.length ? (
              <>
                <hr className="pg-hr" />
                <span className="pg-eyebrow">The words</span>
                <div className="pg-words">
                  {dialogue.slice(0, 6).map((line, index) => (
                    <p key={index}>{line.speaker ? <b>{line.speaker} </b> : null}{line.text}</p>
                  ))}
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </aside>
      )}
    </div>
  );
}

/* ---------- Production asset strips (Cast & Places) ---------- */

function isImageFileName(name: string): boolean {
  return /\.(png|jpe?g|webp|gif|bmp)$/i.test(name);
}

function ProAssetStrip({
  label,
  bucket,
  records,
  accept,
  onUploadAssets,
  query = ""
}: {
  label: string;
  bucket: keyof UploadedAssetManifest;
  records: UploadedAssetRecord[];
  accept: string;
  onUploadAssets: (bucket: keyof UploadedAssetManifest, files: File[]) => void;
  query?: string;
}) {
  const visible = records.filter((asset) => !asset.disabled && assetMatchesQuery(asset, query));
  return (
    <div className="pg-asset-panel">
      <div className="pg-row">
        <strong className="pg-grow">{label}</strong>
        <span className="pg-chip">{visible.length}</span>
      </div>
      <div className="pg-asset-mini-wall">
        {visible.map((asset) => {
          const key = asset.storageKey ? String(asset.storageKey) : "";
          const showImage = key && isImageFileName(asset.fileName);
          return (
            <div className="pg-asset-tile" key={asset.assetId || asset.fileName} title={asset.fileName}>
              <span className="pg-asset-thumb">
                {showImage ? (
                  <img alt="" loading="lazy" src={assetDisplayUrl(asset, 256) || keyUrl(key)} />
                ) : (
                  <i className="pg-file-glyph">{(asset.fileName.split(".").pop() || "file").toUpperCase().slice(0, 4)}</i>
                )}
              </span>
              <small>{(asset.label || asset.fileName).slice(0, 18)}</small>
            </div>
          );
        })}
        <label className="pg-asset-tile add">
          <input
            accept={accept}
            className="visually-hidden"
            multiple
            onChange={(event) => {
              const files = Array.from(event.target.files || []);
              if (files.length) onUploadAssets(bucket, files);
              event.target.value = "";
            }}
            type="file"
          />
          <span className="pg-asset-thumb dashed"><i>+</i></span>
          <small>Add</small>
        </label>
      </div>
    </div>
  );
}

/* ---------- Cast & Places ---------- */

function assetMatchesQuery(asset: UploadedAssetRecord, query: string): boolean {
  if (!query) return true;
  const haystack = `${asset.fileName} ${asset.label || ""} ${asset.characterName || ""} ${asset.kind || ""}`.toLowerCase();
  return query.toLowerCase().split(/\s+/).every((term) => !term || haystack.includes(term));
}

function CastingDesk({
  project,
  onPatchProject
}: {
  project: FilmProject;
  onPatchProject: (patch: Partial<FilmProject>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [suggestions, setSuggestions] = useState<CastingMatch[] | null>(null);
  const cards = (project.uploadedAssets?.characterCards || []).filter((asset) => !asset.disabled && asset.storageKey);
  const roles = project.characters.filter((character) => character.name.trim());
  const castingMap = project.castingMap || {};

  async function run() {
    setBusy(true);
    setError("");
    try {
      const result = await requestCasting(
        roles.map((character) => ({ name: character.name, description: character.role || "" })),
        cards.map((asset) => ({ storageKey: String(asset.storageKey), fileName: asset.fileName }))
      );
      if (!result.ok || !result.matches) {
        setError(result.message || "Casting pass failed.");
      } else {
        setSuggestions(result.matches);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Casting pass failed.");
    } finally {
      setBusy(false);
    }
  }

  function apply(match: CastingMatch) {
    if (!match.characterName) return;
    onPatchProject({ castingMap: { ...castingMap, [match.characterName]: match.storageKey } });
  }

  function applyAll() {
    const next = { ...castingMap };
    const taken = new Set(Object.values(next));
    for (const match of suggestions || []) {
      if (match.characterName && match.confidence >= 0.6 && !next[match.characterName] && !taken.has(match.storageKey)) {
        next[match.characterName] = match.storageKey;
        taken.add(match.storageKey);
      }
    }
    onPatchProject({ castingMap: next });
  }

  if (!roles.length || !cards.length) return null;
  return (
    <div className="pg-panel pg-casting-desk">
      <div className="pg-row">
        <strong className="pg-grow">AI casting</strong>
        <small className="pg-muted">{cards.length} card{cards.length === 1 ? "" : "s"} · {roles.length} role{roles.length === 1 ? "" : "s"}</small>
        <button className="pg-pill ghost small" disabled={busy} onClick={() => void run()} type="button">
          {busy ? "Casting…" : suggestions ? "Re-run" : "🎭 Cast with AI"}
        </button>
        {suggestions?.some((match) => match.characterName) ? (
          <button className="pg-pill primary small" onClick={applyAll} type="button">Apply all ≥60%</button>
        ) : null}
      </div>
      {error ? <small className="pg-muted">⚠ {error}</small> : null}
      {suggestions ? (
        <ul className="pg-ai-list">
          {suggestions.map((match) => {
            const applied = match.characterName && castingMap[match.characterName] === match.storageKey;
            return (
              <li className="pg-row" key={match.storageKey}>
                <span className="pg-grow">
                  <b>{match.fileName.slice(0, 28)}</b>
                  {match.characterName
                    ? <> → {match.characterName} <small className="pg-muted">({Math.round(match.confidence * 100)}% — {match.why})</small></>
                    : <small className="pg-muted"> — no role fits ({match.why})</small>}
                </span>
                {match.characterName ? (
                  applied
                    ? <span className="pg-chip ok">cast ✓</span>
                    : <button className="pg-pill ghost small" onClick={() => apply(match)} type="button">Apply</button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <small className="pg-muted">One cheap AI look per card matches your uploaded people to the script's roles.</small>
      )}
    </div>
  );
}

function IntelDesk({
  project,
  onPatchProject
}: {
  project: FilmProject;
  onPatchProject: (patch: Partial<FilmProject>) => void;
}) {
  const [busy, setBusy] = useState<"" | "bible" | "gaps">("");
  const [error, setError] = useState("");
  const [open, setOpen] = useState<"" | "bible" | "gaps">("");
  const bible = project.continuityBible;
  const gaps = project.gapReport;

  function assetLabels(): Array<{ fileName: string; label: string; bucket: string }> {
    const manifest = project.uploadedAssets;
    if (!manifest) return [];
    const out: Array<{ fileName: string; label: string; bucket: string }> = [];
    (Object.keys(manifest) as Array<keyof UploadedAssetManifest>).forEach((bucket) => {
      (manifest[bucket] || []).forEach((asset) => {
        if (!asset.disabled) out.push({ fileName: asset.fileName, label: asset.label || "", bucket: String(bucket) });
      });
    });
    return out.slice(0, 140);
  }

  async function run(mode: "bible" | "gaps") {
    setBusy(mode);
    setError("");
    try {
      const context = { ...storyAiContext(project), assets: assetLabels(), castingMap: project.castingMap || {} };
      const result = await askAssistant(mode, context);
      if (!result.ok || !result.answer) {
        setError(result.message || "The supervisor is unavailable right now.");
      } else {
        const stamped = { ...(result.answer as Record<string, unknown>), generatedAt: new Date().toISOString() };
        onPatchProject(mode === "bible" ? { continuityBible: stamped } : { gapReport: stamped });
        setOpen(mode);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setBusy("");
    }
  }

  const missing = objList(gaps?.missing);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceNote, setVoiceNote] = useState("");
  const unlabeledVoices = (project.uploadedAssets?.voiceAudio || [])
    .filter((asset) => !asset.disabled && asset.storageKey && (!asset.label || asset.label === asset.fileName));

  async function labelVoices() {
    setVoiceBusy(true);
    setVoiceNote("");
    const names = project.characters.map((character) => character.name);
    let labeled = 0;
    for (const asset of unlabeledVoices.slice(0, 10)) {
      try {
        const result = await requestVoiceLabel({ storageKey: String(asset.storageKey), fileName: asset.fileName, characters: names });
        if (result.ok && (result.label || result.characterName)) {
          labeled += 1;
          const manifest = project.uploadedAssets;
          if (manifest) {
            onPatchProject({
              uploadedAssets: {
                ...manifest,
                voiceAudio: manifest.voiceAudio.map((record) =>
                  record.assetId === asset.assetId
                    ? { ...record, label: result.label || record.label, characterName: result.characterName || record.characterName }
                    : record
                )
              }
            });
          }
        }
      } catch {
        // keep looping; one bad file shouldn't stop the librarian
      }
    }
    setVoiceNote(labeled ? `${labeled} read${labeled === 1 ? "" : "s"} transcribed and labeled.` : "Couldn't label those reads.");
    setVoiceBusy(false);
  }

  return (
    <div className="pg-panel pg-casting-desk">
      <div className="pg-row">
        <strong className="pg-grow">Production intelligence</strong>
        {unlabeledVoices.length ? (
          <button className="pg-pill ghost small" disabled={voiceBusy} onClick={() => void labelVoices()} type="button">
            {voiceBusy ? "Listening…" : `🎙 Label ${unlabeledVoices.length} voice${unlabeledVoices.length === 1 ? "" : "s"}`}
          </button>
        ) : null}
        <button className="pg-pill ghost small" disabled={busy !== ""} onClick={() => void run("bible")} type="button">
          {busy === "bible" ? "Writing…" : bible ? "Re-run bible" : "📖 Continuity bible"}
        </button>
        <button className="pg-pill ghost small" disabled={busy !== ""} onClick={() => void run("gaps")} type="button">
          {busy === "gaps" ? "Scouting…" : gaps ? "Re-run scout" : "🧭 Scout report"}
        </button>
      </div>
      {error ? <small className="pg-muted">⚠ {error}</small> : null}
      {voiceNote ? <small className="pg-muted">{voiceNote}</small> : null}
      {!bible && !gaps ? (
        <small className="pg-muted">
          The bible locks each character's look so every prompt stays consistent; the scout report writes your asset shopping list.
        </small>
      ) : null}
      {bible ? (
        <div className="pg-ai-doc">
          <button className="pg-row pg-ai-doc-head" onClick={() => setOpen(open === "bible" ? "" : "bible")} type="button">
            <strong className="pg-grow">Continuity bible</strong>
            <span className="pg-chip ok">{objList(bible.characters).length} cast · {objList(bible.places).length} places</span>
            <span className="pg-chip">{open === "bible" ? "▴" : "▾"}</span>
          </button>
          {open === "bible" ? (
            <>
              {objList(bible.characters).map((c, i) => (
                <p key={i}><b>{str(c.name)}</b> — {str(c.look)} {str(c.wardrobe) ? `· ${str(c.wardrobe)}` : ""} {str(c.props) ? `· ${str(c.props)}` : ""}</p>
              ))}
              {objList(bible.places).map((pl, i) => (
                <p className="pg-muted" key={i}><b>{str(pl.name)}</b> — {str(pl.light)} · {str(pl.palette)} {str(pl.dressing) ? `· ${str(pl.dressing)}` : ""}</p>
              ))}
              {strList(bible.rules).length ? <ul className="pg-ai-list">{strList(bible.rules).map((r, i) => <li key={i}>{r}</li>)}</ul> : null}
              <small className="pg-muted">Cut and beat drafts automatically weave these facts into prompts.</small>
            </>
          ) : null}
        </div>
      ) : null}
      {gaps ? (
        <div className="pg-ai-doc">
          <button className="pg-row pg-ai-doc-head" onClick={() => setOpen(open === "gaps" ? "" : "gaps")} type="button">
            <strong className="pg-grow">Scout report</strong>
            <span className={`pg-chip ${missing.length ? "warn" : "ok"}`}>{missing.length ? `${missing.length} to source` : "covered"}</span>
            <span className="pg-chip">{open === "gaps" ? "▴" : "▾"}</span>
          </button>
          {open === "gaps" ? (
            <>
              {str(gaps.summary) ? <p>{str(gaps.summary)}</p> : null}
              {missing.length ? (
                <ul className="pg-ai-list">
                  {missing.map((m, i) => (
                    <li key={i}><b>{str(m.scene)}</b> — {str(m.need)} <i className="pg-muted">({str(m.suggestion)})</i></li>
                  ))}
                </ul>
              ) : null}
              {strList(gaps.covered).length ? <small className="pg-muted">Covered: {strList(gaps.covered).join(" · ")}</small> : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ProCast({
  project,
  people,
  places,
  onSetVoicePreset,
  onUploadPersonPhoto,
  onUploadAssets,
  onSmartIngest,
  onPatchProject
}: {
  project: FilmProject;
  people: MatchedPerson[];
  places: MatchedPlace[];
  onSetVoicePreset: (characterId: string, preset: SeedanceVoicePreset) => void;
  onUploadPersonPhoto: (personName: string, files: File[]) => void;
  onUploadAssets: (bucket: keyof UploadedAssetManifest, files: File[]) => void;
  onSmartIngest: (files: File[]) => void;
  onPatchProject: (patch: Partial<FilmProject>) => void;
}) {
  const [filter, setFilter] = useState<"all" | "people" | "places" | "voices" | "assets">("all");
  const [query, setQuery] = useState("");
  const [openPerson, setOpenPerson] = useState<MatchedPerson | null>(null);
  const [openPlace, setOpenPlace] = useState<MatchedPlace | null>(null);
  const castInScript = people.filter((person) => person.inScript && (!query || person.name.toLowerCase().includes(query.toLowerCase())));
  const showPeople = filter === "all" || filter === "people" || filter === "voices";
  const showPlaces = filter === "all" || filter === "places";
  const showAssets = filter === "all" || filter === "assets";

  function voiceLabel(person: MatchedPerson): { label: string; tone: string } {
    const reads = readsForCharacter(project, person.name);
    if (reads.length) return { label: `Voice: your read${reads.length > 1 ? "s" : ""}`, tone: "ok" };
    const character = project.characters.find((c) => c.id === person.characterId || c.name === person.name);
    if (character?.voiceStatus === "not_needed") return { label: "Voice: none needed", tone: "" };
    const preset = VOICE_PRESETS.find((p) => p.id === character?.voicePreset);
    return { label: `Voice: preset · ${(preset?.label || "cinematic natural").toLowerCase()}`, tone: "" };
  }

  return (
    <div className="pg-body pg-cast">
      <div className="pg-row pg-cast-head">
        <div className="pg-grow">
          <h2>Everyone and everywhere</h2>
          <p className="pg-muted">Your uploads always win — generated looks only fill what's missing.</p>
        </div>
        <label className="pg-pill primary pg-smart-drop" title="Dump everything — AI files each item: characters, places, branding, music, docs">
          📥 Smart drop
          <input
            className="visually-hidden"
            multiple
            onChange={(event) => {
              const files = Array.from(event.target.files || []);
              if (files.length) onSmartIngest(files);
              event.target.value = "";
            }}
            type="file"
          />
        </label>
        <div className="pg-itabs">
          {(["all", "people", "places", "voices", "assets"] as const).map((f) => (
            <button className={filter === f ? "active" : ""} key={f} onClick={() => setFilter(f)} type="button">
              {f === "all" ? "All" : f[0].toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <input
        className="pg-input pg-asset-search"
        onChange={(event) => setQuery(event.target.value)}
        placeholder="🔎 Search everything — names, looks, places, files… (Smart Ingest labels make this smart)"
        type="search"
        value={query}
      />
      <CastingDesk onPatchProject={onPatchProject} project={project} />
      <IntelDesk onPatchProject={onPatchProject} project={project} />
      {showPeople ? (
        <>
          <span className="pg-eyebrow">People</span>
          <div className="pg-wall">
            {castInScript.map((person) => {
              const src = lookUrl(person.defaultLook, 384);
              const voice = voiceLabel(person);
              const fromUpload = person.looks.some((look) => look.asset.sourceMode === "user_upload");
              return (
                <button className="pg-person-card" key={person.name} onClick={() => setOpenPerson(person)} type="button">
                  <span className="pg-portrait">{src ? <img loading="lazy" src={src} alt={person.name} /> : <i>{initials(person.name)}</i>}</span>
                  <strong>{person.name}</strong>
                  <small className="pg-muted">
                    {person.looks.length ? `${person.looks.length} look${person.looks.length === 1 ? "" : "s"} · ${fromUpload ? "your photos" : "made for you"}` : "Made for you"}
                  </small>
                  <span className={`pg-chip ${voice.tone}`}>{voice.label}</span>
                  {project.castingMap?.[person.name] ? <span className="pg-chip ok">🎭 AI cast</span> : null}
                </button>
              );
            })}
            <label className="pg-person-card add">
              <input
                className="visually-hidden"
                type="file"
                multiple
                accept="image/*,audio/*"
                onChange={(event) => {
                  const files = Array.from(event.target.files || []);
                  const audio = files.filter((f) => f.type.startsWith("audio/"));
                  const visuals = files.filter((f) => !audio.includes(f));
                  if (visuals.length) onUploadAssets("characterCards", visuals);
                  if (audio.length) onUploadAssets("voiceAudio", audio);
                  event.target.value = "";
                }}
              />
              <span className="pg-portrait dashed"><i>+</i></span>
              <strong>Add photos</strong>
              <small className="pg-muted">or voice reads</small>
            </label>
          </div>
        </>
      ) : null}
      {showPlaces ? (
        <>
          <span className="pg-eyebrow">Places</span>
          <div className="pg-wall places">
            {places.filter((place) => !query || place.name.toLowerCase().includes(query.toLowerCase())).map((place) => {
              const src = lookUrl(place.defaultLook, 512);
              const fromUpload = place.looks.some((look) => look.asset.sourceMode === "user_upload");
              return src ? (
                <button className="pg-place-card" key={place.name} onClick={() => setOpenPlace(place)} type="button" title={`${place.name} — every plate`}>
                  <span className="pg-plate"><img loading="lazy" src={src} alt={place.name} /></span>
                  <strong>{place.name}</strong>
                  <small className="pg-muted">{place.looks.length} plate{place.looks.length === 1 ? "" : "s"} · {fromUpload ? "your photos" : "made for you"}</small>
                </button>
              ) : null;
            })}
            <label className="pg-place-card add">
              <input
                className="visually-hidden"
                type="file"
                multiple
                accept="image/*"
                onChange={(event) => {
                  const files = Array.from(event.target.files || []);
                  if (files.length) onUploadAssets("sceneReferences", files);
                  event.target.value = "";
                }}
              />
              <span className="pg-plate dashed"><i>+</i></span>
              <strong>Add place photos</strong>
            </label>
          </div>
        </>
      ) : null}
      {showAssets ? (
        <>
          <span className="pg-eyebrow">Production assets</span>
          <div className="pg-asset-grid">
            <ProAssetStrip query={query} accept="image/*,.png,.jpg,.jpeg,.webp" bucket="styleRefs" label="Style & AI refs" onUploadAssets={onUploadAssets} records={project.uploadedAssets?.styleRefs || []} />
            <ProAssetStrip query={query} accept="image/*,.pdf" bucket="branding" label="Branding & props" onUploadAssets={onUploadAssets} records={project.uploadedAssets?.branding || []} />
            <ProAssetStrip query={query} accept="video/*,.mp4,.mov,.webm" bucket="videoReferences" label="Video references" onUploadAssets={onUploadAssets} records={project.uploadedAssets?.videoReferences || []} />
            <ProAssetStrip query={query} accept="audio/*,.mp3,.wav,.m4a" bucket="music" label="Music" onUploadAssets={onUploadAssets} records={project.uploadedAssets?.music || []} />
            <ProAssetStrip query={query} accept="audio/*,.mp3,.wav,.m4a" bucket="sfx" label="SFX" onUploadAssets={onUploadAssets} records={project.uploadedAssets?.sfx || []} />
            <ProAssetStrip query={query} accept=".pdf,.docx,.txt,.md" bucket="documents" label="Documents" onUploadAssets={onUploadAssets} records={project.uploadedAssets?.documents || []} />
          </div>
        </>
      ) : null}
      <small className="pg-muted pg-tip">
        Tip: name files like “Elle Look 05 — Warehouse District.png” and scenes pick the right look automatically.
      </small>
      {openPerson ? (
        <PersonSheet
          project={project}
          person={openPerson}
          onClose={() => setOpenPerson(null)}
          onSetVoicePreset={onSetVoicePreset}
          onUploadPersonPhoto={onUploadPersonPhoto}
          onUploadAssets={onUploadAssets}
        />
      ) : null}
      {openPlace ? (
        <PlaceSheet place={openPlace} project={project} onClose={() => setOpenPlace(null)} onUploadAssets={onUploadAssets} />
      ) : null}
    </div>
  );
}

function PersonSheet({
  project,
  person,
  onClose,
  onSetVoicePreset,
  onUploadPersonPhoto,
  onUploadAssets
}: {
  project: FilmProject;
  person: MatchedPerson;
  onClose: () => void;
  onSetVoicePreset: (characterId: string, preset: SeedanceVoicePreset) => void;
  onUploadPersonPhoto: (personName: string, files: File[]) => void;
  onUploadAssets: (bucket: keyof UploadedAssetManifest, files: File[]) => void;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const character: Character | undefined = project.characters.find(
    (c) => c.id === person.characterId || c.name.toLowerCase() === person.name.toLowerCase()
  );
  const reads = readsForCharacter(project, person.name);
  const scenes = project.scenes.filter((scene) => scene.characters.some((n) => n.toLowerCase() === person.name.toLowerCase()));
  const faceSrc = lookUrl(person.defaultLook, 128);
  return (
    <div className="pg-sheet-backdrop" onClick={onClose} role="presentation">
      <section aria-modal="true" className="pg-panel pg-sheet" onClick={(event) => event.stopPropagation()} role="dialog" aria-label={`${person.name} — looks and voice`}>
        <div className="pg-row pg-sheet-head">
          <span className="pg-face big">{faceSrc ? <img src={faceSrc} alt="" /> : <i>{initials(person.name)}</i>}</span>
          <div className="pg-grow">
            <h2>{person.name}</h2>
            <p className="pg-muted">
              {scenes.length ? `In scene${scenes.length === 1 ? "" : "s"} ${scenes.map((s) => s.number).join(", ")}` : "Not in any scene yet"}
              {person.looks.length ? ` · ${person.looks.length} look${person.looks.length === 1 ? "" : "s"}` : ""}
            </p>
          </div>
          <button className="pg-pill primary" onClick={onClose} type="button">Done</button>
        </div>
        <span className="pg-eyebrow">Looks — your photos win, every time</span>
        <div className="pg-looks">
          {person.looks.slice(0, 8).map((look) => (
            <figure key={`${look.asset.assetId || look.asset.fileName}`}>
              <img loading="lazy" src={lookUrl(look, 256)} alt={look.parsed.lookLabel} />
              <figcaption>{look.parsed.lookLabel}</figcaption>
            </figure>
          ))}
          <label className="pg-look-add">
            <input
              className="visually-hidden"
              type="file"
              multiple
              accept="image/*"
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                if (files.length) onUploadPersonPhoto(person.name, files);
                event.target.value = "";
              }}
            />
            <i>+</i>
          </label>
        </div>
        <hr className="pg-hr" />
        <div className="pg-row">
          <span className="pg-eyebrow pg-grow">Voice</span>
          <span className="pg-chip">uploads always outrank presets</span>
        </div>
        <div className={`pg-reads${reads.length ? " active" : ""}`}>
          <div className="pg-grow">
            <strong>{reads.length ? "Your reads — in use" : "No reads uploaded yet"}</strong>
            <small className="pg-muted pg-clip">
              {reads.length ? reads.map((r) => r.fileName).join(" · ") : "Drop audio files named with the line's opening words."}
            </small>
          </div>
          {reads.length ? <span className="pg-chip ok">✓ active</span> : null}
          <label className="pg-pill ghost pg-file">
            Upload {reads.length ? "more" : "a read"}
            <input
              className="visually-hidden"
              type="file"
              multiple
              accept="audio/*"
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                if (files.length) onUploadAssets("voiceAudio", files);
                event.target.value = "";
              }}
            />
          </label>
        </div>
        <span className="pg-eyebrow">Or hand it to Seedance — ten performances, rendered with the film</span>
        <div className="pg-presets">
          {VOICE_PRESETS.map((preset) => {
            const active = character?.voicePreset === preset.id;
            return (
              <button
                className={`pg-preset${active ? " active" : ""}`}
                disabled={!character}
                key={preset.id}
                onClick={() => character && onSetVoicePreset(character.id, preset.id)}
                type="button"
              >
                <span className="pg-play">▶</span>
                <span className="pg-grow">
                  <strong>{preset.label}</strong>
                  <small className="pg-muted">{preset.blurb}</small>
                </span>
                {active ? <span className="pg-chip info">standby</span> : null}
              </button>
            );
          })}
        </div>
        <small className="pg-muted">
          Presets are performed at render time by Seedance using each line's emotion notes. Nothing is spent until you approve a render.
        </small>
      </section>
    </div>
  );
}

/** Every plate for one place — the browser the place cards open. */
function PlaceSheet({
  place,
  project,
  onClose,
  onUploadAssets
}: {
  place: MatchedPlace;
  project: FilmProject;
  onClose: () => void;
  onUploadAssets: (bucket: keyof UploadedAssetManifest, files: File[]) => void;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const scenes = project.scenes.filter((scene) => place.sceneIds.includes(scene.id));
  return (
    <div className="pg-sheet-backdrop" onClick={onClose} role="presentation">
      <section aria-modal="true" className="pg-panel pg-sheet" onClick={(event) => event.stopPropagation()} role="dialog" aria-label={`${place.name} — plates`}>
        <div className="pg-row pg-sheet-head">
          <div className="pg-grow">
            <h2>{place.name}</h2>
            <p className="pg-muted">
              {place.looks.length} plate{place.looks.length === 1 ? "" : "s"}
              {scenes.length ? ` · used in scene${scenes.length === 1 ? "" : "s"} ${scenes.map((s) => s.number).join(", ")}` : " · not matched to a scene yet"}
            </p>
          </div>
          <label className="pg-pill ghost pg-file">
            Add plates
            <input
              className="visually-hidden"
              type="file"
              multiple
              accept="image/*"
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                if (files.length) onUploadAssets("sceneReferences", files);
                event.target.value = "";
              }}
            />
          </label>
          <button className="pg-pill primary" onClick={onClose} type="button">Done</button>
        </div>
        <div className="pg-plate-grid">
          {place.looks.map((look) => (
            <figure key={`${look.asset.assetId || look.asset.fileName}`}>
              <img loading="lazy" src={lookUrl(look, 384)} alt={look.parsed.lookLabel} />
              <figcaption>
                <strong>{look.parsed.lookLabel}</strong>
                <small>{look.asset.sourceMode === "user_upload" ? "Your photo" : "Made for you"}</small>
              </figcaption>
            </figure>
          ))}
        </div>
        <small className="pg-muted">Scenes pick their plate by location automatically; swap any scene's plate from its scene sheet.</small>
      </section>
    </div>
  );
}

/* ---------- Delivery ---------- */

function ProDelivery({
  project,
  isSavingPackage,
  isSharingLink,
  onPackage,
  onShareLink,
  onSaveCutPlans,
  onPatchProject
}: {
  project: FilmProject;
  isSavingPackage: boolean;
  isSharingLink: boolean;
  onPackage: (gates: ReviewGateState) => Promise<boolean>;
  onShareLink: () => void;
  onSaveCutPlans: (store: { activeId: string; plans: CutPlanDoc[] }) => void;
  onPatchProject: (patch: Partial<FilmProject>) => void;
}) {
  const [gates, setGates] = useState<ReviewGateState>({
    scriptRights: false,
    likenessRights: false,
    voiceRights: false,
    brandIpRights: false,
    humanReview: false,
    finalConfirm: false
  });
  const allChecked = ALL_GATES.every((gate) => gates[gate.key]);
  const frames = project.scenes.flatMap((scene) => scene.frames.filter((f) => !f.disabled));
  const yourFrames = frames.filter((f) => f.source === "uploaded" || f.locked).length;
  const reads = (project.uploadedAssets?.voiceAudio || []).filter((a) => !a.disabled).length;
  const presets = project.characters.filter((c) => c.voiceStatus === "assigned" || c.voicePreset).length;
  const runtime = Math.round(project.scenes.reduce((sum, scene) => sum + scene.durationSeconds, 0));
  const packages = [...(project.productionPackages || [])].reverse();

  return (
    <div className="pg-body pg-delivery">
      <section className="pg-panel pg-cut" aria-label="This cut">
        <h2>This cut</h2>
        <p className="pg-muted">Everything the renderer needs, packaged from your boards.</p>
        <div className="pg-kv"><small>Scope</small><b>All {project.scenes.length} scenes</b></div>
        <div className="pg-kv"><small>Runtime</small><b>{Math.floor(runtime / 60)}:{String(runtime % 60).padStart(2, "0")}</b></div>
        <div className="pg-kv"><small>Boards</small><b>{frames.length} frames{yourFrames ? ` · ${yourFrames} yours` : ""}</b></div>
        <div className="pg-kv"><small>Voices</small><b>{reads} read{reads === 1 ? "" : "s"} · {presets} preset{presets === 1 ? "" : "s"}</b></div>
        <hr className="pg-hr" />
        <span className="pg-eyebrow">Before it can render</span>
        {ALL_GATES.map((gate) => (
          <label className="pg-gate" key={gate.key}>
            <input
              checked={gates[gate.key]}
              onChange={(event) => setGates((current) => ({ ...current, [gate.key]: event.target.checked }))}
              type="checkbox"
            />
            <span>{gate.label}</span>
          </label>
        ))}
        <button
          className="pg-pill primary"
          disabled={!allChecked || isSavingPackage}
          onClick={() => void onPackage(gates)}
          type="button"
        >
          {isSavingPackage ? "Packaging…" : "Package this cut"}
        </button>
        <small className="pg-muted">Dry run — builds the full package, spends nothing.</small>
      </section>

      <section className="pg-versions" aria-label="Versions">
        <div className="pg-row">
          <h2 className="pg-grow">Versions</h2>
          <span className="pg-chip">{packages.length} saved</span>
        </div>
        {packages.length ? packages.slice(0, 10).map((item) => (
          <div className="pg-panel pg-version" key={item.id}>
            <div className="pg-row">
              <strong className="pg-grow">{item.versionLabel || item.title}</strong>
              <span className={`pg-chip ${item.ready ? "ok" : "warn"}`} title={item.ready ? "ready" : String(item.status)}>
                {item.ready ? "ready" : String(item.status).replace(/_/g, " ")}
              </span>
            </div>
            <small className="pg-muted">
              {new Date(item.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              {" · "}
              {new Date(item.createdAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
              {" · "}{item.scopeLabel} · {item.frameCount} frames
            </small>
          </div>
        )) : (
          <p className="pg-muted">No packages yet — check the gates and package this cut.</p>
        )}
        <button className="pg-pill ghost" disabled={isSharingLink} onClick={onShareLink} type="button">
          {isSharingLink ? "Making link…" : "Copy share link"}
        </button>
        <div className="pg-row">
          <button
            className="pg-pill ghost small"
            onClick={() => downloadText(`${project.title.replace(/[^A-Za-z0-9 _-]+/g, "").trim() || "film"}.kinetix.json`, JSON.stringify(project, null, 2))}
            title="The whole project as one file — script, scenes, plans, notes, asset manifest. Media stays in storage."
            type="button"
          >
            ⬇ Export project
          </button>
          <label className="pg-pill ghost small pg-file">
            ⬆ Import
            <input
              accept=".json,application/json"
              className="visually-hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (!file) return;
                void file.text().then((raw) => {
                  try {
                    const parsed = JSON.parse(raw) as Partial<FilmProject>;
                    if (!parsed || typeof parsed !== "object" || !parsed.id) throw new Error("not a project file");
                    if (window.confirm(`Replace the current project with "${parsed.title || parsed.id}"? Your current project stays in its own save.`)) {
                      onPatchProject(parsed);
                    }
                  } catch {
                    window.alert("That file doesn't look like a Kinetix project export.");
                  }
                });
              }}
              type="file"
            />
          </label>
        </div>
      </section>

      <RenderBudgetCard project={project} gates={gates} allGatesChecked={allChecked} onSaveCutPlans={onSaveCutPlans} />

      <DoctorCard />
      <ExportsCard project={project} onPatchProject={onPatchProject} onSaveCutPlans={onSaveCutPlans} />
    </div>
  );
}

/* ---------- Render gate (Delivery right column) ---------- */

function keyUrl(storageKey: string): string {
  return `/api/assets/object/${storageKey.split("/").map(encodeURIComponent).join("/")}`;
}

/* ---------- Objective Cuts: plans as editable documents ---------- */

export type BeatAudio = "speaks" | "vo" | "stem" | "score" | "silent";

export interface BeatSpec {
  id: string;
  label: string;
  seconds: number;
  /** Legacy flag — superseded by `audio`; kept for old saved plans. */
  speak: boolean;
  enabled: boolean;
  prompt: string;
  kind?: "chunk" | "card";
  cardText?: string;
  audio?: BeatAudio;
  /** Uploaded read laid under this beat (stem) or lip-synced by the render (speaks). */
  stemKey?: string;
  sceneHeading?: string;
  /** Resolved asset-store keys, in @imageN order. */
  refs: string[];
  /** Loose asset-name hints (from drafts) resolved against the library at plan time. */
  refHints?: string[];
  audioKeys?: string[];
  /** SFX cue layered over this beat's audio at stitch. */
  sfxKey?: string;
  /** Structured dialogue for generated Seedance speech. Never injected into visual text prompts. */
  line?: string;
  spokenText?: string;
  speaker?: string;
  /** Stitch-time trim (the editor's scissors) — seconds into/out of the rendered clip. */
  inSeconds?: number;
  outSeconds?: number;
}

export type TrailerDirectorStyleId =
  | "auto"
  | "prestige_noir"
  | "horror_dread"
  | "sci_fi_awe"
  | "disaster_epic"
  | "action_bayhem"
  | "warm_indie"
  | "romance_glow"
  | "comedy_pop"
  | "documentary_grit";

export interface FinishSpec {
  preset: "trailer" | "cinematic" | "action" | "commercial" | "raw" | "custom";
  directorStyle?: TrailerDirectorStyleId;
  musicKey?: string;
  duck?: boolean;
  nativeLevel?: number;
  bedLevel?: number;
  /** Advanced overrides merged over the preset's look. */
  look?: Record<string, unknown>;
}

export const TRAILER_DIRECTOR_STYLES: Record<Exclude<TrailerDirectorStyleId, "auto">, { label: string; blurb: string; look: Record<string, unknown> }> = {
  prestige_noir: {
    label: "Prestige Noir",
    blurb: "Adult thriller, crime, conspiracy, serious drama",
    look: { grade: "noir", grain: 10, fade: 0.12, declick: 0.18, vignette: true, soften: true, fps24: true }
  },
  horror_dread: {
    label: "Horror Dread",
    blurb: "Supernatural, creature, home invasion, creeping terror",
    look: { grade: "horror_dread", grain: 13, fade: 0.18, declick: 0.2, vignette: true, soften: true, fps24: true }
  },
  sci_fi_awe: {
    label: "Sci-Fi Awe",
    blurb: "Speculative, alien, future tech, wonder and scale",
    look: { grade: "sci_fi_awe", grain: 7, fade: 0.08, declick: 0.18, vignette: true, soften: true, fps24: true }
  },
  disaster_epic: {
    label: "Disaster Epic",
    blurb: "Survival, extinction, weather, war, global stakes",
    look: { grade: "disaster_epic", grain: 11, fade: 0, declick: 0.22, vignette: true, soften: true, fps24: true }
  },
  action_bayhem: {
    label: "Action Bayhem",
    blurb: "Explosions, chases, militarized spectacle, hot color",
    look: { grade: "bayhem", grain: 12, fade: 0, declick: 0.2, vignette: true, soften: true, fps24: true }
  },
  warm_indie: {
    label: "Indie Warmth",
    blurb: "Grounded drama, family, coming-of-age, bittersweet",
    look: { grade: "warm_indie", grain: 6, fade: 0.22, declick: 0.16, vignette: false, soften: true, fps24: true }
  },
  romance_glow: {
    label: "Rom-Com Glow",
    blurb: "Chick flick, romance, heartfelt relationship drama",
    look: { grade: "romance_glow", grain: 5, fade: 0.2, declick: 0.14, vignette: false, soften: true, fps24: true }
  },
  comedy_pop: {
    label: "Comedy Pop",
    blurb: "Broad comedy, workplace, ensemble, upbeat social satire",
    look: { grade: "comedy_pop", grain: 4, fade: 0.08, declick: 0.12, vignette: false, soften: true, fps24: true }
  },
  documentary_grit: {
    label: "Documentary Grit",
    blurb: "True-story texture, realism, proof-of-concept grit",
    look: { grade: "documentary_grit", grain: 14, fade: 0, declick: 0.18, vignette: true, soften: true, fps24: true }
  }
};

const TRAILER_STYLE_LOOK_KEYS = ["grade", "grain", "fade", "fadeColor", "flashCut", "declick", "vignette", "soften", "fps24"];

function trailerStyleLook(style?: TrailerDirectorStyleId): Record<string, unknown> {
  if (!style || style === "auto") return {};
  return TRAILER_DIRECTOR_STYLES[style]?.look || {};
}

function normalizeTrailerStyle(value: unknown): TrailerDirectorStyleId {
  const style = String(value || "auto") as TrailerDirectorStyleId;
  if (style === "auto") return "auto";
  return TRAILER_DIRECTOR_STYLES[style as Exclude<TrailerDirectorStyleId, "auto">] ? style : "auto";
}

function stripTrailerStyleOverrides(look?: Record<string, unknown>): Record<string, unknown> {
  const next = { ...(look || {}) };
  for (const key of TRAILER_STYLE_LOOK_KEYS) delete next[key];
  return next;
}

function effectiveFinishLook(finish: FinishSpec): Record<string, unknown> {
  return {
    ...(FINISH_PRESETS[finish.preset]?.look || {}),
    ...(finish.preset === "trailer" ? trailerStyleLook(finish.directorStyle) : {}),
    ...(finish.look || {})
  };
}

function isTrailerObjective(objectiveId: string): boolean {
  return ["trailer", "teaser", "sizzle", "social"].includes(objectiveId);
}

/** One-tap finishing — every knob from the real sessions, bundled. */
export const FINISH_PRESETS: Record<string, { label: string; blurb: string; look: Record<string, unknown> }> = {
  trailer: {
    label: "🎟 Trailer Final",
    blurb: "Score bed, risers, loudness glue; style chosen below",
    look: { letterbox: "2.39", grain: 8, fade: 0, declick: 0.2, vignette: true, soften: true, fps24: true, loudnorm: true, typography: true, cardRiser: true, trailerBed: true, trailerFinal: true }
  },
  cinematic: {
    label: "🎬 Cinematic",
    blurb: "Widescreen, film grain, gentle dips, rich color",
    look: { letterbox: "2.39", grain: 8, fade: 0.3, vignette: true, grade: "teal_orange", soften: true, fps24: true, loudnorm: true, typography: true, cardRiser: true }
  },
  action: {
    label: "💥 Action Bayhem",
    blurb: "Hard cuts, hot color, heavy grain, big spectacle",
    look: { letterbox: "2.39", grain: 12, fade: 0, declick: 0.2, vignette: true, grade: "bayhem", soften: true, fps24: true, loudnorm: true, typography: true, cardRiser: true }
  },
  commercial: {
    label: "📺 TV Commercial",
    blurb: "Clean broadcast color, eased cuts, score-ready mix",
    look: { grain: 5, fade: 0, declick: 0.2, grade: "broadcast", soften: true, fps24: true, loudnorm: true }
  },
  raw: {
    label: "🎞️ Raw",
    blurb: "Exactly what rendered — no treatment",
    look: {}
  }
};

export function defaultFinishForObjective(objectiveId: string): FinishSpec {
  if (objectiveId === "commercial") return { preset: "commercial", duck: true, nativeLevel: 0.84, bedLevel: 0.32 };
  if (isTrailerObjective(objectiveId)) return { preset: "trailer", directorStyle: "auto", duck: true, nativeLevel: 0.72, bedLevel: 0.55 };
  if (["previz", "broll", "plates"].includes(objectiveId)) return { preset: "raw", duck: true };
  return { preset: "cinematic", duck: true };
}

export interface CutPlanDoc {
  id: string;
  objectiveId: string;
  title: string;
  beats: BeatSpec[];
  /** Narrator reads, in order, laid across `vo` beats at stitch. */
  voiceKeys: string[];
  openingCard: string;
  aspect?: string;
  finish?: FinishSpec;
  updatedAt: string;
}

export function beatAudio(beat: BeatSpec): BeatAudio {
  if (beat.kind === "card") return "silent";
  if (beat.audio) return beat.audio;
  return beat.speak ? "speaks" : "vo";
}

function cutPlansStorageKey(projectId: string): string {
  return `kinetix:cutplans:${projectId}`;
}

export function loadCutPlanStore(project: FilmProject): { activeId: string; plans: CutPlanDoc[] } {
  const stored = project.cutPlans as { activeId?: string; plans?: CutPlanDoc[] } | undefined;
  if (stored && Array.isArray(stored.plans) && stored.plans.length) {
    return { activeId: String(stored.activeId || ""), plans: stored.plans };
  }
  // One-time migration from the Phase-1 browser-local store.
  const legacy = loadCutPlansLocal(project.id);
  return legacy;
}

function loadCutPlansLocal(projectId: string): { activeId: string; plans: CutPlanDoc[] } {
  try {
    const raw = window.localStorage.getItem(cutPlansStorageKey(projectId));
    if (raw) {
      const parsed = JSON.parse(raw) as { activeId?: string; plans?: CutPlanDoc[] };
      if (Array.isArray(parsed.plans)) return { activeId: String(parsed.activeId || ""), plans: parsed.plans };
    }
    // Migrate the Phase-1 single-plan format.
    const legacy = window.localStorage.getItem(`kinetix:cutplan:${projectId}`);
    if (legacy) {
      const beats = JSON.parse(legacy);
      if (Array.isArray(beats) && beats.length) {
        const doc: CutPlanDoc = {
          id: `plan-${Date.now().toString(36)}`,
          objectiveId: "custom",
          title: "Custom plan",
          beats,
          voiceKeys: [],
          openingCard: "",
          updatedAt: new Date().toISOString()
        };
        window.localStorage.removeItem(`kinetix:cutplan:${projectId}`);
        const store = { activeId: doc.id, plans: [doc] };
        window.localStorage.setItem(cutPlansStorageKey(projectId), JSON.stringify(store));
        return store;
      }
    }
  } catch {
    // fall through to empty
  }
  return { activeId: "", plans: [] };
}

export function saveCutPlans(projectId: string, store: { activeId: string; plans: CutPlanDoc[] }): void {
  try {
    window.localStorage.setItem(cutPlansStorageKey(projectId), JSON.stringify(store));
  } catch {
    // storage full/blocked — the session still works, the plans just won't persist
  }
}

/** Exact filename first, then prefix, then substring — "man-card.png" can never
    accidentally land on "business-man-card.png". */
function pickFromPool(pool: Array<{ fileName: string; storageKey?: unknown }>, tokens: string[]): string {
  for (const token of tokens) {
    const exact = pool.find((a) => a.fileName.toLowerCase() === token);
    if (exact?.storageKey) return String(exact.storageKey);
  }
  for (const token of tokens) {
    const starts = pool.find((a) => a.fileName.toLowerCase().startsWith(token));
    if (starts?.storageKey) return String(starts.storageKey);
  }
  for (const token of tokens) {
    const within = pool.find((a) => a.fileName.toLowerCase().includes(token));
    if (within?.storageKey) return String(within.storageKey);
  }
  return "";
}

const RENDER_DIALOGUE_PLACEHOLDER = "the provided spoken line";
const RENDER_PROMPT_GUARD =
  "Dialogue and script lines are audio/performance only; never display their words. No speech bubbles, dialogue boxes, game HUD, menu boxes, subtitles, captions, lower thirds, readable UI overlays, real-world brand logos, copyrighted logos, watermarks, random website text, garbled or misspelled signage, or recognizable celebrity lookalikes.";

function sanitizeRenderPromptText(value: unknown): string {
  let base = String(value || "Cinematic scene as boarded.").replace(/\s+/g, " ").trim();
  base = base.replace(
    /\b(speaks?|says?|whispers?|shouts?|asks?|replies?|delivers?|dialogue|line|narration|narrator(?: line)?|voiceover|voice-over)([^.!?]{0,100}?)[\"“][^\"”]{3,260}[\"”]/gi,
    (_match, cue: string, bridge: string) => `${cue}${bridge} ${RENDER_DIALOGUE_PLACEHOLDER}`
  );
  base = base.replace(
    /\b([A-Z][A-Z0-9 .'-]{1,28}):\s*[\"“][^\"”]{3,220}[\"”]/g,
    (_match, speaker: string) => `${speaker} has a spoken line (audio only)`
  );
  return base;
}

function safeRenderPrompt(value: unknown, limit = 945): string {
  let base = sanitizeRenderPromptText(value);
  if (!base.toLowerCase().includes("dialogue and script lines are audio/performance only")) {
    const reserve = Math.max(0, limit - RENDER_PROMPT_GUARD.length - 1);
    base = `${base.slice(0, reserve).trimEnd()} ${RENDER_PROMPT_GUARD}`.trim();
  }
  return base.slice(0, limit);
}

function RenderBudgetCard({
  project,
  gates,
  allGatesChecked,
  onSaveCutPlans
}: {
  project: FilmProject;
  gates: ReviewGateState;
  allGatesChecked: boolean;
  onSaveCutPlans: (store: { activeId: string; plans: CutPlanDoc[] }) => void;
}) {
  const [gate, setGate] = useState<RenderGateStatus | null>(null);
  const [quality, setQuality] = useState<"480p" | "720p" | "1080p">("480p");
  const [estimate, setEstimate] = useState<RenderEstimate | null>(null);
  const [cap, setCap] = useState(0);
  const [job, setJob] = useState<RenderJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [planMode, setPlanMode] = useState<"film" | "cuts">("film");
  const [stitching, setStitching] = useState<StitchProgress | null>(null);
  const [stitchNote, setStitchNote] = useState("");

  /** The Membank infomercial, to the letter of the script: chyron card, four spoken
      "problem" vignettes (Seedance native dialogue), then the narrator section — four
      recorded reads in SCRIPT order laid over silent beats at stitch time. */
  const VO_ORDER = ["It_only_takes_a_second", "Side_effects_may_include", "If_youre_one_of_the_millions", "With_the_help_of_a_Membank"];
  const CHYRON_TEXT = "The following program is brought to you by\nMembank Laboratory Technologies©";
  /** Vignette dialogue is structured voice data. If a matching uploaded stem exists,
      Seedance lip-syncs to @audio1; otherwise it generates the spoken line natively. */
  const VIG_LINES: Array<{ vig: string; speaker: string; tokens: string[]; line: string }> = [
    { vig: "vig_1", speaker: "Dana", tokens: ["vig1", "You_still_have_the_kids"], line: "You still have the kids to think about. You can't let one incident affect you like this forever." },
    { vig: "vig_2", speaker: "The daughter", tokens: ["vig2", "Dad_whats_the_matter"], line: "Dad, what's the matter? You always look so upset." },
    { vig: "vig_3", speaker: "The executive", tokens: ["vig3", "You_havent_been_the_same"], line: "You haven't been the same since you lost the Foster account. I need you to get your head back in the game." },
    { vig: "vig_4", speaker: "The woman at dinner", tokens: ["vig4", "Your_mind_always_seems"], line: "Your mind always seems to be somewhere else. I don't know how much longer I can live like this." }
  ];
  function vignetteLine(vig: string) {
    return VIG_LINES.find((v) => v.vig === vig);
  }
  function vignetteLineKey(vig: string): string {
    const norm = (s: string) => s.replace(/[^a-zA-Z0-9]+/g, "_").toLowerCase();
    const reads = (project.uploadedAssets?.voiceAudio || []).filter((a) => !a.disabled && a.storageKey);
    const spec = vignetteLine(vig);
    if (!spec) return "";
    for (const token of spec.tokens) {
      const hit = reads.find((a) => norm(a.fileName).includes(norm(token)));
      if (hit?.storageKey) return String(hit.storageKey);
    }
    return "";
  }
  function infomercialVoiceKeys(): string[] {
    const reads = (project.uploadedAssets?.voiceAudio || []).filter((a) => !a.disabled && a.storageKey);
    return VO_ORDER
      .map((prefix) => reads.find((a) => a.fileName.replace(/[^a-zA-Z]/g, "_").startsWith(prefix.replace(/[^a-zA-Z]/g, "_"))))
      .filter((a): a is NonNullable<typeof a> => Boolean(a))
      .map((a) => String(a.storageKey));
  }
  const assetPools = useMemo(() => ({
    refs: (project.uploadedAssets?.sceneReferences || []).filter((a) => !a.disabled && a.storageKey),
    cards: (project.uploadedAssets?.characterCards || []).filter((a) => !a.disabled && a.storageKey),
    videos: (project.uploadedAssets?.videoReferences || []).filter((a) => !a.disabled && a.storageKey),
    styles: (project.uploadedAssets?.styleRefs || []).filter((a) => !a.disabled && a.storageKey),
    branding: (project.uploadedAssets?.branding || []).filter((a) => !a.disabled && a.storageKey)
  }), [project]);
  const resolveHints = (hints: string[] | undefined) =>
    (hints || [])
      .map((hint) => pickFromPool(
        [...assetPools.cards, ...assetPools.refs, ...assetPools.branding, ...assetPools.styles, ...assetPools.videos],
        [hint.toLowerCase()]
      ))
      .filter(Boolean);
  const isVideoKey = (key: string) => /\.(mp4|mov|webm|m4v)$/i.test(key);

  /** The script's scene-1 infomercial as DATA. The Cut Composer edits a copy of this;
      a saved plan wins over these defaults. */
  function defaultInfomercialBeats(): BeatSpec[] {
    const plate = (...tokens: string[]) => pickFromPool(assetPools.refs, tokens);
    const card = (...tokens: string[]) => pickFromPool(assetPools.cards, tokens);
    const NEG = "No visible text, no captions, no titles, no subtitles, no speech bubbles, no dialogue boxes, no game HUD, no logos, no watermarks, no graphics overlays.";
    const STYLE = "Polished retro-future TV commercial, dramatized infomercial reenactment acting — sincere, a touch theatrical. Anamorphic lens, gentle film grain, glossy advertising light, cinematic widescreen.";
    const SPEAK = "Single speaker facing camera at a natural conversational angle, clear unobstructed mouth for accurate lip-sync, no other voices.";
    const CASTING = "Cast ordinary, specific non-celebrity faces; avoid any recognizable movie-star likeness.";
    const TEXT_SAFE = "Treat background signage as abstract light panels unless it is an approved fictional Membank graphic; no random words, no misspellings.";
    const PUSH = "This is one continuous very slow dolly push-in across the problem-vignette sequence: each new shot begins at the framing scale where the previous shot ended, matching eye height and forward drift.";
    // The script's actual infomercial, beat for beat. Vignettes SPEAK (Seedance native
    // dialogue, generateAudio:true); narrator beats are silent — the four recorded
    // reads are laid over them at stitch time, in script order.
    const beats: Array<Omit<BeatSpec, "label" | "enabled">> = [
      // ---- Problem vignettes: visible speakers, generated or audio-reference dialogue,
      // no captions/dialogue boxes/script text on the image. ----
      { id: "vig_1", seconds: 7, speak: true, line: vignetteLine("vig_1")?.line, spokenText: vignetteLine("vig_1")?.line, speaker: vignetteLine("vig_1")?.speaker, audioKeys: [vignetteLineKey("vig_1")].filter(Boolean), refs: [card("present-day-dana", "dana")], prompt: `Shot 1 of the continuous push-in: begin in a wide upscale kitchen tableau and creep forward until it ends as a medium-wide speaking frame. Dramatized TV-commercial vignette: the woman from @image1 stands at the granite island, morning light through the window, speaking the provided line with tense concern and restrained emotion, eyes wet, jaw tight, hands on the countertop. She is the only speaker, mouth clear and unobstructed for natural lip-sync. ${PUSH} ${CASTING} ${SPEAK} Slightly desaturated everyday-drama grading. ${STYLE} ${NEG}` },
      { id: "vig_2", seconds: 5, speak: true, line: vignetteLine("vig_2")?.line, spokenText: vignetteLine("vig_2")?.line, speaker: vignetteLine("vig_2")?.speaker, audioKeys: [vignetteLineKey("vig_2")].filter(Boolean), refs: [card("teenage-girl-card.png", "teenage")], prompt: `Shot 2 of the continuous push-in: start at the medium-wide scale where the kitchen shot ended and move slowly inward to a medium speaking frame. Dramatized TV-commercial vignette: the teenage daughter from @image1 stands on a staircase landing in a suburban tract home, leaning over the rail and speaking the provided line with scared tenderness toward her father below camera. She is the only speaker, mouth clear and unobstructed for natural lip-sync. ${PUSH} ${CASTING} ${SPEAK} Slightly desaturated everyday-drama grading. ${STYLE} ${NEG}` },
      { id: "vig_3", seconds: 8, speak: true, line: vignetteLine("vig_3")?.line, spokenText: vignetteLine("vig_3")?.line, speaker: vignetteLine("vig_3")?.speaker, audioKeys: [vignetteLineKey("vig_3")].filter(Boolean), refs: [card("business-man-card.png", "business")], prompt: `Shot 3 of the continuous push-in: start at the same medium scale where the staircase shot ended and drift inward to a medium close-up. Dramatized TV-commercial vignette: the business man from @image1 sits at the head of a long conference table in a corporate office, city light behind glass, addressing someone across the table with measured frustration. He speaks the provided line with precise natural lip-sync; the words are audio only and never visible on screen. Firm but not unkind. ${PUSH} ${CASTING} ${SPEAK} Slightly desaturated corporate grading. ${STYLE} ${NEG}` },
      { id: "vig_4", seconds: 7, speak: true, line: vignetteLine("vig_4")?.line, spokenText: vignetteLine("vig_4")?.line, speaker: vignetteLine("vig_4")?.speaker, audioKeys: [vignetteLineKey("vig_4")].filter(Boolean), refs: [card("woman-card.png", "woman")], prompt: `Shot 4 of the continuous push-in: start at the same medium close-up scale where the boardroom shot ended and complete the slow move into an intimate close-up. Dramatized TV-commercial vignette: the woman from @image1 sits at a candlelit table for two in an intimate restaurant, wine glasses catching the light, speaking quietly across the table with hurt in her voice. She speaks the provided line with precise natural lip-sync; the words are audio only and never visible on screen. Wounded, sincere delivery. ${PUSH} ${CASTING} ${SPEAK} Warm low candlelight, shallow depth of field. ${STYLE} ${NEG}` },
      // ---- Narrator section (silent; recorded VO laid at stitch, script order) ----
      { id: "promo_1", seconds: 15, speak: false, refs: [plate("pedestrian", "street-level"), plate("hero-establishing"), plate("membank")], prompt: `Time-lapse photography: dense, racially mixed crowds of pedestrians stream through the large city square of @image2 near @image1, crisscrossing in every direction and paying no attention to one another — long-exposure motion trails, the lonely-in-a-crowd feeling, the fictional Membank billboard shape of @image3 glowing above as abstract light. ${TEXT_SAFE} Muted cool grading warming slightly toward the end. ${STYLE} ${NEG}` },
      { id: "promo_2", seconds: 15, speak: false, refs: [plate("street-level", "pedestrian"), plate("low-angle", "intersection"), plate("membank")], prompt: `Continuing time-lapse of anonymous crowds flowing through the streets of @image1 and the towers of @image2, day shifting toward golden afternoon, faces softly out of focus, the fictional Membank billboard shape of @image3 presiding over the masses — then the flow gently slows to normal speed as warmth enters the color. ${TEXT_SAFE} ${STYLE} ${NEG}` },
      { id: "promo_3", seconds: 14, speak: false, refs: [card("man-card.png"), card("teenage-girl-card.png", "teenage")], prompt: `A series of home-movie-style scenes, warm nostalgic Super-8 texture with soft gate weave: the father from @image1 and the daughter from @image2 laughing in a sunlit backyard, blowing out birthday candles, pushing a swing, a picnic blanket in golden light — smiling children and adults across varied races, ages, and body types in pleasant ordinary situations. ${CASTING} Saturated honeyed home-video warmth. ${NEG}` },
      { id: "promo_4", seconds: 12, speak: false, refs: [card("hansen-look", "hansen"), plate("street-level", "pedestrian")], prompt: `A dignified cross-section of people who feel left behind by the memory economy: mixed races, ages, genders, and body types in varied real-world settings — a clinic waiting room, an apartment courtyard, a rural porch, a bus stop, a clean modest office break room, the man from @image1 near his trailer as only one example. No one race represents hardship; no dangerous-neighborhood shorthand. Each person looks warmly into camera, hopeful and proud, handheld documentary sincerity. ${CASTING} Honest warm grading, gentle lens flare. ${STYLE} ${NEG}` },
      { id: "promo_5", seconds: 8, speak: false, refs: [card("woman-card.png", "woman"), card("man-card.png")], prompt: `The woman from @image1 slow-dances with her husband from @image2 in a softly lit living room at dusk, her head nestled affectionately against his chest, eyes closed in peace, fairy lights bokeh behind them — slow romantic camera orbit. ${CASTING} Fully warm, healed, golden grading. ${STYLE} ${NEG}` },
      { id: "promo_6", seconds: 6, speak: false, refs: [card("membank"), plate("hero-establishing"), plate("membank")], prompt: `A checkerboard of images coalesces: a grid mosaic of racially mixed people of different ages and nationalities engaged in various sublime activities — dancing, graduating, embracing, sailing — tiles drifting together over the skyline of @image2, resolving into the Membank brand glow of @image1. End locked on a clean closing card reading exactly "YOUR FUTURE IS WAITING" in elegant Membank brand typography. That is the ONLY text on screen — no other text, no captions, no logos, no watermarks. ${STYLE}` }
    ];
    return beats.map((beat) => ({
      ...beat,
      enabled: true,
      label: beat.id.startsWith("vig_") ? beat.id.replace("vig_", "vignette ") : beat.id.replace("promo_", "narrator "),
      refs: beat.refs.filter(Boolean)
    }));
  }

  /** The Membank infomercial as a built-in seed plan — proof the format works; copy it
      to make it yours. Only offered when its assets resolve in this project. */
  function infomercialSeedPlan(): CutPlanDoc | null {
    const beats = defaultInfomercialBeats().map((beat) => {
      const vig = beat.id.startsWith("vig_");
      const audio: BeatAudio = vig
        ? (beat.speak ? "speaks" : "stem")
        : "vo";
      const stemKey = vig ? (beat.speak ? (beat.audioKeys || [])[0] || "" : vignetteLineKey(beat.id)) : "";
      return { ...beat, kind: "chunk" as const, audio, stemKey };
    });
    if (!beats.some((beat) => beat.refs.length)) return null;
    return {
      id: "builtin-infomercial",
      objectiveId: "commercial",
      title: "Membank infomercial (script)",
      beats,
      voiceKeys: infomercialVoiceKeys(),
      openingCard: CHYRON_TEXT,
      updatedAt: ""
    };
  }

  const planStore = loadCutPlanStore(project);
  const seedPlan = infomercialSeedPlan();
  const allPlans: CutPlanDoc[] = [...planStore.plans, ...(seedPlan ? [seedPlan] : [])];
  const activePlan: CutPlanDoc | null =
    allPlans.find((plan) => plan.id === planStore.activeId) || allPlans[0] || null;
  function updatePlans(next: { activeId: string; plans: CutPlanDoc[] }) {
    onSaveCutPlans(next);
    saveCutPlans(project.id, next); // browser copy doubles as offline backup
  }
  function upsertPlan(doc: CutPlanDoc) {
    const stamped = { ...doc, updatedAt: new Date().toISOString() };
    const isBuiltin = doc.id === "builtin-infomercial";
    const real: CutPlanDoc = isBuiltin ? { ...stamped, id: `plan-${Date.now().toString(36)}`, title: doc.title.replace(" (script)", "") } : stamped;
    const existing = planStore.plans.some((plan) => plan.id === real.id);
    updatePlans({
      activeId: real.id,
      plans: existing ? planStore.plans.map((plan) => (plan.id === real.id ? real : plan)) : [...planStore.plans, real]
    });
  }

  /** Render chunks from the active plan — cards are stitch-time, not render-time. */
  function cutScenesPayload() {
    const scene = project.scenes[0];
    if (!scene || !activePlan) return [];
    return activePlan.beats
      .filter((beat) => beat.enabled !== false && (beat.kind || "chunk") === "chunk")
      .map((beat) => {
        const audio = beatAudio(beat);
        const allRefs = [...beat.refs, ...resolveHints(beat.refHints)].filter(Boolean);
        return {
          sceneId: `${scene.id}::${beat.id}`,
          label: `${activePlan.title} · ${beat.label || beat.id}`,
          durationSeconds: beat.seconds,
          prompt: safeRenderPrompt(beat.prompt),
          imageKeys: allRefs.filter((key) => !isVideoKey(key)).slice(0, 9),
          videoKeys: allRefs.filter(isVideoKey).slice(0, 3),
          audioKeys: (audio === "speaks" && beat.stemKey ? [beat.stemKey] : (beat.audioKeys || [])).slice(0, 1),
          generateAudio: audio === "speaks" || audio === "score",
          spokenText: audio === "speaks" ? (beat.spokenText || beat.line || "") : "",
          line: audio === "speaks" ? (beat.line || beat.spokenText || "") : "",
          speaker: audio === "speaks" ? (beat.speaker || "") : ""
        };
      });
  }

  async function handleStitch() {
    if (!job || stitching) return;
    setStitchNote("");
    // The active plan IS the edit decision list: cards become title cards, `speaks`
    // beats keep their lip-synced native audio, `stem` beats carry their pristine
    // uploaded read, `vo` beats receive the narrator track in order.
    const plan = planMode === "cuts" ? activePlan : null;
    const voiceKeys = plan ? plan.voiceKeys : [];
    const scene = project.scenes[0];
    const finished = new Map(job.tasks.filter((t) => t.videoKey).map((t) => [t.chunkId, t]));
    let timeline: Array<Record<string, unknown>> | undefined;
    let expectedChunkIds: string[] = [];
    if (plan && scene) {
      timeline = [];
      if (plan.openingCard) timeline.push({ type: "card", text: plan.openingCard, seconds: 3 });
      for (const beat of plan.beats) {
        if (beat.enabled === false) continue;
        if ((beat.kind || "chunk") === "card") {
          if (beat.cardText) timeline.push({ type: "card", text: beat.cardText, seconds: Math.max(2, Math.min(6, beat.seconds || 3)) });
          continue;
        }
        const chunkId = `${scene.id}::${beat.id}`;
        expectedChunkIds.push(chunkId);
        if (!finished.has(chunkId)) continue;
        const audio = beatAudio(beat);
        timeline.push({
          type: "chunk",
          chunkId,
          ...(beat.inSeconds ? { inSeconds: beat.inSeconds } : {}),
          ...(beat.outSeconds ? { outSeconds: beat.outSeconds } : {}),
          ...(beat.sfxKey ? { sfxKey: beat.sfxKey } : {}),
          ...(audio === "stem" && beat.stemKey
            ? { audio: "file", audioKey: beat.stemKey }
            : { audio: audio === "vo" ? "vo" : "native" })
        });
      }
      if (!timeline.some((item) => item.type === "chunk")) timeline = undefined;
    }
    const hasVignettes = Boolean(timeline);
    // One-tap finishing: the plan's preset (or the objective's smart default) becomes
    // the full look payload; music bed + ducking ride along when chosen.
    const finish = plan ? (plan.finish || defaultFinishForObjective(plan.objectiveId)) : null;
    const finishPayload: Record<string, unknown> = {};
    if (finish && timeline) {
      const look = effectiveFinishLook(finish);
      const trailerLike = Boolean(plan && isTrailerObjective(plan.objectiveId));
      finishPayload.look = look;
      if (plan) {
        finishPayload.objectiveId = plan.objectiveId;
        finishPayload.expectedChunkIds = expectedChunkIds;
      }
      if (trailerLike) {
        finishPayload.trailerFinal = true;
        finishPayload.directorStyle = finish.directorStyle || "auto";
      }
      if (!finish.musicKey && look.trailerBed) finishPayload.syntheticBed = true;
      if (finish.musicKey || look.trailerBed) {
        if (finish.musicKey) finishPayload.musicKey = finish.musicKey;
        finishPayload.duck = finish.duck !== false;
        finishPayload.nativeLevel = finish.nativeLevel ?? (trailerLike ? 0.72 : 1.0);
        finishPayload.bedLevel = finish.bedLevel ?? (trailerLike ? 0.55 : 0.65);
      }
    }
    // Master quality first: the engine runs the assembly as a JOB — no proxy or browser
    // timeout can sever it — and we poll real ffmpeg progress.
    const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const downloadMaster = (storageKey: string, bytes: number, notes?: string[]) => {
      setStitching(null);
      const extra = notes && notes.length ? ` (${notes.join(" · ")})` : "";
      setStitchNote(`Master saved to your library (${(bytes / 1e6).toFixed(1)} MB)${extra} — downloading…`);
      const anchor = document.createElement("a");
      anchor.href = keyUrl(storageKey);
      anchor.download = `${project.title} — infomercial.mp4`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    };
    setStitching({ elapsedSeconds: 0, totalSeconds: 0, segmentLabel: "engine master…" });
    let engineFailure = "";
    try {
      const started = await startStitchJobOnEngine({ jobId: job.id, voiceKeys, timeline, title: `${project.title} — ${plan?.title || "cut"}`, ...finishPayload });
      if (started.ok && started.stitchId) {
        for (let polls = 0; polls < 240; polls += 1) {
          await sleep(2500);
          const polled = await fetchStitchJob(started.stitchId).catch(() => null);
          const record = polled?.stitch;
          if (!record) continue;
          if (record.status === "running") {
            const { step, segment, total } = record.progress;
            setStitching({ elapsedSeconds: 0, totalSeconds: 0, segmentLabel: `master: ${step}${total ? ` ${segment}/${total}` : ""}` });
            continue;
          }
          if (record.status === "done" && record.result?.storageKey) {
            downloadMaster(record.result.storageKey, record.result.bytes || 0, record.result.notes);
            return;
          }
          engineFailure = record.message || "engine assembly failed";
          break;
        }
      } else if (started.message) {
        engineFailure = started.message;
      }
      if (!engineFailure) {
        // Older engine without stitch jobs — one synchronous attempt before preview.
        const master = await stitchRenderJobOnEngine({ jobId: job.id, voiceKeys, timeline, title: `${project.title} — ${plan?.title || "cut"}`, ...finishPayload });
        if (master.ok && master.storageKey) {
          downloadMaster(master.storageKey, master.bytes || 0);
          return;
        }
        engineFailure = master.message || "engine stitch unavailable";
      }
    } catch {
      engineFailure = engineFailure || "engine unreachable";
    }
    setStitchNote(
      `Engine master NOT built — ${engineFailure}. Building a PREVIEW instead${hasVignettes ? " (preview drops vignette dialogue and the opening card)" : ""}. Fix the engine and stitch again for the real master.`
    );
    setStitching(null);
    const segments = job.tasks.filter((t) => t.videoKey).map((t) => ({ videoUrl: keyUrl(t.videoKey), label: t.label }));
    const voiceUrls = voiceKeys.map(keyUrl);
    try {
      const result = await stitchRenderJob(segments, voiceUrls, {
        title: `${project.title} — infomercial`,
        onProgress: setStitching
      });
      downloadStitch(result, `${project.title} — infomercial PREVIEW`);
      setStitchNote(`PREVIEW saved to your downloads as .${result.extension} — this is a canvas re-encode, not the master. Fix the engine and stitch again for the real thing.`);
    } catch (err) {
      setStitchNote(err instanceof Error ? err.message : "Stitch failed.");
    } finally {
      setStitching(null);
    }
  }

  useEffect(() => {
    fetchRenderStatus().then(setGate).catch(() => setGate(null));
  }, []);

  // Poll a live job every 8s until every task settles.
  useEffect(() => {
    if (!job || !["running", "rendering", "cap_reached"].includes(job.status)) return;
    const pending = job.tasks.some((task) => !["completed", "failed"].includes(task.status));
    if (!pending) return;
    const timer = window.setInterval(() => {
      fetchRenderJob(job.id)
        .then((response) => {
          if (response.ok && response.job) setJob(response.job);
        })
        .catch(() => undefined);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [job]);

  function renderScenesPayload() {
    return project.scenes.map((scene) => {
      const page = sceneDirectorPage(project, scene.id);
      const prompt = safeRenderPrompt([
        scene.slugline,
        scene.synopsis,
        page?.directorIntent || "",
        page?.visualLanguage || "",
        project.sceneNotes?.[scene.id]?.trim() ? `Director note: ${project.sceneNotes[scene.id].trim()}` : "",
        "Stay faithful to the attached boards; cinematic continuity across shots."
      ].filter(Boolean).join(" "));
      const imageKeys = scene.frames
        .filter((frame) => !frame.disabled && frame.storageKey)
        .map((frame) => String(frame.storageKey))
        .slice(0, 9);
      const audioKeys = voiceTracksForScene(project, scene.id, project.uploadedAssets)
        .map((track) => String(track.asset.storageKey || ""))
        .filter(Boolean)
        .slice(0, 1);
      return { sceneId: scene.id, label: `Scene ${String(scene.number).padStart(2, "0")} · ${scene.slugline}`, durationSeconds: scene.durationSeconds, prompt, imageKeys, audioKeys };
    });
  }

  async function handleEstimate() {
    setBusy(true);
    setError("");
    try {
      const scenes = planMode === "cuts" ? cutScenesPayload() : renderScenesPayload();
      if (planMode === "cuts" && !scenes.length) {
        setError(activePlan ? "This plan has no enabled video beats." : "Create or draft a cut plan first.");
        return;
      }
      const result = await requestRenderEstimate({
        quality,
        aspectRatio: (planMode === "cuts" && activePlan?.aspect) || project.aspectRatio,
        scenes
      });
      setEstimate(result);
      setCap(Math.ceil(result.estimatedCredits * 1.25));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't build the estimate.");
    } finally {
      setBusy(false);
    }
  }

  async function handleStart() {
    if (!estimate || cap <= 0) return;
    setBusy(true);
    setError("");
    try {
      const result = await startRenderJob({
        projectId: project.id,
        title: project.title,
        quality: estimate.quality,
        aspectRatio: estimate.aspectRatio,
        approvedCapCredits: cap,
        gates,
        chunks: estimate.chunks,
        generateAudio: planMode === "film" // cut plans drive audio per beat
      });
      if (result.ok && result.job) {
        setJob(result.job);
        setEstimate(null);
      } else {
        setError(result.message || "The engine refused the render.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Render start failed.");
    } finally {
      setBusy(false);
    }
  }

  const balance = estimate?.balance ?? gate?.balance ?? null;

  return (
    <section className="pg-panel pg-budget" aria-label="Render budget">
      <div className="pg-row">
        <h2 className="pg-grow">Render</h2>
        {gate?.keyPresent ? <span className="pg-chip ok">key connected</span> : <span className="pg-chip bad">DRY RUN ONLY</span>}
      </div>
      {!gate?.keyPresent ? (
        <>
          <p className="pg-muted">
            Live video spend stays locked. To arm it: put <code>EVOLINK_API_KEY=…</code> in the repo's <code>.env</code> and restart the engine —
            the key never touches the browser.
          </p>
          <div className="pg-kv"><small>Spend approved</small><b>$0.00</b></div>
          <small className="pg-muted">Everything else stays free: boards, voices, packages, share links, video exports.</small>
        </>
      ) : job ? (
        <>
          <div className="pg-kv"><small>Job</small><b>{job.status.replace(/_/g, " ")}</b></div>
          <div className="pg-kv"><small>Reserved</small><b>{job.reservedCredits.toFixed(1)} / cap {job.approvedCapCredits.toFixed(0)} credits</b></div>
          {job.stopReason ? <p className="pg-small">{job.stopReason}</p> : null}
          <hr className="pg-hr" />
          {job.tasks.map((task) => (
            <div className="pg-kv" key={task.chunkId}>
              <small className="pg-clip">{task.label}</small>
              {task.videoKey ? (
                <a className="pg-link" href={keyUrl(task.videoKey)} rel="noreferrer" target="_blank">▶ Watch</a>
              ) : task.status === "failed" ? (
                <b className="pg-chip bad" title={task.error}>failed</b>
              ) : (
                <b>{task.status}{typeof task.progress === "number" && task.progress > 0 ? ` ${task.progress}%` : ""}</b>
              )}
            </div>
          ))}
          <small className="pg-muted">Finished footage is saved into your library automatically (provider links expire in 24h).</small>
          {planMode === "cuts" && activePlan && job.tasks.some((t) => t.videoKey) ? (
            <FinishingPanel
              music={(project.uploadedAssets?.music || []).filter((a) => !a.disabled && a.storageKey)}
              plan={activePlan}
              onChange={(finish) => upsertPlan({ ...activePlan, finish })}
            />
          ) : null}
          {job.tasks.some((t) => t.videoKey) ? (
            <button className="pg-pill primary" disabled={Boolean(stitching)} onClick={() => void handleStitch()} type="button">
              {stitching
                ? (stitching.totalSeconds > 0
                  ? `Stitching… ${Math.round(stitching.elapsedSeconds)}/${Math.round(stitching.totalSeconds)}s`
                  : stitching.segmentLabel || "Stitching…")
                : planMode === "cuts" ? "Make my video" : "Stitch & save video"}
            </button>
          ) : null}
          {stitchNote ? <small className="pg-muted">{stitchNote}</small> : null}
          <button className="pg-pill ghost" onClick={() => setJob(null)} type="button">New render</button>
        </>
      ) : estimate ? (
        <>
          <div className="pg-kv"><small>Plan</small><b>{estimate.chunks.length} chunks · {estimate.totalSeconds}s · {estimate.quality}</b></div>
          <div className="pg-kv"><small>Estimated</small><b>≈ {estimate.estimatedCredits.toFixed(0)} credits</b></div>
          {balance ? <div className="pg-kv"><small>Your balance</small><b>{balance.tokenUnlimited ? "unlimited" : `${balance.tokenRemaining.toFixed(0)} credits`}</b></div> : null}
          <SpendLine projectId={project.id} />
          {estimate.warnings.map((warning, index) => (
            <p className="pg-small pg-warn-text" key={index}>⚠ {warning}</p>
          ))}
          <label className="pg-kv pg-cap">
            <small>Hard cap (credits)</small>
            <input
              min={1}
              onChange={(event) => setCap(Math.max(0, Math.round(Number(event.target.value) || 0)))}
              type="number"
              value={cap}
            />
          </label>
          <button
            className="pg-pill primary"
            disabled={busy || !allGatesChecked || cap <= 0}
            onClick={() => void handleStart()}
            title={allGatesChecked ? "Creates provider tasks until the cap would be crossed" : "Confirm all six rights gates first"}
            type="button"
          >
            {busy ? "Starting…" : `Render — cap ${cap} credits`}
          </button>
          {!allGatesChecked ? <small className="pg-muted">Confirm all six gates on the left to arm this button.</small> : null}
          <small className="pg-muted">{estimate.note}</small>
          <button className="pg-pill ghost" onClick={() => setEstimate(null)} type="button">Cancel</button>
        </>
      ) : (
        <>
          <p className="pg-muted">Key connected. Get an exact plan before anything is spent.</p>
          <div className="pg-itabs">
            <button className={planMode === "film" ? "active" : ""} onClick={() => setPlanMode("film")} type="button">Whole film</button>
            <button className={planMode === "cuts" ? "active" : ""} onClick={() => setPlanMode("cuts")} type="button">Objective cuts</button>
          </div>
          {planMode === "cuts" ? (
            <small className="pg-muted">
              Trailer, teaser, sizzle, festival reel, short, pre-viz, B-roll, plates, commercial, social — draft a cut for any
              objective from your whole script, then edit every beat: reorder, rewrite, recast, retime, remove.
            </small>
          ) : null}
          {balance ? <div className="pg-kv"><small>Your balance</small><b>{balance.tokenUnlimited ? "unlimited" : `${balance.tokenRemaining.toFixed(0)} credits`}</b></div> : null}
          <div className="pg-row">
            <small className="pg-muted">Quality</small>
            {(["480p", "720p", "1080p"] as const).map((option) => (
              <button
                className={`pg-pill ghost small${quality === option ? " active" : ""}`}
                key={option}
                onClick={() => setQuality(option)}
                type="button"
              >
                {option}
              </button>
            ))}
          </div>
          <small className="pg-muted">Start with 480p — half the credits, perfect for the first pass.</small>
          {planMode === "cuts" ? (
            <ObjectiveCutsPanel
              activePlan={activePlan}
              plans={allPlans}
              project={project}
              onSelect={(planId) => updatePlans({ ...planStore, activeId: planId })}
              onSave={upsertPlan}
              onDelete={(planId) => updatePlans({
                activeId: planStore.activeId === planId ? "" : planStore.activeId,
                plans: planStore.plans.filter((plan) => plan.id !== planId)
              })}
              beatsContext={() => ({
                scenes: project.scenes.slice(0, 40).map((scene) => ({
                  heading: scene.slugline,
                  synopsis: scene.synopsis,
                  dialogue: sceneDialogue(project, scene.id).slice(0, 12)
                })),
                characters: project.characters.map((c) => c.name).slice(0, 30),
                assets: [
                  ...(project.uploadedAssets?.characterCards || []).map((a) => a.fileName),
                  ...(project.uploadedAssets?.sceneReferences || []).map((a) => a.fileName),
                  ...(project.uploadedAssets?.branding || []).map((a) => a.fileName),
                  ...(project.uploadedAssets?.styleRefs || []).map((a) => a.fileName)
                ].slice(0, 100),
                documents: (project.uploadedAssets?.documents || []).map((a) => a.fileName).slice(0, 20),
                voiceReads: (project.uploadedAssets?.voiceAudio || []).filter((a) => !a.disabled).map((a) => a.fileName),
                title: project.title,
                continuityBible: project.continuityBible || undefined,
                castingMap: project.castingMap || undefined,
                sceneNotes: Object.entries(project.sceneNotes || {}).map(([sceneId, note]) => ({
                  sceneId,
                  heading: project.scenes.find((scene) => scene.id === sceneId)?.slugline || sceneId,
                  note
                })),
                imageFilm: project.scenes.length <= 1 && !(project.scenes[0]?.synopsis || "").trim()
              })}
            />
          ) : null}
          <button className="pg-pill primary" disabled={busy} onClick={() => void handleEstimate()} type="button">
            {busy ? "Planning…" : planMode === "cuts" ? `Plan "${activePlan?.title || "cut"}" & show cost` : "Plan render & show cost"}
          </button>
          <small className="pg-muted">Free — nothing is created or spent at this step.</small>
        </>
      )}
      {error ? <p className="pg-small pg-warn-text">⚠ {error}</p> : null}
      <AssistantPanel
        explainContext={() => ({
          gate: gate ? { keyPresent: gate.keyPresent, dryRunOnly: gate.dryRunOnly, balance: gate.balance } : null,
          planMode,
          activePlan: activePlan ? { title: activePlan.title, beats: activePlan.beats.length } : null,
          estimate: estimate ? { chunks: estimate.chunks.length, estimatedCredits: estimate.estimatedCredits, warnings: estimate.warnings } : null,
          job: job ? { status: job.status, stopReason: job.stopReason, reservedCredits: job.reservedCredits, tasks: job.tasks.map((t) => ({ label: t.label, status: t.status, error: t.error })) } : null,
          stitchNote
        })}
      />
    </section>
  );
}

/* ---------- Objective Cuts: pick an offering, draft against the whole script,
   then edit everything — order, beats, prompts, casting hints, audio, cards ---------- */

const FALLBACK_OBJECTIVES: CutObjective[] = [
  { id: "trailer", label: "Trailer", target: 100, range: [60, 150], aspect: "16:9", blurb: "Premise hook, world, faces, stakes, escalation, title, button.", grammar: [], audioNote: "Continuous score bed required." },
  { id: "teaser", label: "Teaser", target: 40, range: [20, 60], aspect: "16:9", blurb: "Mood fragments, one hook, designed title, sting.", grammar: [], audioNote: "Continuous score bed required." },
  { id: "sizzle", label: "Sizzle / Proof-of-Concept", target: 90, range: [60, 180], aspect: "16:9", blurb: "Tone, world, characters and stakes with a score/VO spine.", grammar: [], audioNote: "Continuous score or VO spine required." },
  { id: "pitch", label: "Festival / Investor reel", target: 120, range: [90, 180], aspect: "16:9", blurb: "Logline, world, emblematic scenes, contact card.", grammar: [], audioNote: "" },
  { id: "scene", label: "Concept scene", target: 60, range: [20, 120], aspect: "16:9", blurb: "One scene, fully realized as written.", grammar: [], audioNote: "" },
  { id: "short", label: "Short film", target: 240, range: [120, 600], aspect: "16:9", blurb: "The whole script as a condensed finished short.", grammar: [], audioNote: "" },
  { id: "previz", label: "Pre-viz / Animatic", target: 120, range: [60, 300], aspect: "16:9", blurb: "Blocking, coverage and key beats in neutral shots.", grammar: [], audioNote: "" },
  { id: "broll", label: "Establishing & B-roll", target: 60, range: [30, 180], aspect: "16:9", blurb: "One beat per location — drop-in coverage.", grammar: [], audioNote: "" },
  { id: "plates", label: "VFX plates & extensions", target: 60, range: [30, 180], aspect: "16:9", blurb: "Clean environment plates for compositing.", grammar: [], audioNote: "" },
  { id: "commercial", label: "In-world commercial", target: 90, range: [30, 120], aspect: "16:9", blurb: "An ad from inside the story's world.", grammar: [], audioNote: "" },
  { id: "social", label: "Social cut", target: 22, range: [10, 45], aspect: "9:16", blurb: "Vertical, hook-first scroll-stopper with no dead-air card.", grammar: [], audioNote: "Platform music or score bed required." }
];

function ObjectiveCutsPanel({
  activePlan,
  plans,
  project,
  onSelect,
  onSave,
  onDelete,
  beatsContext
}: {
  activePlan: CutPlanDoc | null;
  plans: CutPlanDoc[];
  project: FilmProject;
  onSelect: (planId: string) => void;
  onSave: (doc: CutPlanDoc) => void;
  onDelete: (planId: string) => void;
  beatsContext: () => Record<string, unknown>;
}) {
  const [objectives, setObjectives] = useState<CutObjective[]>(FALLBACK_OBJECTIVES);
  const [creating, setCreating] = useState(!activePlan);
  const [objectiveId, setObjectiveId] = useState("trailer");
  const [targetSeconds, setTargetSeconds] = useState(100);
  const [drafting, setDrafting] = useState(false);
  const [direction, setDirection] = useState("");
  const [draftNote, setDraftNote] = useState("");
  const [editing, setEditing] = useState<CutPlanDoc | null>(null);
  const [rewriting, setRewriting] = useState("");
  const voiceAssets = (project.uploadedAssets?.voiceAudio || []).filter((a) => !a.disabled && a.storageKey);

  useEffect(() => {
    fetchObjectives().then((r) => { if (r.ok && r.objectives?.length) setObjectives(r.objectives); }).catch(() => undefined);
  }, []);
  const selectedObjective = objectives.find((o) => o.id === objectiveId) || objectives[0];

  function startDraft() {
    setDrafting(true);
    setDraftNote("");
    askAssistant("cut", { ...beatsContext(), objectiveId, targetSeconds, direction: direction.trim() || undefined })
      .then((result) => {
        const answer = result.answer as {
          title?: string; openingCard?: string; finishStyle?: string; voReads?: string[];
          beats?: Array<Partial<BeatSpec> & { audio?: string; kind?: string }>;
        } | undefined;
        if (!result.ok || !answer?.beats?.length) {
          setDraftNote(result.message || "The assistant returned no beats — try again or start blank.");
          return;
        }
        const norm = (s: string) => s.replace(/[^a-zA-Z0-9]+/g, "_").toLowerCase();
        const voiceKeys = (answer.voReads || [])
          .map((name) => voiceAssets.find((a) => norm(a.fileName).includes(norm(name).slice(0, 24)))?.storageKey)
          .filter(Boolean)
          .map(String);
        const beats: BeatSpec[] = answer.beats.map((draft, index) => ({
          id: String(draft.id || `beat_${index + 1}`),
          label: String(draft.label || draft.id || `beat ${index + 1}`),
          seconds: Math.max(2, Math.min(15, Math.round(Number(draft.seconds) || 5))),
          speak: draft.audio === "speaks",
          enabled: true,
          kind: draft.kind === "card" ? "card" : "chunk",
          cardText: String(draft.cardText || ""),
          audio: (["speaks", "vo", "stem", "score", "silent"].includes(String(draft.audio)) ? draft.audio : "silent") as BeatAudio,
          stemKey: "",
          sceneHeading: String(draft.sceneHeading || ""),
          prompt: String(draft.prompt || ""),
          refs: [],
          refHints: Array.isArray(draft.refHints) ? draft.refHints.map(String) : [],
          line: String(draft.line || "")
        }));
        const doc: CutPlanDoc = {
          id: `plan-${Date.now().toString(36)}`,
          objectiveId,
          title: String(answer.title || `${selectedObjective?.label || "Cut"} — ${project.title}`),
          beats,
          voiceKeys,
          openingCard: String(answer.openingCard || ""),
          aspect: selectedObjective?.aspect,
          finish: isTrailerObjective(objectiveId)
            ? { ...defaultFinishForObjective(objectiveId), directorStyle: normalizeTrailerStyle(answer.finishStyle) }
            : defaultFinishForObjective(objectiveId),
          updatedAt: new Date().toISOString()
        };
        onSave(doc);
        setEditing(doc);
        setCreating(false);
        setDraftNote("Draft ready — every beat below is yours to reorder, rewrite, retime, or remove.");
      })
      .catch(() => setDraftNote("Assistant unreachable — is the engine running with an OpenAI key?"))
      .finally(() => setDrafting(false));
  }

  function startBlank() {
    const doc: CutPlanDoc = {
      id: `plan-${Date.now().toString(36)}`,
      objectiveId,
      title: `${selectedObjective?.label || "Cut"} — ${project.title}`,
      beats: [{
        id: "beat_1", label: "beat 1", seconds: 8, speak: false, enabled: true, kind: "chunk",
        audio: "silent", prompt: "", refs: [], refHints: [], line: ""
      }],
      voiceKeys: [],
      openingCard: "",
      aspect: selectedObjective?.aspect,
      finish: defaultFinishForObjective(objectiveId),
      updatedAt: new Date().toISOString()
    };
    onSave(doc);
    setEditing(doc);
    setCreating(false);
  }

  const doc = editing || activePlan;
  const rows = doc?.beats || [];
  const dirty = Boolean(editing);
  const totalSeconds = rows.filter((r) => r.enabled !== false).reduce((sum, r) => sum + (r.seconds || 0), 0);

  function edit(patch: Partial<CutPlanDoc>) {
    if (!doc) return;
    setEditing({ ...(editing || doc), ...patch });
  }
  function editBeat(index: number, patch: Partial<BeatSpec>) {
    if (!doc) return;
    edit({ beats: rows.map((row, i) => (i === index ? { ...row, ...patch } : row)) });
  }
  function moveBeat(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    edit({ beats: next });
  }
  function removeBeat(index: number) {
    edit({ beats: rows.filter((_, i) => i !== index) });
  }
  function addBeat(kind: "chunk" | "card") {
    const id = `beat_${Date.now().toString(36).slice(-5)}`;
    edit({
      beats: [...rows, kind === "card"
        ? { id, label: "title card", seconds: 3, speak: false, enabled: true, kind: "card", cardText: "", audio: "silent" as BeatAudio, prompt: "", refs: [], refHints: [], line: "" }
        : { id, label: `beat ${rows.length + 1}`, seconds: 6, speak: false, enabled: true, kind: "chunk", audio: "silent" as BeatAudio, prompt: "", refs: [], refHints: [], line: "" }]
    });
  }

  async function rewritePrompt(index: number) {
    const instruction = window.prompt("How should this beat's prompt change? (e.g. 'more crowds, dusk, slower camera')");
    if (!instruction || !doc) return;
    setRewriting(rows[index].id);
    try {
      const result = await askAssistant("prompt", { prompt: rows[index].prompt, instruction });
      const next = (result.answer as { prompt?: string } | undefined)?.prompt;
      if (result.ok && next) editBeat(index, { prompt: next });
      else window.alert(result.message || "The assistant couldn't rewrite that prompt.");
    } catch {
      window.alert("Assistant unreachable — is the engine running with an OpenAI key?");
    } finally {
      setRewriting("");
    }
  }

  return (
    <div className="pg-panel pg-composer">
      <div className="pg-row">
        <strong className="pg-grow">Cut plans</strong>
        {plans.length ? (
          <select
            onChange={(e) => { onSelect(e.target.value); setEditing(null); setCreating(false); }}
            value={doc?.id || ""}
          >
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>{plan.title}</option>
            ))}
          </select>
        ) : null}
        <button className="pg-pill ghost small" onClick={() => { setCreating((v) => !v); setDraftNote(""); }} type="button">
          {creating ? "Back" : "New cut"}
        </button>
        {doc && doc.id !== "builtin-infomercial" ? (
          <button className="pg-pill ghost small" onClick={() => { onDelete(doc.id); setEditing(null); }} title="Delete this plan" type="button">✕</button>
        ) : null}
      </div>

      {creating || !doc ? (
        <>
          <div className="pg-objgrid">
            {objectives.map((objective) => (
              <button
                className={`pg-obj${objectiveId === objective.id ? " active" : ""}`}
                key={objective.id}
                onClick={() => { setObjectiveId(objective.id); setTargetSeconds(objective.target); }}
                type="button"
              >
                <b>{objective.label}</b>
                <small>{objective.blurb}</small>
                <small className="pg-muted">~{objective.target}s · {objective.aspect}</small>
              </button>
            ))}
          </div>
          <textarea
            onChange={(e) => setDirection(e.target.value)}
            placeholder="Creative direction (optional) — e.g. 'continuous dark pulsing trailer score, deep gravelly narrator, end on the billboard'"
            rows={2}
            value={direction}
          />
          <div className="pg-row">
            <small className="pg-muted">Target length</small>
            <input
              max={selectedObjective?.range?.[1] || 600}
              min={selectedObjective?.range?.[0] || 10}
              onChange={(e) => setTargetSeconds(Math.round(Number(e.target.value) || 0))}
              style={{ width: 70 }}
              type="number"
              value={targetSeconds}
            />
            <small className="pg-muted">seconds</small>
            <span className="pg-grow" />
            <button className="pg-pill primary small" disabled={drafting} onClick={startDraft} type="button">
              {drafting ? "Drafting from your whole script…" : "Draft with assistant"}
            </button>
            <button className="pg-pill ghost small" disabled={drafting} onClick={startBlank} type="button">Start blank</button>
          </div>
          {draftNote ? <small className="pg-muted">{draftNote}</small> : null}
        </>
      ) : (
        <>
          <div className="pg-row">
            <input
              className="pg-title-input"
              onChange={(e) => edit({ title: e.target.value })}
              value={doc.title}
            />
            <span className="pg-chip">{rows.filter((r) => r.enabled !== false).length} beats · {totalSeconds}s{doc.aspect && doc.aspect !== "16:9" ? ` · ${doc.aspect}` : ""}</span>
          </div>
          <label className="pg-kv pg-cardline">
            <small>Opening card</small>
            <input onChange={(e) => edit({ openingCard: e.target.value })} placeholder="(none)" value={doc.openingCard} />
          </label>
          {rows.map((beat, index) => {
            const audio = beatAudio(beat);
            const isCard = (beat.kind || "chunk") === "card";
            return (
              <div className="pg-beat" key={beat.id} style={{ opacity: beat.enabled === false ? 0.45 : 1 }}>
                <div className="pg-row">
                  <label className="pg-row" style={{ gap: 6 }}>
                    <input checked={beat.enabled !== false} onChange={(e) => editBeat(index, { enabled: e.target.checked })} type="checkbox" />
                    <input className="pg-beat-label" onChange={(e) => editBeat(index, { label: e.target.value })} value={beat.label} />
                  </label>
                  {isCard ? <span className="pg-chip">card</span> : (
                    <select onChange={(e) => editBeat(index, { audio: e.target.value as BeatAudio, speak: e.target.value === "speaks" })} value={audio}>
                      <option value="silent">silent</option>
                      <option value="score">score — generated music/VO</option>
                      <option value="vo">narrator VO</option>
                      <option value="speaks">speaks (lip-sync)</option>
                      <option value="stem">read laid under</option>
                    </select>
                  )}
                  <input
                    max={15} min={2}
                    onChange={(e) => editBeat(index, { seconds: Math.max(2, Math.min(15, Math.round(Number(e.target.value) || 2))) })}
                    style={{ width: 52 }}
                    type="number"
                    value={beat.seconds}
                  />
                  <small className="pg-muted">s</small>
                  {!isCard ? (
                    <>
                      <input
                        min={0} step={0.1} title="Trim: start this clip N seconds in (at stitch)"
                        onChange={(e) => editBeat(index, { inSeconds: Math.max(0, Number(e.target.value) || 0) })}
                        style={{ width: 46 }} type="number" value={beat.inSeconds || 0}
                      />
                      <small className="pg-muted">→</small>
                      <input
                        min={0} step={0.1} title="Trim: end this clip at N seconds (0 = full)"
                        onChange={(e) => editBeat(index, { outSeconds: Math.max(0, Number(e.target.value) || 0) })}
                        style={{ width: 46 }} type="number" value={beat.outSeconds || 0}
                      />
                    </>
                  ) : null}
                  <button className="pg-pill ghost small" onClick={() => moveBeat(index, -1)} title="Move up" type="button">▲</button>
                  <button className="pg-pill ghost small" onClick={() => moveBeat(index, 1)} title="Move down" type="button">▼</button>
                  <button className="pg-pill ghost small" onClick={() => removeBeat(index)} title="Remove beat" type="button">✕</button>
                </div>
                {isCard ? (
                  <input onChange={(e) => editBeat(index, { cardText: e.target.value })} placeholder="EXACT words on screen" value={beat.cardText || ""} />
                ) : (
                  <>
                    {(audio === "speaks" || audio === "stem") ? (
                      <div className="pg-row">
                        {audio === "speaks" ? (
                          <input className="pg-grow" onChange={(e) => editBeat(index, { line: e.target.value })} placeholder='Spoken line — e.g. "You always look so upset."' value={beat.line || ""} />
                        ) : null}
                        <select onChange={(e) => editBeat(index, { stemKey: e.target.value })} value={beat.stemKey || ""}>
                          <option value="">{audio === "speaks" ? "(generate the voice)" : "pick the read…"}</option>
                          {voiceAssets.map((asset) => (
                            <option key={String(asset.storageKey)} value={String(asset.storageKey)}>{asset.fileName.slice(0, 48)}</option>
                          ))}
                        </select>
                      </div>
                    ) : null}
                    <div className="pg-row">
                      <small className="pg-muted">refs</small>
                      <input
                        className="pg-grow"
                        onChange={(e) => editBeat(index, { refHints: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                        placeholder="asset names, comma-separated — e.g. Elle.PNG, Times-Square-Look-01"
                        value={(beat.refHints || []).join(", ")}
                      />
                      {beat.refs.length ? <span className="pg-chip ok" title="resolved keys from the original plan">{beat.refs.length} fixed</span> : null}
                      <button className="pg-pill ghost small" disabled={Boolean(rewriting)} onClick={() => void rewritePrompt(index)} type="button">
                        {rewriting === beat.id ? "Rewriting…" : "AI rewrite"}
                      </button>
                    </div>
                    <textarea onChange={(e) => editBeat(index, { prompt: e.target.value })} rows={3} value={beat.prompt} />
                  </>
                )}
              </div>
            );
          })}
          <div className="pg-row">
            <button className="pg-pill ghost small" onClick={() => addBeat("chunk")} type="button">+ beat</button>
            <button className="pg-pill ghost small" onClick={() => addBeat("card")} type="button">+ title card</button>
            <span className="pg-grow" />
          </div>
          {rows.some((beat) => beat.enabled !== false && beatAudio(beat) === "vo") ? (
            <div className="pg-vo">
              <small className="pg-muted">Narrator reads, in play order (laid across the VO beats at stitch):</small>
              {doc.voiceKeys.map((key, index) => (
                <div className="pg-row" key={`${key}-${index}`}>
                  <small className="pg-clip pg-grow">{index + 1}. {key.split("/").pop()?.replace(/^asset_[a-f0-9]+-/, "").slice(0, 52)}</small>
                  <button className="pg-pill ghost small" onClick={() => { if (index > 0) { const v = [...doc.voiceKeys]; [v[index - 1], v[index]] = [v[index], v[index - 1]]; edit({ voiceKeys: v }); } }} type="button">▲</button>
                  <button className="pg-pill ghost small" onClick={() => { if (index < doc.voiceKeys.length - 1) { const v = [...doc.voiceKeys]; [v[index + 1], v[index]] = [v[index], v[index + 1]]; edit({ voiceKeys: v }); } }} type="button">▼</button>
                  <button className="pg-pill ghost small" onClick={() => edit({ voiceKeys: doc.voiceKeys.filter((_, i) => i !== index) })} type="button">✕</button>
                </div>
              ))}
              <select
                onChange={(e) => { if (e.target.value) { edit({ voiceKeys: [...doc.voiceKeys, e.target.value] }); e.target.value = ""; } }}
                value=""
              >
                <option value="">+ add a read…</option>
                {voiceAssets.map((asset) => (
                  <option key={String(asset.storageKey)} value={String(asset.storageKey)}>{asset.fileName.slice(0, 48)}</option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="pg-row">
            <button className="pg-pill primary small" disabled={!dirty} onClick={() => { if (editing) { onSave(editing); setEditing(null); } }} type="button">
              {doc.id === "builtin-infomercial" ? "Save as my plan" : "Save plan"}
            </button>
            {dirty ? <button className="pg-pill ghost small" onClick={() => setEditing(null)} type="button">Discard edits</button> : null}
            {dirty ? <small className="pg-muted">unsaved edits</small> : <small className="pg-muted">saved</small>}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Finish & Export: tonight's whole mixing desk, as taps ---------- */

function SpendLine({ projectId }: { projectId: string }) {
  const [spend, setSpend] = useState<{ totalCredits?: number; renders?: number } | null>(null);
  useEffect(() => {
    fetchRenderHistory(projectId).then((result) => { if (result.ok) setSpend(result); }).catch(() => setSpend(null));
  }, [projectId]);
  if (!spend || !spend.renders) return null;
  return (
    <div className="pg-kv">
      <small>This film so far</small>
      <b>{(spend.totalCredits || 0).toFixed(0)} credits · {spend.renders} render{spend.renders === 1 ? "" : "s"}</b>
    </div>
  );
}

function FinishingPanel({
  plan,
  music,
  onChange
}: {
  plan: CutPlanDoc;
  music: Array<{ fileName: string; storageKey?: unknown }>;
  onChange: (finish: FinishSpec) => void;
}) {
  const finish = plan.finish || defaultFinishForObjective(plan.objectiveId);
  const set = (patch: Partial<FinishSpec>) => onChange({ ...finish, ...patch });
  const setLook = (patch: Record<string, unknown>) => set({ preset: finish.preset, look: { ...(finish.look || {}), ...patch } });
  const setDirectorStyle = (style: TrailerDirectorStyleId) => {
    set({ preset: "trailer", directorStyle: style, look: stripTrailerStyleOverrides(finish.look) });
  };
  const USUAL_KEY = "kinetix_usual_look";
  const hasUsual = (() => { try { return Boolean(window.localStorage.getItem(USUAL_KEY)); } catch { return false; } })();
  const saveUsual = () => {
    try {
      const merged = effectiveFinishLook(finish);
      window.localStorage.setItem(USUAL_KEY, JSON.stringify({ preset: finish.preset, directorStyle: finish.directorStyle, look: merged, musicKey: finish.musicKey, duck: finish.duck, nativeLevel: finish.nativeLevel, bedLevel: finish.bedLevel }));
    } catch { /* private mode */ }
  };
  const applyUsual = () => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(USUAL_KEY) || "null") as { preset?: FinishSpec["preset"]; directorStyle?: TrailerDirectorStyleId; look?: Record<string, unknown>; musicKey?: string; duck?: boolean; nativeLevel?: number; bedLevel?: number } | null;
      if (saved?.look) set({ preset: saved.preset || "custom", directorStyle: saved.directorStyle, look: saved.look, musicKey: saved.musicKey ?? finish.musicKey, duck: saved.duck ?? finish.duck, nativeLevel: saved.nativeLevel ?? finish.nativeLevel, bedLevel: saved.bedLevel ?? finish.bedLevel });
    } catch { /* ignore */ }
  };
  const effective = effectiveFinishLook(finish);
  const cutStyle = effective.flashCut ? "flash" : (Number(effective.fade) || 0) > 0 ? "dip" : "hard";
  const isTrailerFinish = finish.preset === "trailer" || isTrailerObjective(plan.objectiveId);
  const selectedTrailerStyle = finish.directorStyle && finish.directorStyle !== "auto"
    ? finish.directorStyle as Exclude<TrailerDirectorStyleId, "auto">
    : null;

  return (
    <>
    <div className="pg-row pg-usual">
      <small className="pg-muted pg-grow">Studio memory</small>
      <button className="pg-pill ghost small" onClick={saveUsual} title="Remember this exact finishing setup as your house style" type="button">★ Save as my usual</button>
      {hasUsual ? <button className="pg-pill ghost small" onClick={applyUsual} type="button">Use my usual</button> : null}
    </div>
    <div className="pg-finish">
      <span className="pg-eyebrow">Finish &amp; export</span>
      <div className="pg-presets">
        {(Object.keys(FINISH_PRESETS) as Array<keyof typeof FINISH_PRESETS>).map((key) => (
          <button
            className={`pg-preset${finish.preset === key ? " active" : ""}`}
            key={String(key)}
            onClick={() => set({ preset: key as FinishSpec["preset"], directorStyle: key === "trailer" ? (finish.directorStyle || "auto") : undefined, look: {} })}
            type="button"
          >
            <b>{FINISH_PRESETS[key].label}</b>
            <small>{FINISH_PRESETS[key].blurb}</small>
          </button>
        ))}
      </div>
      {isTrailerFinish ? (
        <div className="pg-row pg-style-row">
          <small className="pg-muted">Director style</small>
          <select onChange={(e) => setDirectorStyle(e.target.value as TrailerDirectorStyleId)} value={finish.directorStyle || "auto"}>
            <option value="auto">Auto from script</option>
            {(Object.keys(TRAILER_DIRECTOR_STYLES) as Array<Exclude<TrailerDirectorStyleId, "auto">>).map((style) => (
              <option key={style} value={style}>{TRAILER_DIRECTOR_STYLES[style].label}</option>
            ))}
          </select>
          <small className="pg-muted pg-grow">
            {selectedTrailerStyle
              ? TRAILER_DIRECTOR_STYLES[selectedTrailerStyle]?.blurb
              : "The AI can choose; override here for a specific genre language."}
          </small>
        </div>
      ) : null}
      <div className="pg-row">
        <small className="pg-muted">Music</small>
        <select onChange={(e) => set({ musicKey: e.target.value || undefined })} value={finish.musicKey || ""}>
          <option value="">{isTrailerFinish ? "Use synthetic trailer bed" : "No music bed"}</option>
          {music.map((track) => (
            <option key={String(track.storageKey)} value={String(track.storageKey)}>{track.fileName.slice(0, 44)}</option>
          ))}
        </select>
        {finish.musicKey ? (
          <label className="pg-row" style={{ gap: 6 }}>
            <input checked={finish.duck !== false} onChange={(e) => set({ duck: e.target.checked })} type="checkbox" />
            <small>Voices louder than music</small>
          </label>
        ) : null}
      </div>
      {isTrailerFinish && !finish.musicKey ? <small className="pg-muted">Trailer Final will synthesize a low score bed when no music asset is selected.</small> : null}
      <details className="pg-advanced">
        <summary>Advanced</summary>
        <div className="pg-row">
          <small className="pg-muted">Cuts</small>
          <select
            onChange={(e) => {
              const style = e.target.value;
              if (style === "dip") setLook({ fade: 0.3, flashCut: false, declick: false });
              else if (style === "flash") setLook({ fade: 0.08, fadeColor: "white", flashCut: true, declick: false });
              else setLook({ fade: 0, flashCut: false, declick: 0.2 });
            }}
            value={cutStyle}
          >
            <option value="hard">Hard cuts (eased audio)</option>
            <option value="dip">Dip to black</option>
            <option value="flash">White flash</option>
          </select>
          <small className="pg-muted">Grain</small>
          <input
            max={20} min={0}
            onChange={(e) => setLook({ grain: Math.max(0, Math.min(20, Math.round(Number(e.target.value) || 0))) })}
            style={{ width: 52 }}
            type="number"
            value={Number(effective.grain) || 0}
          />
        </div>
        <div className="pg-row">
          <label className="pg-row" style={{ gap: 6 }}>
            <input checked={Boolean(effective.letterbox)} onChange={(e) => setLook({ letterbox: e.target.checked ? "2.39" : "" })} type="checkbox" />
            <small>Widescreen bars</small>
          </label>
          <label className="pg-row" style={{ gap: 6 }}>
            <input checked={Boolean(effective.fps24)} onChange={(e) => setLook({ fps24: e.target.checked })} type="checkbox" />
            <small>24fps film motion</small>
          </label>
          <label className="pg-row" style={{ gap: 6 }}>
            <input checked={Boolean(effective.typography)} onChange={(e) => setLook({ typography: e.target.checked, cardRiser: e.target.checked })} type="checkbox" />
            <small>Fancy title cards</small>
          </label>
        </div>
      </details>
    </div>
  </>
  );
}

/* ---------- The director's copilot: proposes, never spends ---------- */

function AssistantPanel({ explainContext }: { explainContext: () => Record<string, unknown> }) {
  const [busy, setBusy] = useState(false);
  const [explanation, setExplanation] = useState<{ explanation: string; nextSteps: string[] } | null>(null);
  const [note, setNote] = useState("");

  async function run() {
    setBusy(true);
    setNote("");
    try {
      const result = await askAssistant("explain", explainContext());
      if (!result.ok) {
        setNote(result.message || "Assistant unavailable.");
      } else {
        const answer = result.answer as { explanation?: string; nextSteps?: string[] } | undefined;
        setExplanation({ explanation: answer?.explanation || "", nextSteps: answer?.nextSteps || [] });
      }
    } catch {
      setNote("Assistant unreachable — is the engine running with an OpenAI key?");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pg-assistant">
      <hr className="pg-hr" />
      <div className="pg-row">
        <span className="pg-eyebrow pg-grow">Assistant — proposes, you approve. Never renders, never spends.</span>
        <button className="pg-pill ghost small" disabled={busy} onClick={() => void run()} type="button">
          {busy ? "Reading the state…" : "Explain what's happening"}
        </button>
      </div>
      {explanation ? (
        <div className="pg-panel pg-explain">
          <p className="pg-small">{explanation.explanation}</p>
          {explanation.nextSteps.map((step, index) => (
            <p className="pg-small" key={index}>→ {step}</p>
          ))}
        </div>
      ) : null}
      {note ? <small className="pg-muted">{note}</small> : null}
    </div>
  );
}

/* ---------- Doctor: today's tribal knowledge as green checks ---------- */

function DoctorCard() {
  const [report, setReport] = useState<DoctorReport | null>(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const refresh = () => {
    fetchDoctor().then((r) => { setReport(r); setFailed(false); }).catch(() => setFailed(true));
  };
  useEffect(refresh, []);
  const issues = report ? report.checks.filter((c) => !c.ok) : [];
  return (
    <section className="pg-panel pg-doctor" aria-label="Engine doctor">
      <div className="pg-row">
        <h2 className="pg-grow">Doctor</h2>
        {failed ? <span className="pg-chip bad">engine unreachable</span>
          : !report ? <span className="pg-chip">checking…</span>
          : report.allOk ? <span className="pg-chip ok">all systems go</span>
          : <span className="pg-chip warn">{issues.length} issue{issues.length === 1 ? "" : "s"}</span>}
        <button className="pg-pill ghost small" onClick={() => { refresh(); setOpen(true); }} type="button">Re-check</button>
        <button className="pg-pill ghost small" onClick={() => setOpen((v) => !v)} type="button">{open ? "Hide" : "Details"}</button>
      </div>
      {report && !open && !report.allOk ? (
        <small className="pg-muted">{issues[0]?.label}: {issues[0]?.fix || issues[0]?.detail}</small>
      ) : null}
      {failed ? <small className="pg-muted">Start the engine: <code>npm run api:dev</code> from the repo folder.</small> : null}
      {open && report ? (
        <>
          <small className="pg-muted">engine {report.commit ? `@ ${report.commit} (${report.committedAgo})` : ""} · started {report.startedAt.slice(11, 19)}Z</small>
          {report.checks.map((check) => (
            <div className="pg-kv" key={check.id}>
              <small className="pg-clip">{check.ok ? "✓" : "✗"} {check.label}</small>
              <small className="pg-muted pg-clip" title={check.ok ? check.detail : check.fix || check.detail}>
                {check.ok ? check.detail : check.fix || check.detail}
              </small>
            </div>
          ))}
        </>
      ) : null}
    </section>
  );
}

/* ---------- Exports: "where is my file?" answered with a panel ---------- */

interface BeatMarker {
  label: string;
  start: number;
  kind: string;
}

function planBeatMarkers(plan: CutPlanDoc | undefined): BeatMarker[] {
  if (!plan) return [];
  const markers: BeatMarker[] = [];
  let clock = plan.openingCard ? 2.2 : 0;
  if (plan.openingCard) markers.push({ label: `“${plan.openingCard}”`, start: 0, kind: "card" });
  for (const beat of plan.beats) {
    if (beat.enabled === false) continue;
    const trimmed = (beat.outSeconds || 0) > 0 ? Math.max(1, (beat.outSeconds || 0) - (beat.inSeconds || 0)) : beat.seconds;
    markers.push({ label: beat.kind === "card" ? `“${beat.cardText || beat.label}”` : beat.label, start: clock, kind: beat.kind || "chunk" });
    clock += Math.max(1, trimmed || 5);
  }
  return markers;
}

function CompareRoom({ masters, onClose }: { masters: ProjectExportItem[]; onClose: () => void }) {
  const [leftKey, setLeftKey] = useState(masters[1]?.storageKey || masters[0]?.storageKey || "");
  const [rightKey, setRightKey] = useState(masters[0]?.storageKey || "");
  const leftRef = useRef<HTMLVideoElement | null>(null);
  const rightRef = useRef<HTMLVideoElement | null>(null);
  const both = (action: (video: HTMLVideoElement) => void) => {
    [leftRef.current, rightRef.current].forEach((video) => { if (video) action(video); });
  };
  const pickLabel = (key: string) => masters.find((master) => master.storageKey === key)?.fileName || key;
  return (
    <div className="pg-tour-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Compare versions">
      <div className="pg-panel pg-compare" onClick={(event) => event.stopPropagation()}>
        <div className="pg-row">
          <strong className="pg-grow">⇄ Version compare</strong>
          <button className="pg-pill ghost small" onClick={() => both((video) => { video.currentTime = 0; void video.play().catch(() => undefined); })} type="button">⏮ Restart both</button>
          <button className="pg-pill primary small" onClick={() => both((video) => void video.play().catch(() => undefined))} type="button">▶ Play both</button>
          <button className="pg-pill ghost small" onClick={() => both((video) => video.pause())} type="button">⏸</button>
          <button className="pg-pill ghost small" onClick={onClose} type="button">Close</button>
        </div>
        <div className="pg-compare-grid">
          {[{ key: leftKey, set: setLeftKey, ref: leftRef }, { key: rightKey, set: setRightKey, ref: rightRef }].map((side, index) => (
            <div className="pg-compare-side" key={index}>
              <select className="pg-sfx-select pg-compare-pick" onChange={(event) => side.set(event.target.value)} value={side.key}>
                {masters.map((master) => (
                  <option key={master.storageKey} value={master.storageKey}>{master.fileName}</option>
                ))}
              </select>
              <video controls muted={index === 1} ref={side.ref} src={keyUrl(side.key)} title={pickLabel(side.key)} />
            </div>
          ))}
        </div>
        <small className="pg-muted">Right side starts muted so the mixes don't fight — unmute either player as needed.</small>
      </div>
    </div>
  );
}

function ScreeningRoom({
  item,
  plan,
  notes,
  onSaveNotes,
  onClose
}: {
  item: ProjectExportItem;
  plan: CutPlanDoc | undefined;
  notes: Array<{ at: number; text: string; createdAt: string }>;
  onSaveNotes: (next: Array<{ at: number; text: string; createdAt: string }>) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [now, setNow] = useState(0);
  const [draft, setDraft] = useState("");
  const markers = useMemo(() => planBeatMarkers(plan), [plan]);
  const activeMarker = [...markers].reverse().find((marker) => now >= marker.start - 0.2);

  function seek(at: number) {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, at);
    void video.play().catch(() => undefined);
  }

  function addNote() {
    if (!draft.trim()) return;
    const video = videoRef.current;
    const at = video ? video.currentTime : now;
    const next = [...notes, { at: Math.round(at * 10) / 10, text: draft.trim().slice(0, 400), createdAt: new Date().toISOString() }]
      .sort((a, b) => a.at - b.at);
    onSaveNotes(next);
    setDraft("");
  }

  function notesMarkdown(): string {
    return [
      `# Screening notes — ${item.fileName}`,
      "",
      ...notes.map((note) => `- **${formatSeconds(note.at)}** — ${note.text}`)
    ].join("\n");
  }

  return (
    <div className="pg-tour-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Screening room">
      <div className="pg-panel pg-screening" onClick={(event) => event.stopPropagation()}>
        <div className="pg-row">
          <strong className="pg-grow pg-clip" title={item.fileName}>🎬 {item.fileName}</strong>
          {notes.length ? (
            <button className="pg-pill ghost small" onClick={() => downloadText(`${item.fileName}-notes.md`, notesMarkdown())} type="button">⬇ Notes .md</button>
          ) : null}
          <button className="pg-pill ghost small" onClick={onClose} type="button">Close</button>
        </div>
        <div className="pg-screening-body">
          <div className="pg-screening-stage">
            <video
              controls
              onTimeUpdate={(event) => setNow((event.target as HTMLVideoElement).currentTime)}
              ref={videoRef}
              src={keyUrl(item.storageKey)}
            />
            <div className="pg-row pg-screening-noter">
              <input
                className="pg-input pg-grow"
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => { if (event.key === "Enter") addNote(); }}
                placeholder={`Note at ${formatSeconds(now)} — Enter to pin it`}
                type="text"
                value={draft}
              />
              <button className="pg-pill primary small" disabled={!draft.trim()} onClick={addNote} type="button">
                📌 {formatSeconds(now)}
              </button>
            </div>
          </div>
          <aside className="pg-screening-rail">
            {markers.length ? (
              <>
                <span className="pg-eyebrow">Beats {plan ? `— ${plan.title}` : ""} (approx)</span>
                <div className="pg-screening-list">
                  {markers.map((marker, index) => (
                    <button
                      className={`pg-screening-beat${marker === activeMarker ? " active" : ""}`}
                      key={index}
                      onClick={() => seek(marker.start)}
                      type="button"
                    >
                      <small className="pg-mono">{formatSeconds(marker.start)}</small>
                      <small className="pg-grow pg-clip">{marker.label}</small>
                    </button>
                  ))}
                </div>
              </>
            ) : <small className="pg-muted">No cut plan to map beats from — notes still work.</small>}
            <span className="pg-eyebrow">Notes</span>
            <div className="pg-screening-list">
              {!notes.length ? <small className="pg-muted">Pin the first note — pause anywhere and type.</small> : null}
              {notes.map((note, index) => (
                <div className="pg-screening-note" key={index}>
                  <button className="pg-linkbtn pg-mono" onClick={() => seek(note.at)} type="button">{formatSeconds(note.at)}</button>
                  <small className="pg-grow">{note.text}</small>
                  <button className="pg-linkbtn pg-story-x" onClick={() => onSaveNotes(notes.filter((_, i) => i !== index))} title="Delete note" type="button">✕</button>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function formatSeconds(value: number): string {
  const total = Math.max(0, Math.floor(value));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

function AudienceReportView({ report }: { report: Record<string, unknown> }) {
  const notes = (report.notes && typeof report.notes === "object" ? report.notes : {}) as Record<string, unknown>;
  const personas = objList(notes.personas);
  const average = Number(notes.averageScore) || 0;
  return (
    <div className="pg-dailies">
      <div className="pg-row">
        <span className={`pg-chip ${average >= 7 ? "ok" : "warn"}`}>{average ? `${average.toFixed(1)}/10` : "screened"}</span>
        <b className="pg-grow">Test screening — 5 viewers</b>
      </div>
      {personas.map((persona, index) => (
        <div className="pg-audience-row" key={index}>
          <div className="pg-row">
            <b>{str(persona.name)}</b>
            <small className="pg-muted pg-grow">{str(persona.profile)}</small>
            <span className="pg-chip">{Number(persona.score) || 0}/10</span>
          </div>
          <small>{str(persona.reaction)}</small>
          <small className="pg-muted">
            {str(persona.grippedAt) ? <>👀 {str(persona.grippedAt)} </> : null}
            {str(persona.driftedAt) ? <>· 😴 {str(persona.driftedAt)}</> : null}
          </small>
        </div>
      ))}
      {str(notes.consensus) ? <p><b>The room says:</b> {str(notes.consensus)}</p> : null}
    </div>
  );
}

function DailiesReportView({
  report,
  onApplyFixes
}: {
  report: Record<string, unknown>;
  onApplyFixes?: (patch: Record<string, unknown>) => void;
}) {
  const notes = (report.notes && typeof report.notes === "object" ? report.notes : {}) as Record<string, unknown>;
  const score = Number(notes.score) || 0;
  const tells = objList(notes.aiTells);
  const continuity = objList(notes.continuity);
  const specPatch = (notes.specPatch && typeof notes.specPatch === "object" ? notes.specPatch : {}) as Record<string, unknown>;
  const patchKeys = Object.keys(specPatch);
  return (
    <div className="pg-dailies">
      <div className="pg-row">
        <span className={`pg-chip ${score >= 7 ? "ok" : "warn"}`}>{score ? `${score}/10` : "screened"}</span>
        <b className="pg-grow">{str(notes.verdict)}</b>
        <small className="pg-muted">{Number(report.frameCount) || 0} frames</small>
      </div>
      {str(notes.overall) ? <p>{str(notes.overall)}</p> : null}
      {tells.length ? (
        <>
          <span className="pg-eyebrow">AI tells</span>
          <ul className="pg-ai-list">
            {tells.map((tell, index) => (
              <li key={index}>
                <b>{str(tell.at)}</b> — {str(tell.tell)} <i className="pg-muted">→ {str(tell.fix)}</i>
                {" "}
                <button
                  className="pg-link pg-linkbtn"
                  onClick={() => void navigator.clipboard.writeText(`Fix at ${str(tell.at)}: ${str(tell.tell)} — ${str(tell.fix)}`)}
                  title="Copy this note — paste it into the beat's prompt doctor or your re-render brief"
                  type="button"
                >
                  ⧉
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : <small className="pg-muted">No AI tells flagged — clean pass.</small>}
      {continuity.length ? (
        <>
          <span className="pg-eyebrow">Continuity</span>
          <ul className="pg-ai-list">{continuity.map((note, index) => <li key={index}><b>{str(note.at)}</b> — {str(note.note)}</li>)}</ul>
        </>
      ) : null}
      {strList(notes.polish).length ? (
        <>
          <span className="pg-eyebrow">Polish next</span>
          <ul className="pg-ai-list">{strList(notes.polish).map((item, index) => <li key={index}>{item}</li>)}</ul>
        </>
      ) : null}
      {strList(notes.strengths).length ? <small className="pg-muted">Working: {strList(notes.strengths).join(" · ")}</small> : null}
      {onApplyFixes && patchKeys.length ? (
        <div className="pg-row">
          <button className="pg-pill primary small" onClick={() => onApplyFixes(specPatch)} type="button">
            ⚡ Apply fixes to finish settings
          </button>
          <small className="pg-muted">{patchKeys.map((key) => `${key}: ${String(specPatch[key])}`).join(" · ")}</small>
        </div>
      ) : null}
    </div>
  );
}

function RenderQcReportView({ report }: { report: RenderQcReport | Record<string, unknown> }) {
  const typed = report as RenderQcReport;
  const issues = Array.isArray(typed.issues) ? typed.issues : [];
  const media = typed.media || {};
  const contactSheetKey = typed.artifacts?.contactSheetKey || "";
  const verdict = typed.verdict || "review";
  return (
    <div className="pg-dailies">
      <div className="pg-row">
        <span className={`pg-chip ${verdict === "pass" ? "ok" : verdict === "rerender" ? "bad" : "warn"}`}>
          QC {verdict}
        </span>
        <b className="pg-grow">{Number(typed.score) || 0}/100 render safety</b>
        {contactSheetKey ? <a className="pg-link" href={keyUrl(contactSheetKey)} rel="noreferrer" target="_blank">Contact sheet</a> : null}
      </div>
      <small className="pg-muted">
        {str(media.videoCodec)} {str(media.width)}x{str(media.height)} · {str(media.pixFmt)} · audio {str(media.audioSampleRate)} Hz
        {str(media.faststart) ? ` · ${str(media.faststart)}` : ""}
      </small>
      {issues.length ? (
        <>
          <span className="pg-eyebrow">Render QC findings</span>
          <ul className="pg-ai-list">
            {issues.map((issue, index) => (
              <li key={index}>
                <b className={issue.severity === "fail" ? "pg-warn-text" : ""}>{issue.severity.toUpperCase()}</b>
                {issue.at ? <> <b>{issue.at}</b></> : null}
                {" "}
                {issue.message}
                {issue.fix ? <i className="pg-muted"> → {issue.fix}</i> : null}
              </li>
            ))}
          </ul>
        </>
      ) : <small className="pg-muted">No deterministic render issues found.</small>}
      {typed.vision && typeof typed.vision === "object" && str((typed.vision as Record<string, unknown>).summary) ? (
        <p><b>Visual QC:</b> {str((typed.vision as Record<string, unknown>).summary)}</p>
      ) : null}
    </div>
  );
}

function ExportsCard({
  project,
  onPatchProject,
  onSaveCutPlans
}: {
  project: FilmProject;
  onPatchProject: (patch: Partial<FilmProject>) => void;
  onSaveCutPlans: (store: { activeId: string; plans: CutPlanDoc[] }) => void;
}) {
  const projectId = project.id;
  const [items, setItems] = useState<ProjectExportItem[] | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [screening, setScreening] = useState("");
  const [openReport, setOpenReport] = useState("");
  const [dailiesError, setDailiesError] = useState("");
  const [qcError, setQcError] = useState("");
  const [qcBusy, setQcBusy] = useState("");
  const [openQc, setOpenQc] = useState("");
  const [packBusy, setPackBusy] = useState("");
  const [packNote, setPackNote] = useState("");
  const [screeningKey, setScreeningKey] = useState("");
  const [audienceOpen, setAudienceOpen] = useState("");
  const [compareOpen, setCompareOpen] = useState(false);

  async function screenAudience(item: ProjectExportItem) {
    const mapKey = `${item.storageKey}::audience`;
    if (project.dailies?.[mapKey] && audienceOpen !== item.storageKey) {
      setAudienceOpen(item.storageKey);
      return;
    }
    setScreening(item.storageKey);
    setDailiesError("");
    const jobId = `audience-${item.storageKey}`;
    announceJob({ id: jobId, label: "Test screening", status: "running" });
    try {
      const result = await requestDailies(item.storageKey, { title: item.fileName }, { style: "audience", frameCount: 12 });
      if (!result.ok) {
        setDailiesError(result.message || "Screening failed.");
      } else {
        onPatchProject({ dailies: { ...(project.dailies || {}), [mapKey]: result as unknown as Record<string, unknown> } });
        setAudienceOpen(item.storageKey);
      }
    } catch (error) {
      setDailiesError(error instanceof Error ? error.message : "Screening failed.");
    } finally {
      setScreening("");
      announceJob({ id: jobId, label: "Test screening", status: "done" });
    }
  }

  const [finishBackup, setFinishBackup] = useState<{ planId: string; finish: FinishSpec | undefined } | null>(null);

  function applyFixes(patch: Record<string, unknown>) {
    const store = loadCutPlanStore(project);
    const plan = store.plans.find((doc) => doc.id === store.activeId) || store.plans[0];
    if (!plan) {
      setPackNote("No cut plan to apply fixes to — draft one in the Cut Composer first.");
      return;
    }
    const finish = (plan.finish || { preset: "custom" }) as FinishSpec;
    setFinishBackup({ planId: plan.id, finish: plan.finish ? JSON.parse(JSON.stringify(plan.finish)) as FinishSpec : undefined });
    plan.finish = { ...finish, preset: "custom", look: { ...(finish.look || {}), ...patch } };
    onSaveCutPlans({ activeId: store.activeId, plans: store.plans });
    setPackNote(`Fixes applied to "${plan.title || "your cut"}" — re-render the master to see them.`);
  }

  function undoFixes() {
    if (!finishBackup) return;
    const store = loadCutPlanStore(project);
    const plan = store.plans.find((doc) => doc.id === finishBackup.planId);
    if (plan) {
      plan.finish = finishBackup.finish;
      plan.updatedAt = new Date().toISOString();
      onSaveCutPlans({ activeId: store.activeId, plans: store.plans });
      setPackNote("Finish settings restored.");
    }
    setFinishBackup(null);
  }

  async function runPack(item: ProjectExportItem) {
    setPackBusy(item.storageKey);
    setPackNote("");
    const jobId = `pack-${item.storageKey}`;
    announceJob({ id: jobId, label: "Deliverables pack", status: "running" });
    try {
      const result = await deriveExports({ storageKey: item.storageKey, projectId, formats: ["vertical", "square", "thumb"] });
      setPackNote(result.ok
        ? (result.status === "queued" ? String(result.message || "Queued for your render agent.") : `Pack ready: ${(result.items || []).length} files added.`)
        : String(result.message || "Pack failed."));
      if (result.status === "derived") refresh();
    } catch (error) {
      setPackNote(error instanceof Error ? error.message : "Pack failed.");
    } finally {
      setPackBusy("");
      announceJob({ id: jobId, label: "Deliverables pack", status: "done", detail: "files land in Finished videos" });
    }
  }

  async function runGradedClips() {
    setPackBusy("graded");
    setPackNote("");
    const jobId = `graded-${Date.now()}`;
    announceJob({ id: jobId, label: "Graded clips", status: "running" });
    try {
      const store = loadCutPlanStore(project);
      const plan = store.plans.find((doc) => doc.id === store.activeId) || store.plans[0];
      const look = ((plan?.finish as FinishSpec | undefined)?.look) || FINISH_PRESETS.cinematic.look;
      const result = await deriveExports({ projectId, formats: ["gradedClips"], look });
      setPackNote(result.ok
        ? (result.status === "queued" ? String(result.message || "Queued for your render agent.") : `Graded ${(result.items || []).length} clips.`)
        : String(result.message || "Grading failed."));
      if (result.status === "derived") refresh();
    } catch (error) {
      setPackNote(error instanceof Error ? error.message : "Grading failed.");
    } finally {
      setPackBusy("");
      announceJob({ id: jobId, label: "Graded clips", status: "done" });
    }
  }

  async function runCaptions(item: ProjectExportItem) {
    setPackBusy(`cc:${item.storageKey}`);
    setPackNote("");
    const jobId = `cc-${item.storageKey}`;
    announceJob({ id: jobId, label: "Captions", status: "running" });
    try {
      const result = await requestCaptions(item.storageKey);
      if (result.ok && result.srt) {
        downloadText(item.fileName.replace(/\.mp4$/i, "") + ".srt", result.srt);
        setPackNote("Captions ready — .srt downloaded and saved next to the master.");
        refresh();
      } else {
        setPackNote(String(result.message || "Captioning failed."));
      }
    } catch (error) {
      setPackNote(error instanceof Error ? error.message : "Captioning failed.");
    } finally {
      setPackBusy("");
      announceJob({ id: jobId, label: "Captions", status: "done" });
    }
  }
  const refresh = () => {
    fetchProjectExports(projectId).then((r) => setItems(r.items)).catch(() => setItems([]));
  };
  useEffect(refresh, [projectId]);
  const list = items || [];
  const visible = showAll ? list : list.slice(0, 6);

  async function screen(item: ProjectExportItem) {
    setScreening(item.storageKey);
    setDailiesError("");
    const jobId = `dailies-${item.storageKey}`;
    announceJob({ id: jobId, label: item.isMaster ? "Dailies pass" : "Clip check", status: "running" });
    try {
      const style = item.isMaster ? "master" : "clip";
      const result = await requestDailies(item.storageKey, { title: item.fileName }, { style });
      if (!result.ok) {
        setDailiesError(result.message || "Dailies pass failed.");
      } else {
        onPatchProject({ dailies: { ...(project.dailies || {}), [item.storageKey]: result as unknown as Record<string, unknown> } });
        setOpenReport(item.storageKey);
      }
    } catch (error) {
      setDailiesError(error instanceof Error ? error.message : "Dailies pass failed.");
    } finally {
      setScreening("");
      announceJob({ id: jobId, label: item.isMaster ? "Dailies pass" : "Clip check", status: "done" });
    }
  }

  async function runRenderQc(item: ProjectExportItem) {
    setQcBusy(item.storageKey);
    setQcError("");
    const jobId = `qc-${item.storageKey}`;
    announceJob({ id: jobId, label: "Render QC", status: "running", detail: item.fileName });
    try {
      const result = await requestRenderQc({
        projectId,
        storageKey: item.storageKey,
        isMaster: item.isMaster,
        context: { title: project.title, fileName: item.fileName }
      });
      if (!result.ok) {
        setQcError(result.message || "Render QC failed.");
      } else {
        onPatchProject({ renderQc: { ...(project.renderQc || {}), [item.storageKey]: result as unknown as Record<string, unknown> } });
        setOpenQc(item.storageKey);
      }
    } catch (error) {
      setQcError(error instanceof Error ? error.message : "Render QC failed.");
    } finally {
      setQcBusy("");
      announceJob({ id: jobId, label: "Render QC", status: "done" });
    }
  }

  return (
    <section className="pg-panel pg-exports" aria-label="Finished videos">
      <div className="pg-row">
        <h2 className="pg-grow">Finished videos</h2>
        <span className="pg-chip">{list.length}</span>
        {list.filter((item) => item.isMaster).length >= 2 ? (
          <button className="pg-pill ghost small" onClick={() => setCompareOpen(true)} title="Two masters side by side, synced controls" type="button">
            ⇄ Compare
          </button>
        ) : null}
        <button
          className="pg-pill ghost small"
          disabled={packBusy !== ""}
          onClick={() => void runGradedClips()}
          title="Export every raw clip through the active cut's finishing look — individual graded files for outside editors"
          type="button"
        >
          {packBusy === "graded" ? "Grading…" : "🎬 Graded clips"}
        </button>
        <button className="pg-pill ghost small" onClick={refresh} type="button">Refresh</button>
      </div>
      {dailiesError ? <small className="pg-muted">⚠ {dailiesError}</small> : null}
      {qcError ? <small className="pg-muted">⚠ {qcError}</small> : null}
      {packNote ? (
        <small className="pg-muted">
          {packNote}
          {finishBackup ? <> · <button className="pg-link pg-linkbtn" onClick={undoFixes} type="button">↩ Undo</button></> : null}
        </small>
      ) : null}
      {items === null ? <p className="pg-muted">Looking…</p>
        : !list.length ? <p className="pg-muted">Nothing rendered yet — finished clips and stitched masters land here automatically.</p>
        : (
          <>
            {visible.map((item) => {
              const report = project.dailies?.[item.storageKey];
              const qcReport = project.renderQc?.[item.storageKey] as unknown as RenderQcReport | undefined;
              return (
                <div className="pg-export-row" key={item.storageKey}>
                  <div className="pg-kv">
                    <small className="pg-clip" title={item.fileName}>
                      {item.isMaster ? <b>★ </b> : null}{item.fileName}
                    </small>
                    <span>
                      <small className="pg-muted">{(item.bytes / 1e6).toFixed(1)} MB · </small>
                      {/\.(mp4|mov|webm)$/i.test(item.fileName) ? (
                        <button className="pg-link pg-linkbtn" onClick={() => setScreeningKey(item.storageKey)} type="button">▶ Watch</button>
                      ) : (
                        <a className="pg-link" href={keyUrl(item.storageKey)} rel="noreferrer" target="_blank">▶ Open</a>
                      )}
                      {" · "}
                      <a className="pg-link" download={item.fileName} href={keyUrl(item.storageKey)}>Save</a>
                      {" · "}
                      <button
                        className="pg-link pg-linkbtn"
                        disabled={screening !== ""}
                        onClick={() => (report && openReport !== item.storageKey ? setOpenReport(item.storageKey) : void screen(item))}
                        type="button"
                      >
                        {screening === item.storageKey
                          ? "Screening…"
                          : report
                            ? (openReport === item.storageKey ? "Re-screen" : item.isMaster ? "🎞 Dailies" : "✓ Check")
                            : item.isMaster ? "🎞 Dailies" : "✓ Check"}
                      </button>
                      {" · "}
                      <button
                        className="pg-link pg-linkbtn"
                        disabled={qcBusy !== ""}
                        onClick={() => (qcReport && openQc !== item.storageKey ? setOpenQc(item.storageKey) : void runRenderQc(item))}
                        title="Checks delivery format, black gaps, stale stitches, contact sheet, and optional visual artifacts"
                        type="button"
                      >
                        {qcBusy === item.storageKey ? "QC…" : qcReport ? (openQc === item.storageKey ? "Re-QC" : `QC ${qcReport.verdict}`) : "QC"}
                      </button>
                      {item.isMaster ? (
                        <>
                          {" · "}
                          <button
                            className="pg-link pg-linkbtn"
                            disabled={screening !== ""}
                            onClick={() => void screenAudience(item)}
                            title="Five AI viewers watch the cut and report where they leaned in and where they drifted"
                            type="button"
                          >
                            👥
                          </button>
                          {" · "}
                          <button className="pg-link pg-linkbtn" disabled={packBusy !== ""} onClick={() => void runPack(item)} type="button">
                            {packBusy === item.storageKey ? "Packing…" : "📦 Pack"}
                          </button>
                          {" · "}
                          <button className="pg-link pg-linkbtn" disabled={packBusy !== ""} onClick={() => void runCaptions(item)} type="button">
                            {packBusy === `cc:${item.storageKey}` ? "Transcribing…" : "CC"}
                          </button>
                        </>
                      ) : null}
                    </span>
                  </div>
                  {report && openReport === item.storageKey ? <DailiesReportView onApplyFixes={item.isMaster ? applyFixes : undefined} report={report} /> : null}
                  {qcReport && openQc === item.storageKey ? <RenderQcReportView report={qcReport} /> : null}
                  {project.dailies?.[`${item.storageKey}::audience`] && audienceOpen === item.storageKey ? (
                    <AudienceReportView report={project.dailies[`${item.storageKey}::audience`]} />
                  ) : null}
                </div>
              );
            })}
            {list.length > 6 ? (
              <button className="pg-pill ghost small" onClick={() => setShowAll((v) => !v)} type="button">
                {showAll ? "Show fewer" : `Show all ${list.length}`}
              </button>
            ) : null}
            <small className="pg-muted">★ = stitched master · 🎞 Dailies = an AI post supervisor screens it for pacing, continuity, and AI tells.</small>
          </>
        )}
      {compareOpen ? <CompareRoom masters={list.filter((item) => item.isMaster)} onClose={() => setCompareOpen(false)} /> : null}
      {screeningKey ? (() => {
        const item = list.find((entry) => entry.storageKey === screeningKey);
        if (!item) return null;
        const store = loadCutPlanStore(project);
        const plan = store.plans.find((doc) => doc.id === store.activeId) || store.plans[0];
        return (
          <ScreeningRoom
            item={item}
            notes={project.screeningNotes?.[screeningKey] || []}
            onClose={() => setScreeningKey("")}
            onSaveNotes={(next) => onPatchProject({ screeningNotes: { ...(project.screeningNotes || {}), [screeningKey]: next } })}
            plan={plan}
          />
        );
      })() : null}
    </section>
  );
}

/* ---------- Day One: checklist strip, first-run tour, command palette ---------- */

function ProChecklist({
  project,
  people,
  onSection
}: {
  project: FilmProject;
  people: MatchedPerson[];
  onSection: (section: ProSection) => void;
}) {
  const hasScript = Boolean(project.scriptUpload?.text || project.scriptExcerpt || project.scenes.length > 1);
  const castInScript = people.filter((person) => person.inScript);
  const castReady = castInScript.filter((person) => person.looks.length).length;
  const allFrames = project.scenes.flatMap((scene) => scene.frames.filter((frame) => !frame.disabled));
  const boardsReady = allFrames.filter((frame) => frame.generationStatus === "generated" || frame.source === "uploaded").length;
  const hasPlan = Boolean(project.cutPlans?.plans?.length);
  const steps: Array<{ id: string; label: string; done: boolean; hint: string; section: ProSection }> = [
    { id: "script", label: hasScript ? "Script ✓" : "1 · Script", done: hasScript, hint: "Drop a script or type an idea", section: "story" },
    { id: "cast", label: castInScript.length ? `Cast ${castReady}/${castInScript.length}` : "2 · Cast", done: castInScript.length > 0 && castReady >= castInScript.length, hint: "Photos for your people & places", section: "cast" },
    { id: "boards", label: allFrames.length ? `Boards ${boardsReady}/${allFrames.length}` : "3 · Boards", done: allFrames.length > 0 && boardsReady >= allFrames.length, hint: "Generate and QC the boards", section: "boards" },
    { id: "plan", label: hasPlan ? "Cut plan ✓" : "4 · Cut plan", done: hasPlan, hint: "Draft a trailer/teaser/film cut", section: "delivery" },
    { id: "deliver", label: "5 · Render & deliver", done: false, hint: "Budget-gated render, then dailies, pack, share", section: "delivery" }
  ];
  const next = steps.find((step) => !step.done);
  return (
    <nav aria-label="What's next" className="pg-checklist">
      {steps.map((step) => (
        <button
          className={`pg-check-chip${step.done ? " done" : ""}${next?.id === step.id ? " next" : ""}`}
          key={step.id}
          onClick={() => onSection(step.section)}
          title={step.hint}
          type="button"
        >
          {step.label}
        </button>
      ))}
    </nav>
  );
}

function WritersRoom({
  project,
  onPatchProject,
  onClose
}: {
  project: FilmProject;
  onPatchProject: (patch: Partial<FilmProject>) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const messages = project.writersRoom?.messages || [];
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages.length, busy]);

  async function send() {
    const question = draft.trim();
    if (!question || busy) return;
    setDraft("");
    setBusy(true);
    const base = [...messages, { role: "you", text: question, at: new Date().toISOString() }];
    onPatchProject({ writersRoom: { messages: base.slice(-40) } });
    try {
      const story = storyAiContext(project);
      const coverage = project.coverage as Record<string, unknown> | undefined;
      const plans = (project.cutPlans?.plans || []) as Array<Record<string, unknown>>;
      const dailies = Object.values(project.dailies || {}).slice(-3).map((report) => {
        const notes = (report.notes && typeof report.notes === "object" ? report.notes : {}) as Record<string, unknown>;
        return { verdict: str(notes.verdict), score: Number(notes.score) || 0 };
      });
      const result = await askAssistant("chat", {
        projectContext: {
          ...story,
          script: String(story.script || "").slice(0, 6000),
          bible: project.continuityBible || undefined,
          coverageVerdict: coverage ? { verdict: coverage.verdict, logline: coverage.logline } : undefined,
          plans: plans.map((plan) => ({ title: plan.title, beats: Array.isArray(plan.beats) ? plan.beats.length : 0 })),
          recentDailies: dailies
        },
        history: base.slice(-10).map((message) => ({ role: message.role, text: message.text })),
        question
      });
      const answer = (result.answer || {}) as Record<string, unknown>;
      const reply = str(answer.reply) || result.message || "The studio line is busy — try again.";
      const actions = strList(answer.actions);
      const replyText = actions.length ? `${reply}\n\n${actions.map((action) => `→ ${action}`).join("\n")}` : reply;
      onPatchProject({ writersRoom: { messages: [...base, { role: "studio", text: replyText, at: new Date().toISOString() }].slice(-40) } });
    } catch (error) {
      onPatchProject({ writersRoom: { messages: [...base, { role: "studio", text: error instanceof Error ? error.message : "Request failed.", at: new Date().toISOString() }].slice(-40) } });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pg-writers" role="dialog" aria-label="Writers' room">
      <div className="pg-row">
        <strong className="pg-grow">✦ Writers&rsquo; room</strong>
        {messages.length ? (
          <button className="pg-pill ghost small" onClick={() => onPatchProject({ writersRoom: { messages: [] } })} type="button">Clear</button>
        ) : null}
        <button className="pg-pill ghost small" onClick={onClose} type="button">Close</button>
      </div>
      <div className="pg-writers-thread">
        {!messages.length ? (
          <small className="pg-muted">
            It knows your whole film — script, cast, bible, coverage, cuts, dailies. Ask anything:
            &ldquo;which scene is weakest?&rdquo;, &ldquo;what should I render first?&rdquo;, &ldquo;punch up the rooftop dialogue.&rdquo;
          </small>
        ) : null}
        {messages.map((message, index) => (
          <div className={`pg-writers-msg ${message.role === "you" ? "you" : "studio"}`} key={index}>
            <small>{message.text}</small>
          </div>
        ))}
        {busy ? <div className="pg-writers-msg studio"><small className="pg-muted">thinking…</small></div> : null}
        <div ref={endRef} />
      </div>
      <div className="pg-row">
        <input
          className="pg-input pg-grow"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter") void send(); }}
          placeholder="Ask the studio…"
          type="text"
          value={draft}
        />
        <button className="pg-pill primary small" disabled={busy || !draft.trim()} onClick={() => void send()} type="button">Send</button>
      </div>
    </div>
  );
}

const TOUR_KEY = "kinetix_pro_tour_v1";

const TOUR_STEPS: Array<{ title: string; body: string }> = [
  { title: "Welcome to the studio", body: "Four rooms: Story, Boards, Cast & Places, Delivery. The checklist strip up top always shows your next step — tap any chip to jump there." },
  { title: "Story", body: "Drop a script — or just type an idea and the screenwriter drafts one. Then run Coverage and a Pitch kit from the Studio reader." },
  { title: "Boards", body: "Generate boards per scene, then hit 🔍 QC boards: an AI supervisor flags wrong faces and broken shots BEFORE you pay to render video." },
  { title: "Cast & Places", body: "📥 Smart drop files everything automatically. 🎭 Cast with AI matches your photos to roles. The bible and scout report keep every shot consistent." },
  { title: "Delivery", body: "Draft an Objective Cut, render with the budget gate, then: 🎞 Dailies notes, ⚡ Apply fixes, 📦 Pack (vertical/square/thumb), 🎬 Graded clips, CC captions, and a share link." }
];

function ProTour({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState(0);
  const current = TOUR_STEPS[step];
  return (
    <div className="pg-tour-backdrop" role="dialog" aria-modal="true" aria-label="Welcome tour">
      <div className="pg-panel pg-tour">
        <span className="pg-eyebrow">{step + 1} of {TOUR_STEPS.length}</span>
        <h2>{current.title}</h2>
        <p>{current.body}</p>
        <div className="pg-row">
          <span className="pg-tour-dots pg-grow">
            {TOUR_STEPS.map((_, index) => <i className={index === step ? "active" : ""} key={index} />)}
          </span>
          <button className="pg-pill ghost small" onClick={onDone} type="button">Skip</button>
          <button
            className="pg-pill primary small"
            onClick={() => (step + 1 < TOUR_STEPS.length ? setStep(step + 1) : onDone())}
            type="button"
          >
            {step + 1 < TOUR_STEPS.length ? "Next" : "Let's make a film"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface PaletteAction {
  id: string;
  label: string;
  hint: string;
  run: () => void;
}

function CommandPalette({ actions, onClose }: { actions: PaletteAction[]; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const visible = actions.filter((action) =>
    !query.trim() || `${action.label} ${action.hint}`.toLowerCase().includes(query.trim().toLowerCase())
  );
  const pick = (action: PaletteAction | undefined) => {
    if (!action) return;
    onClose();
    action.run();
  };
  return (
    <div className="pg-tour-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Command palette">
      <div className="pg-panel pg-palette" onClick={(event) => event.stopPropagation()}>
        <input
          autoFocus
          className="pg-input"
          onChange={(event) => { setQuery(event.target.value); setIndex(0); }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") { event.preventDefault(); setIndex((i) => Math.min(i + 1, visible.length - 1)); }
            if (event.key === "ArrowUp") { event.preventDefault(); setIndex((i) => Math.max(i - 1, 0)); }
            if (event.key === "Enter") { event.preventDefault(); pick(visible[index]); }
            if (event.key === "Escape") onClose();
          }}
          placeholder="Jump anywhere, do anything…"
          type="text"
          value={query}
        />
        <div className="pg-palette-list">
          {visible.map((action, i) => (
            <button
              className={`pg-palette-item${i === index ? " active" : ""}`}
              key={action.id}
              onClick={() => pick(action)}
              onMouseEnter={() => setIndex(i)}
              type="button"
            >
              <span className="pg-grow">{action.label}</span>
              <small className="pg-muted">{action.hint}</small>
            </button>
          ))}
          {!visible.length ? <p className="pg-muted">Nothing matches.</p> : null}
        </div>
        <small className="pg-muted">↑↓ navigate · Enter run · Esc close</small>
      </div>
    </div>
  );
}

/* ---------- Shell ---------- */

export function ProGlass(props: ProGlassProps) {
  const { project, theme } = props;
  const [section, setSection] = useState<ProSection>(project.scenes.length ? "boards" : "story");
  const [selectedSceneId, setSelectedSceneId] = useState(project.scenes[0]?.id || "");
  const [showTour, setShowTour] = useState(() => {
    try { return !window.localStorage.getItem(TOUR_KEY); } catch { return false; }
  });
  const [showPalette, setShowPalette] = useState(false);
  const [showWriters, setShowWriters] = useState(false);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setShowPalette((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const closeTour = () => {
    try { window.localStorage.setItem(TOUR_KEY, "done"); } catch { /* private mode */ }
    setShowTour(false);
  };
  useEffect(() => {
    if (!project.scenes.some((scene) => scene.id === selectedSceneId)) {
      setSelectedSceneId(project.scenes[0]?.id || "");
    }
  }, [project.scenes, selectedSceneId]);
  const people = useMemo(() => matchPeople(project.characters, project.uploadedAssets), [project.characters, project.uploadedAssets]);
  const places = useMemo(() => matchPlaces(project.scenes, project.uploadedAssets), [project.scenes, project.uploadedAssets]);
  const busy = props.isAnalyzing || props.isEasyModeRunning || props.isGeneratingBoards;

  return (
    <div className={`pro-glass ${theme}`}>
      <ProTopbar
        project={project}
        section={section}
        saveStatus={props.saveStatus}
        isBusy={busy}
        theme={theme}
        onThemeChange={props.onThemeChange}
        onSection={setSection}
        onOpenSimple={props.onOpenSimple}
        onOpenLibrary={props.onOpenLibrary}
        onAskStudio={() => setShowWriters((value) => !value)}
        onJustMakeIt={props.onJustMakeIt}
        onPatchProject={props.onPatchProject}
      />
      {props.apiStatus === "offline" ? (
        <p className="pg-banner" role="status">Can't reach the Kinetix engine — start the API server, then refresh.</p>
      ) : props.engineStale ? (
        <p className="pg-banner" role="status">The engine is running older code — restart it to pick up new features.</p>
      ) : null}
      <ProChecklist onSection={setSection} people={people} project={project} />
      {section === "story" ? (
        <ProStory
          project={project}
          isAnalyzing={props.isAnalyzing}
          onAnalyze={props.onAnalyze}
          onScriptUpload={props.onScriptUpload}
          onSmartIngest={props.onSmartIngest}
          onPatchProject={props.onPatchProject}
          onMakePoster={props.onMakePoster}
          isMakingPoster={props.isMakingPoster}
          onOpenScene={(sceneId) => {
            setSelectedSceneId(sceneId);
            setSection("boards");
          }}
        />
      ) : null}
      {section === "boards" ? (
        <ProBoards
          project={project}
          people={people}
          selectedSceneId={selectedSceneId}
          onSelectScene={setSelectedSceneId}
          isGenerating={props.isGeneratingBoards}
          isEasyModeRunning={props.isEasyModeRunning}
          onGenerateScene={props.onGenerateScene}
          onRemakeScene={props.onRemakeScene}
          onRestoreTake={props.onRestoreTake}
          onSaveSceneNote={props.onSaveSceneNote}
          onSaveCutPlans={props.onSaveCutPlans}
        />
      ) : null}
      {section === "cast" ? (
        <ProCast
          project={project}
          people={people}
          places={places}
          onSetVoicePreset={props.onSetVoicePreset}
          onUploadPersonPhoto={props.onUploadPersonPhoto}
          onUploadAssets={props.onUploadAssets}
        
          onSmartIngest={props.onSmartIngest}
          onPatchProject={props.onPatchProject}
        />
      ) : null}
      {section === "delivery" ? (
        <ProDelivery
          project={project}
          isSavingPackage={props.isSavingPackage}
          isSharingLink={props.isSharingLink}
          onPackage={props.onPackage}
          onShareLink={props.onShareLink}
          onSaveCutPlans={props.onSaveCutPlans}
          onPatchProject={props.onPatchProject}
        />
      ) : null}
      {props.notice ? <p className="pg-toast" role="status">{props.notice}</p> : null}
      {showWriters ? <WritersRoom onClose={() => setShowWriters(false)} onPatchProject={props.onPatchProject} project={project} /> : null}
      {showTour ? <ProTour onDone={closeTour} /> : null}
      {showPalette ? (
        <CommandPalette
          actions={[
            { id: "story", label: "Go to Story", hint: "script, coverage, pitch kit", run: () => setSection("story") },
            { id: "boards", label: "Go to Boards", hint: "generate + QC boards", run: () => setSection("boards") },
            { id: "cast", label: "Go to Cast & Places", hint: "smart drop, casting, bible, scout", run: () => setSection("cast") },
            { id: "delivery", label: "Go to Delivery", hint: "cuts, render, dailies, pack, share", run: () => setSection("delivery") },
            { id: "library", label: "Open Library", hint: "every uploaded asset", run: props.onOpenLibrary },
            { id: "make", label: "✨ Make film", hint: "the one-tap path", run: props.onJustMakeIt },
            { id: "share", label: "Create share link", hint: "read-only pitch link", run: props.onShareLink },
            { id: "poster", label: "🎞 Generate poster", hint: "key art from your cast and pitch", run: props.onMakePoster },
            { id: "theme", label: "Toggle theme", hint: "dark ↔ light", run: () => props.onThemeChange(theme === "dark" ? "light" : "dark") },
            { id: "simple", label: "Switch to Simple mode", hint: "the easy interface", run: props.onOpenSimple },
            { id: "writers", label: "✦ Writers' room", hint: "chat with the studio about this film", run: () => setShowWriters(true) },
            { id: "tour", label: "Replay the tour", hint: "five rooms in five steps", run: () => setShowTour(true) }
          ]}
          onClose={() => setShowPalette(false)}
        />
      ) : null}
    </div>
  );
}
